import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  MAX_LEVEL, buildTree, visibleRows, gapTarget, nestTarget, isNoop, applyMove
} from '../todoTree';
import {
  VIEWS, VIEW_KEYS, SHARE_VIEWS, SHARE_VIEW_KEYS, assignedToMe, byWhen, dueState, matches, viewCounts, viewGroups,
  SMART_VIEWS, SMART_VIEW_KEYS, smartCounts, smartGroups
} from '../todoViews';
import {
  addDays, dateForTime, dueLabel, firstOccurrence, isoDay, longDay, nameKey, parseQuickAdd, repeatText
} from '../../lib/quickAdd';
import { askToNotify } from '../../lib/osNotify';
import TodoSidebar, { NameForm, listMenuItems } from './TodoSidebar';
// Oct 2026, the calmer to-do page (todo-ui-proposal.md): the six fixed
// entries, More and Favourites; layouts; the palette; the Display menu.
import {
  ASSIGNED_TABS, MORE_VIEWS, anyPrefs, arrange, displayFromServer, displayToServer, favouritesFrom, labelKey, layoutsToServer,
  legacySel, prefsFor, pushRecent, readDisplay, readLayouts, readMoreOpen, readOnce, readRecent, writeDisplay, writeLayouts,
  writeMoreOpen, writeOnce
} from './todoNav';
import { DisplayBody, DisplayMenu, LayoutSwitch } from './TodoMenus';
import TodoPalette, { ShortcutsSheet } from './TodoPalette';
import TodoLabels from './TodoLabels';
import TodoEditor, { LabelPicker, ListPicker, PRIORITIES, TicketPicker, TimeField, parseTime } from './TodoEditor';
import MoreMenu, { ColorPicker, swatch } from './MoreMenu';
import { cleanRepeat, cleanReminders, reminderLabel } from './RepeatReminders';
import useReorder from './useReorder';
import TodoDetail from './TodoDetail';
import { Avatar, Dialog, Faces, SendDialog, ShareDialog, personKey } from './TodoPeople';
import { NoteCard, NotesGrid, NotesStrip, PinIcon, TakeNote } from './TodoNotes';
import NoteEditor from './NoteEditor';
import TodoBoard from './TodoBoard';
import TodoTemplates from './TodoTemplates';
import { SubtaskTree } from './CardPopup';
// Oct 2026, Phase 2: one side panel for a to-do (the editor, its details,
// its sub-tasks), in place of the inline editor, the comments dialog and the
// board card's popup.
import TodoPanel, { panelMenu } from './TodoPanel';
// 089: the Private list - the locked page, the hint, and its two questions.
import { EmptyListDialog, PrivateHidden, PrivateHint, PrivateMoveDialog } from './TodoPrivate';
import TodoWorkload from './TodoWorkload';
import TodoEmails from './TodoEmails';
import { AnywayDialog, WaitingChip } from './TodoDeps';
import { ClientChip, ClientPicker } from './TodoClients';
import TodoCalendar from './TodoCalendar';
import { WORKDAY_HOURS, hoursLabel, planOf } from '../../lib/todoCalendar';
import { durationLabel } from '../../lib/quickAdd';
import { deadlineState } from '../todoViews';
import useTodoSmart from './useTodoSmart';
import TodoFilters, { FilterMark } from './TodoFilters';
import {
  MAX_FILES, MAX_FILE_BYTES, bodyToChecklist, checklistToBody, convertedToast, isNote, lineId, noteMatches, serverIndex
} from './notes';
import { fileSize } from '../ChatFile';
import { people as fetchPeople } from '../../lib/chatLink';
import { useMe } from '../../lib/me';
import { dayLabel, fmtDate, fmtDateTime, timeLabel } from '../../lib/when';
import { Icon } from './icons';
import { useConfirm } from '../ConfirmDialog';
// Batch C: dictating a to-do, and AI help on one. Whether either is drawn is
// lib/todoAssistGate.js's to say, and nobody else's.
import { MicButton, VoicePanel, useDictation } from './VoiceTodo';
import { VoiceDraftList } from './VoiceDrafts';
import { AssistRow, ChecklistDialog, StepsDialog } from './TodoAssist';
// A to-do's checklist and files, shown wherever its details are open - so a
// note made a to-do keeps what it had in view (Gordon: "it loses all the
// details, attachments").
import TodoParts, { PartsChips } from './TodoParts';
import { DEFAULT_LANGUAGES, aiAllowed, languagesFor, readAssistFeatures, voiceAllowed } from '../../lib/todoAssistGate';
import { draftsFromSpeech, fromAiAnswer, rememberedLanguage, singleLanguage } from '../../lib/voiceDraft';
import './TodoApp.css';
import './TodoNav.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/** A day said mid-sentence: "done yesterday", "was due Mon 19 Oct" - en-GB, never the browser's order. */
const day = (v) => {
  if (!v) return null;
  const s = dayLabel(/^\d{4}-\d{2}-\d{2}$/.test(String(v)) ? String(v) : new Date(v));
  return /^(Today|Yesterday|Tomorrow)/.test(s) ? s[0].toLowerCase() + s.slice(1) : s;
};

/** Whether a media query matches, kept up to date (the phone's shorter placeholder, say). */
const useMedia = (q) => {
  const get = () => Boolean(window.matchMedia && window.matchMedia(q).matches);
  const [on, setOn] = useState(get);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const m = window.matchMedia(q);
    const f = () => setOn(m.matches);
    f();
    if (m.addEventListener) m.addEventListener('change', f); else m.addListener(f);
    return () => { if (m.removeEventListener) m.removeEventListener('change', f); else m.removeListener(f); };
  }, [q]);
  return on;
};

const plural = (n, one) => `${n} ${one}${n === 1 ? '' : 's'}`;

/* Remembered per owner (a contractor's id, or "me" for a member of staff),
   so the list comes back the way it was left: which parents and sections are
   folded, and which view, list or label was open. Private windows throw on
   localStorage; then it is just not remembered. */
const readSet = (key) => {
  try {
    const v = JSON.parse(localStorage.getItem(key) || '[]');
    return new Set(Array.isArray(v) ? v : []);
  } catch (err) {
    return new Set();
  }
};
const writeSet = (key, set) => {
  try { localStorage.setItem(key, JSON.stringify([...set])); } catch (err) { /* not kept */ }
};
const foldKey = (id) => `ctd-collapsed:${id || ''}`;
const secFoldKey = (id) => `tda-sections-folded:${id || ''}`;
/* ALL's own fold state (Gordon: "keep them minimized as default and I can
   open as an option"): a parent under All starts folded, so what is kept is
   the parents the person has OPENED there - an empty set is the default,
   and a list opened from the sidebar still shows what it did (foldKey). */
const allOpenKey = (id) => `ctd-all-open:${id || ''}`;
const viewKey = (id) => `ctd-view:${id || ''}`;
const readView = (id) => {
  try {
    const v = localStorage.getItem(viewKey(id));
    return VIEW_KEYS.includes(v) ? v : 'all';
  } catch (err) {
    return 'all';
  }
};
const writeView = (id, v) => {
  try { localStorage.setItem(viewKey(id), v); } catch (err) { /* not kept */ }
};
const selKey = (id) => `tda-selected:${id || ''}`;
/** Whether this browser has been on this person's list before (for the one-time tips). */
const beenHere = (id) => {
  try { return localStorage.getItem(selKey(id)) !== null; } catch (err) { return false; }
};
const readSel = (id) => {
  try {
    const v = JSON.parse(localStorage.getItem(selKey(id)) || 'null');
    if (v && v.kind === 'view' && (VIEW_KEYS.includes(v.key) || SHARE_VIEW_KEYS.includes(v.key)
      || v.key === 'notes' || v.key === 'workload' || v.key === 'emails' || SMART_VIEW_KEYS.includes(v.key))) return v;
    if (v && v.kind === 'filter' && v.id) return v;   // a saved filter (074) - TodoApp checks it is there
    if (v && v.kind === 'filters') return v;          // the Filters page (074)
    // The calendar (073) is a layout now (Oct 2026): Upcoming, as a calendar.
    if (v && v.kind === 'view' && v.key === 'calendar') {
      const l = legacySel(v);
      writeLayouts(id, { ...readLayouts(id), [l.layout.key]: l.layout.value });
      return l.sel;
    }
    if (v && (v.kind === 'list' || v.kind === 'label') && v.id) return v;
    if (v && v.kind === 'client' && v.id) return v;   // a client's open to-dos (072)
    if (v && v.kind === 'templates') return v;   // templates (071), staff only - TodoApp checks
  } catch (err) { /* fall through */ }
  return { kind: 'list', id: null };   // the Inbox
};
const writeSel = (id, v) => {
  try { localStorage.setItem(selKey(id), JSON.stringify(v)); } catch (err) { /* not kept */ }
};

const EDGE = 70;          // px from the top/bottom of the window where dragging scrolls
const START_AFTER = 4;    // px of movement before a press on the handle becomes a drag
const UNDO_MS = 6000;     // how long "Undo" is offered
const TICK_MS = 420;      // the check animation, before the row leaves
const SWIPE_AT = 80;      // px a finger must travel for a swipe to count

const reducedMotion = () => Boolean(window.matchMedia
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
const phone = () => Boolean(window.matchMedia && window.matchMedia('(max-width: 480px)').matches);

const EMPTY_FORM = { due_date: '', due_time: '', priority: '', ticket_id: '', notes: '',
                     list_id: null, section_id: null };

// Keep's notes (batch 5): a smart view of their own, only where there are lists.
const NOTES_VIEW = { key: 'notes', label: 'Notes' };

/* Today's planned bar (073): how long a working day is, per owner, in this
   browser - there is no per-person settings store on the server yet. */
const WORKDAYS = [4, 5, 6, 7, 7.5, 8, 9, 10, 12];
const workdayKey = (id) => `tda-workday:${id || ''}`;
const readWorkday = (id) => {
  try {
    const n = Number(localStorage.getItem(workdayKey(id)));
    return WORKDAYS.includes(n) ? n : WORKDAY_HOURS;
  } catch (err) {
    return WORKDAY_HOURS;
  }
};

/** The server's own order of siblings: by position alone, pinned or not. */
const byPos = (a, b) => (Number(a.position ?? 0) - Number(b.position ?? 0))
  || String(a.created_at || '').localeCompare(String(b.created_at || ''));
/** The Notes grid: the most recently pinned first among the pinned, the newest first among the rest. */
const noteOrder = (a, b) => (a.pinned && b.pinned
  ? (Date.parse(b.pinned_at || '') || 0) - (Date.parse(a.pinned_at || '') || 0)
  : 0) || String(b.created_at || '').localeCompare(String(a.created_at || ''));
/** A failure that reads like the server's own, for limits checked here. */
const said = (words) => { const e = new Error(words); e.response = { data: { error: words } }; return e; };

/** Every row of `list` under `id`, at any depth. */
const underIn = (list, id) => {
  const out = [];
  const walk = (pid) => list.forEach((t) => {
    if (t.parent_id === pid) { out.push(t.id); walk(t.id); }
  });
  walk(id);
  return out;
};

/** The server is not migrated for lists yet: every new route says so with a 503. */
const notSetUp = (err) => err?.response?.status === 503;

/**
 * Everybody's to-do list: a contractor's, a member of staff's, an
 * administrator's.
 *
 * Gordon: "In the contractor view - somewhere on the portal i need you to add
 * a new view for TO DO Lists where a contractor" keeps the small things a
 * ticket does not carry - order the part, ring the requester back, bring the
 * long ladder - and ticks them off between jobs.
 *
 * Then: "allow parent - son - grandson relationship. In mobile view it should
 * be easy to drag as well." So a to-do can carry sub-tasks, and those
 * sub-tasks one more level of their own - three levels, no deeper. Items are
 * moved by dragging the ⠿ handle (mouse or finger: Pointer Events, so one
 * code path), and the arrow buttons do the same moves one step at a time for
 * anybody who would rather not drag. Ticking a parent ticks everything under
 * it; unticking a sub-task reopens the parents above it - the server does
 * both, this page just reloads.
 *
 * Then: "scope out the to do list to have more functionality and a better
 * gui. Look at apps like Todoist, google keep and apple reminders." What
 * that brought, each borrowed from where it works best:
 *
 *   VIEWS (Reminders' smart lists, Todoist's Today and Upcoming): Today,
 *   Upcoming, All, Priority 1 and Completed, each worked out from the one GET
 *   (todoViews.js). The others are flat and sorted by when. A search box
 *   narrows any of them.
 *
 *   QUICK ADD (Todoist): one box that reads "ring Rita tomorrow 3pm p1" as a
 *   title, a day, a time and a priority, shows what it understood as chips
 *   that can be taken back, and takes those words out of the title. The
 *   parser is lib/quickAdd.js. "More" still opens every field by hand.
 *
 *   ROWS (Todoist): a round checkbox whose ring is the priority colour, the
 *   title, and a line of chips under it - when, which ticket, notes, and
 *   "2/5" on a parent. Ticking plays a short check before the row leaves.
 *
 *   UNDO (all three): ticking off or removing says so at the foot of the
 *   screen with an Undo for six seconds. A removal is not SENT until then -
 *   the row is hidden and the DELETE waits, so Undo has nothing to put back.
 *   It is sent early if the page is left, hidden or unmounted, so a removal
 *   is never silently lost by walking away.
 *
 *   ON A PHONE (Reminders, Todoist): swipe a row right to tick it off, left
 *   to reschedule it. Only a sideways swipe; an up-and-down one is the
 *   page's, and the ⠿ handle is still the drag.
 *
 *   KEYBOARD (Todoist): q to add, arrows to move between rows, Space or Enter
 *   to tick, e to edit, Delete to remove - with Undo, like the mouse.
 *
 * Then the to-do list became the firm's. Gordon decided that everybody -
 * staff, administrators and contractors - gets one of their own, and that it
 * should grow into a proper Todoist: several LISTS (Todoist's projects, with
 * an Inbox that is always there), SECTIONS inside a list, and LABELS across
 * them. So:
 *
 *   A SIDEBAR down the left: the smart views with their counts, then "My
 *   lists" (colour dot, open count, drag to reorder, a ⋯ to rename, colour,
 *   archive or delete), then "Labels". On a phone it folds into one "Today ▾"
 *   switcher at the top that opens it as a drop-down.
 *
 *   A LIST is the tree from before, once per section: "No section" first,
 *   then each section under a heading that folds, renames, reorders and can
 *   be deleted. An item drags between sections as well as up, down and in.
 *
 *   The SMART VIEWS span every list and put a small list chip on each row; a
 *   LABEL shows everything carrying it, from whichever list.
 *
 *   QUICK ADD reads "#List", "/Section" and "@label" too (lib/quickAdd.js has
 *   the rules), and asks before making a label nobody has made yet.
 *
 * Then REPEATS AND REMINDERS (batch 3). Gordon: "every Monday", "1st of the
 * month", and a nudge when something is due. So:
 *
 *   QUICK ADD reads "every monday", "every 15th", "every! 3 days", "until
 *   31 dec" (lib/quickAdd.js) and shows "↻ Every Monday" as a chip. A to-do
 *   given a TIME there gets a reminder at that time by default, as Todoist
 *   does, shown as a chip of its own - take it off and none is set.
 *
 *   THE EDITOR has a Repeat picker and a Reminders list (RepeatReminders.js).
 *
 *   A ROW says ↻ when it repeats and 🔔 when it will remind. Ticking a
 *   repeating one plays the check and then the row STAYS, moved on to its
 *   next date, with "Next: Mon 27 Oct · Undo"; Undo asks the server to put
 *   the date back (undo-repeat). "Complete forever" ticks it off for good.
 *
 *   COMPLETED lists every time round of a repeating to-do as well as the
 *   to-dos that are done, newest first.
 *
 *   ?item=<id> - where a reminder's "Open" lands - opens the list it is in
 *   and lights the row up. The reminders themselves ring from
 *   ReminderAlerts, which the shell mounts on every page.
 *
 * Then SHARE AND ASSIGN (batch 4). Gordon: shared lists, giving a to-do to
 * somebody, comments on an item, and a to-do made from a chat message or a
 * ticket. So, Todoist's shared projects with Slack's people in them:
 *
 *   A LIST can be shared - "Share" on its header and in its ⋯ - with anybody
 *   in the chat directory (TodoPeople.js). Its header then shows their faces
 *   and the sidebar a two-people mark; the owner adds and removes, anybody
 *   else may leave.
 *
 *   A TO-DO can be given to somebody. In a shared list, to any member, from
 *   the picker beside its comments or "+Marco" as it is typed (lib/quickAdd.js
 *   reads it); the row then shows their face. In a list of my own nobody else
 *   can see it, so it is SENT instead: it moves to their Inbox, and I keep
 *   sight of it under DELEGATED, a smart view of its own beside ASSIGNED TO
 *   ME (the ones somebody else gave me).
 *
 *   COMMENTS AND ACTIVITY (TodoDetail.js): the 💬 on a row, or its editor,
 *   opens the to-do's thread and what has happened to it, and says where it
 *   came from when it was made from a chat message or a ticket - those are
 *   made elsewhere (MakeTodo.js in chat, TicketTodos.js on a ticket).
 *
 * Then NOTES AND THE BOARD (batch 5), the last of it. Gordon: "Google
 * Keep-style notes (coloured cards, checklists inside a note, photos and
 * voice notes, pinning, archive) and a board (columns) view". So:
 *
 *   NOTES (TodoNotes.js, NoteEditor.js): a to-do of kind 'note' is Keep's
 *   card. A "Notes" smart view shows them as a grid, pinned first, under a
 *   "Take a note…" bar; a list's own notes sit above its to-dos. A card
 *   opens into an editor that saves itself, with a checklist edited a line
 *   at a time, photos (button, paste, drop) and chat's voice recorder. An
 *   archived note leaves every view for "Archived notes". "note: …" in
 *   quick add makes one.
 *
 *   COLOUR AND PIN for to-dos as well: a stripe down a coloured row, and a
 *   pinned one floats to the top of its group - on screen only; a move is
 *   still sent as a place in the server's order (notes.js, serverIndex).
 *
 *   THE BOARD (TodoBoard.js): any list, by the List | Board switch on its
 *   header, as columns - its sections - of cards, dragged between and within
 *   them, the columns dragged too. Which one a list is shown as is the
 *   server's to remember.
 *
 * WHICH SERVER. The lists live behind /api/todos, which a server only has
 * once migration 057 has run; until then every one of those routes answers
 * 503. A contractor then gets exactly the list they had before - the old
 * /contractors/:id/todos, one list, the view pills - and a member of staff,
 * who had no list before, a calm "not set up yet". Nothing in between: the
 * first answer decides, for as long as the page is open.
 *
 * WHOSE LIST. `owner` is { kind: 'contractor', id } or { kind: 'self' }.
 * `token` is the contractor's own; an administrator using "View as" passes
 * undefined and the app's interceptor attaches their staff token instead -
 * the same shape as ContractorAvailability and the billing tabs - and every
 * call then names whose list it means with `as=c:<id>`, because the staff
 * token alone says "Gordon", and Gordon now has a list of his own. The one
 * request made with fetch rather than axios (a removal sent as the page is
 * left, which needs keepalive) goes through the app's fetch wrapper, which
 * does the same.
 *
 * `tickets` are a contractor's assignments, already loaded by the portal, so
 * the "for ticket" picker and quick add's "#0042" cost no request. A member
 * of staff may read far more tickets than fit in a select, so for them both
 * search the server instead.
 */
export default function TodoApp({ owner, token, tickets = [], onCountChange }) {
  const auth = useMemo(
    () => (token ? { headers: { Authorization: `Bearer ${token}` } } : undefined),
    [token]);
  const navigate = useNavigate();
  const location = useLocation();
  const isContractor = owner?.kind === 'contractor';
  const ownerId = isContractor ? owner.id : null;
  const ownerKey = isContractor ? owner.id : 'me';
  const asParam = isContractor && !token && ownerId ? `c:${ownerId}` : null;
  // Who "me" is, in the keys a list's members and an assignee are named by.
  const meInfo = useMe();
  const myKey = isContractor ? (ownerId ? `c:${ownerId}` : null) : (meInfo?.id ? `u:${meInfo.id}` : null);

  // 'probe' until the first answer, then 'new' (lists), 'legacy' (the
  // contractor's old single list) or 'unavailable' (staff, not migrated).
  const [mode, setModeState] = useState('probe');
  const modeRef = useRef('probe');
  const setMode = (m) => { modeRef.current = m; setModeState(m); };
  const withLists = mode === 'new';

  const [open, setOpen] = useState([]);
  const [done, setDone] = useState([]);
  const [completions, setCompletions] = useState([]);   // every time round of a repeating to-do
  const [lists, setLists] = useState([]);
  const [labels, setLabels] = useState([]);
  const [archived, setArchived] = useState(null);   // archived lists, once asked for
  const [loading, setLoading] = useState(true);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);   // id of the item in the full editor
  const [renaming, setRenaming] = useState(null); // id of the item whose title is being typed over
  const [addingTo, setAddingTo] = useState(null); // id of the item getting a sub-task
  const [sub, setSub] = useState({ title: '', due_date: '' });
  const [folded, setFolded] = useState(() => readSet(foldKey(ownerKey)));
  const [secFolded, setSecFolded] = useState(() => readSet(secFoldKey(ownerKey)));
  const [allOpen, setAllOpen] = useState(() => readSet(allOpenKey(ownerKey)));   // parents opened under All
  const [view, setView] = useState(() => readView(ownerKey));   // the old single list's pills
  const [sel, setSel] = useState(() => readSel(ownerKey));      // the sidebar's choice
  // ?q= - the global search's "See all in To-dos" - starts the box with its words.
  const [query, setQuery] = useState(() => { try { return new URLSearchParams(location.search).get('q') || ''; } catch { return ''; } });
  const [searchOpen, setSearchOpen] = useState(() => query !== '');          // the phone's search, unfolded from its magnifier
  const [drawer, setDrawer] = useState(false);                  // the phone's drop-down
  const [addingSection, setAddingSection] = useState(false);
  const [renamingSection, setRenamingSection] = useState(null);

  // Oct 2026, the calmer page. `layouts` the layout of Today, Upcoming and
  // Calendar for a list ({ today: 'calendar', 'list:<id>': 'calendar' });
  // `moreOpen` More in the sidebar; `palette` Ctrl K; `keysOpen` the "?"
  // sheet; `quickFloat` the floating quick add (Q, "+ Add to-do", the
  // phone's +); `display` the Display menu's choices; `headRename` the
  // open list being renamed from its header.
  const [layouts, setLayouts] = useState(() => readLayouts(ownerKey));
  const [moreOpen, setMoreOpenState] = useState(() => readMoreOpen(ownerKey));
  const setMoreOpen = (v) => { setMoreOpenState(v); writeMoreOpen(ownerKey, v); };
  const [palette, setPalette] = useState(false);
  const [keysOpen, setKeysOpen] = useState(false);
  const [quickFloat, setQuickFloat] = useState(false);
  const [display, setDisplayState] = useState(() => readDisplay(ownerKey));
  const [headRename, setHeadRename] = useState(false);
  const returning = useRef(beenHere(ownerKey));
  const [tipMoved, setTipMoved] = useState(() => !readOnce(ownerKey, 'moved'));
  const [planOpen, setPlanOpen] = useState(false);   // Today's planned bar, unfolded from its heading
  const [newList, setNewList] = useState(false);     // the palette's "New list"
  const [showArchived, setShowArchived] = useState(false);   // My lists ▾ → Archived lists
  // Phase 2: the to-do open in the side panel ({ id, tab, at, scroll }), what
  // its fields hold right now (TodoEditor's liveRef), and a section's own
  // "+ Add to-do" being typed into.
  const [panel, setPanel] = useState(null);
  const panelLive = useRef(null);
  const [addingIn, setAddingIn] = useState(null);
  const [onboard, setOnboard] = useState(() => readOnce(ownerKey, 'onboard'));
  // The side panel is the one place a to-do opens (Gordon, 26 Oct: the
  // classic inline editor, the comments dialog and the board card's popup
  // are gone). The old single list of a server before 057 keeps its own
  // small editor - there is no list for a panel to belong to.
  const panelMode = withLists;

  // Batch 4, share and assign. `delegated` are the to-dos I handed to
  // somebody in a list I am not in; `people` the chat directory, fetched the
  // first time somebody is to be found (null until then); `sharing` the
  // list in the Share dialog; `sendFor` the to-do being sent, and to whom
  // if that is already known.
  const [delegated, setDelegated] = useState([]);
  const [people, setPeople] = useState(null);
  const [sharing, setSharing] = useState(null);     // list id
  const [sendFor, setSendFor] = useState(null);     // { item, preset }
  const [dialogErr, setDialogErr] = useState(null);

  // Batch 5, notes and the board. `noteOpen` is the note in the editor;
  // `notesArchive` whether the Notes view is showing its archive, and
  // `archivedNotes` what the server said is in it (null until asked);
  // `askCol` a card being sent to another column from its ⋯, `noteMeta` a
  // note's list or labels being chosen from its card.
  const [noteOpen, setNoteOpen] = useState(null);
  const [notesArchive, setNotesArchive] = useState(false);
  const [archivedNotes, setArchivedNotes] = useState(null);
  const [askCol, setAskCol] = useState(null);
  // 089: the Private list's questions - a to-do on its way in or out
  // ({ item, from, to, resolve }) and "empty it?" ({ list }) - and quick
  // add's files, attached once the to-do is in.
  const [askMove, setAskMove] = useState(null);
  const [askEmpty, setAskEmpty] = useState(null);
  // "Delete this?" - the one dialog for it (components/ConfirmDialog).
  const [confirm, confirmDialog] = useConfirm();
  const [quickFiles, setQuickFiles] = useState([]);
  const quickFileInput = useRef(null);
  const [noteMeta, setNoteMeta] = useState(null);   // { id, mode: 'list' | 'labels' }

  // Clients, dependencies and the workload (072): what this server can do
  // (GET /todos/features, asked once lists are known to be there), the
  // clients for the pickers (staff only), the to-do whose "complete
  // anyway?" is up, and the list whose client is being chosen.
  const [features, setFeatures] = useState(() => ({ clients: false, dependencies: false, workload: false, email_sort: false,
                                                    email_mode: 'direct', ...readAssistFeatures(null) }));
  // 106: how many emails wait to be sorted into to-dos (the Emails entry's
  // badge), and which one a link or the bell asked to open.
  const [emailsWaiting, setEmailsWaiting] = useState(0);
  const [emailOpen, setEmailOpen] = useState(null);
  const [companies, setCompanies] = useState(null);
  const [anyway, setAnyway] = useState(null);           // { item, opts }
  const [listClient, setListClient] = useState(null);   // { id, value }

  // Templates (071): staff only. `tplIntent` is a list's "Save as
  // template…" on its way to the templates page.
  const [tplIntent, setTplIntent] = useState(null);

  // The calendar (073): a to-do being added from an empty slot, in the
  // editor; and how long a working day is, for Today's planned bar.
  const [drafting, setDrafting] = useState(null);
  const [workday, setWorkdayState] = useState(() => readWorkday(ownerKey));
  const setWorkday = (n) => {
    setWorkdayState(n);
    try { localStorage.setItem(workdayKey(ownerKey), String(n)); } catch (err) { /* not kept */ }
  };

  // Quick add: the line as typed, the kinds of chip taken back, and the
  // fields behind "More", which win over anything the line said.
  const [quick, setQuick] = useState('');
  const [ignore, setIgnore] = useState([]);
  const [confirmed, setConfirmed] = useState(() => new Set());   // new labels said yes to
  const [hint, setHint] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [more, setMore] = useState(false);
  const [foundTickets, setFoundTickets] = useState([]);           // staff: what "#0042" found
  const quickRef = useRef(null);
  // Batch C: the language dictated in (remembered), what went wrong, the
  // preview card's draft ({ draft, transcript, language, note }), whether AI
  // is still reading it, and the AI help dialog that is open ({ kind, id }).
  const [voiceLang, setVoiceLang] = useState(() => rememberedLanguage(DEFAULT_LANGUAGES));
  const [voiceErr, setVoiceErr] = useState(null);
  const [voiceDraft, setVoiceDraft] = useState(null);
  const [voiceReading, setVoiceReading] = useState(false);
  const voiceSeq = useRef(0);
  const [assist, setAssist] = useState(null);
  const narrow = useMedia('(max-width: 640px)');
  // Where the sidebar is the "Today ▾" switcher: the search folds into a
  // magnifier beside it (Gordon, item 12), one row for both.
  const phoneHead = useMedia('(max-width: 760px)');

  // "Today" is worked out from this, and it moves on by itself, so a page
  // left open overnight does not go on calling yesterday today.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60 * 1000);
    return () => clearInterval(t);
  }, []);
  const todayIso = isoDay(now);
  const weekOut = isoDay(addDays(now, 7));   // a deadline nearer than this shows on its row

  const [completing, setCompleting] = useState(() => new Set());
  const [hidden, setHidden] = useState(() => new Set());
  const [toast, setToast] = useState(null);
  const [focusId, setFocusIdState] = useState(null);
  // Held in a ref as well: two arrow presses can land before React has
  // re-rendered from the first, and the second must start where it left off.
  const focusIdRef = useRef(null);
  const setFocusId = (id) => { focusIdRef.current = id; setFocusIdState(id); };
  const [flash, setFlash] = useState(null);       // a row lit up: ?item=, or just moved on by a repeat
  const [sheet, setSheet] = useState(null);       // the item being rescheduled
  const [swipe, setSwipe] = useState(null);       // { id, dx } while a finger slides a row

  // Dragging. `dragId` is set on the press and is what hangs the window
  // listeners; `drag` is the floating copy, `drop` the indicator.
  const [dragId, setDragId] = useState(null);
  const [drag, setDrag] = useState(null);
  const [drop, setDrop] = useState(null);
  const dragRef = useRef(null);
  const treeBox = useRef(null);
  const viewRef = useRef(null);
  const moveRef = useRef(null);

  /* ------------------------------------------------------------- addresses */

  // The old single list, and the new routes. Every new call carries `as`
  // when an administrator is looking through a contractor's eyes.
  const legacyBase = `${API_BASE}/contractors/${ownerId}/todos`;
  const NEW = `${API_BASE}/todos`;
  const qs = useCallback((params = {}) => {
    const s = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== null && v !== undefined) s.set(k, v); });
    if (asParam) s.set('as', asParam);
    const t = s.toString();
    return t ? `?${t}` : '';
  }, [asParam]);
  const itemsUrl = () => (modeRef.current === 'legacy' ? legacyBase : `${NEW}${qs()}`);
  const itemUrl = (id, tail = '') => (modeRef.current === 'legacy'
    ? `${legacyBase}/${id}${tail}` : `${NEW}/${id}${tail}${qs()}`);

  // The comment thread and the activity (TodoDetail) speak to /todos through
  // this, so they carry the same token and the same `as=`.
  const api = useMemo(() => ({
    get: (path) => axios.get(`${NEW}${path}${qs()}`, auth),
    post: (path, body) => axios.post(`${NEW}${path}${qs()}`, body, auth),
    patch: (path, body) => axios.patch(`${NEW}${path}${qs()}`, body, auth),
    put: (path, body) => axios.put(`${NEW}${path}${qs()}`, body, auth),
    del: (path) => axios.delete(`${NEW}${path}${qs()}`, auth),
    // Bytes (a waiting email's file, 106), with the same sign-in and `as=`.
    blob: (path) => axios.get(`${NEW}${path}${qs()}`, { ...(auth || {}), responseType: 'blob' }).then((r) => r.data)
  }), [NEW, qs, auth]);

  // 073: the calendar's meetings and appointments, for the days it shows.
  const calendarFeed = useCallback((from, to) => axios.get(`${NEW}/calendar${qs({ from, to })}`, auth)
    .then((r) => r.data), [NEW, qs, auth]);
  // 077: which event calendars this person shows - saved on the server, so
  // the choice follows them (and, with View as, it is the contractor's).
  const saveLayers = useCallback((choices) => axios.put(`${NEW}/calendar/layers${qs()}`, { layers: choices }, auth)
    .then((r) => r.data), [NEW, qs, auth]);
  // 097: this person's private calendar address (CalendarSubscribe.js) -
  // offered when the server says so (features.calendar_link: the migration
  // in, the firm switch on, and at their own keyboard, never under View as).
  const subscribe = useMemo(() => ({
    get: () => axios.get(`${NEW}/calendar/link${qs()}`, auth).then((r) => r.data),
    create: () => axios.post(`${NEW}/calendar/link${qs()}`, {}, auth).then((r) => r.data),
    update: (body) => axios.patch(`${NEW}/calendar/link${qs()}`, body, auth).then((r) => r.data),
    remove: () => axios.delete(`${NEW}/calendar/link${qs()}`, auth).then((r) => r.data)
  }), [NEW, qs, auth]);

  /*
   * THE DIRECTORY, for "Add people", "Send to…" and quick add's "+Name": the
   * people chat lets this person reach, fetched once and only when somebody
   * is actually to be found - most visits to a to-do list never need it.
   */
  const peopleAsked = useRef(false);
  const needPeople = useCallback(() => {
    if (peopleAsked.current) return;
    peopleAsked.current = true;
    fetchPeople()
      .then((rows) => setPeople((Array.isArray(rows) ? rows : [])
        .map((p) => ({ ...p, key: personKey(p) })).filter((p) => p.key && p.name)))
      .catch(() => { peopleAsked.current = false; setPeople([]); });
  }, []);

  useEffect(() => {
    setFolded(readSet(foldKey(ownerKey)));
    setSecFolded(readSet(secFoldKey(ownerKey)));
    setAllOpen(readSet(allOpenKey(ownerKey)));
    setView(readView(ownerKey));
    setSel(readSel(ownerKey));
    setLayouts(readLayouts(ownerKey));
    setMoreOpenState(readMoreOpen(ownerKey));
    setDisplayState(readDisplay(ownerKey));
    setTipMoved(!readOnce(ownerKey, 'moved'));
    setOnboard(readOnce(ownerKey, 'onboard'));
  }, [ownerKey]);

  /* ------------------------------------------------------------- loading */

  const inboxId = (lists.find((l) => l.is_inbox) || lists[0] || {}).id || null;

  /** One row from the server, with the list fields always there. */
  const tidy = useCallback((t, inbox) => ({
    ...t,
    kind: t.kind === 'note' ? 'note' : 'task',
    list_id: t.list_id || inbox || null,
    section_id: t.section_id || null,
    labels: Array.isArray(t.labels) ? t.labels : [],
    checklist: Array.isArray(t.checklist) ? t.checklist : null,
    attachments: Array.isArray(t.attachments) ? t.attachments : []
  }), []);

  const load = useCallback(async () => {
    if (modeRef.current === 'unavailable') return;
    if (isContractor && !ownerId) return;
    const legacy = async () => {
      const r = await axios.get(`${legacyBase}?include_done=1`, auth);
      return [Array.isArray(r.data?.open) ? r.data.open : [], Array.isArray(r.data?.done) ? r.data.done : [],
              Array.isArray(r.data?.completions) ? r.data.completions : []];
    };
    try {
      setLoading(true);
      let o;
      let d;
      let c = [];
      if (modeRef.current === 'legacy') {
        [o, d, c] = await legacy();
      } else {
        try {
          const [rl, rb, rt] = await Promise.all([
            axios.get(`${NEW}/lists${qs()}`, auth),
            axios.get(`${NEW}/labels${qs()}`, auth),
            axios.get(`${NEW}${qs({ include_done: 1 })}`, auth)
          ]);
          const ls = Array.isArray(rl.data) ? rl.data : [];
          const inbox = (ls.find((l) => l.is_inbox) || ls[0] || {}).id || null;
          setLists(ls.map((l) => ({ ...l, sections: Array.isArray(l.sections) ? l.sections : [] })));
          setLabels(Array.isArray(rb.data) ? rb.data : []);
          o = (Array.isArray(rt.data?.open) ? rt.data.open : []).map((t) => tidy(t, inbox));
          d = (Array.isArray(rt.data?.done) ? rt.data.done : []).map((t) => tidy(t, inbox));
          c = Array.isArray(rt.data?.completions) ? rt.data.completions : [];
          setDelegated((Array.isArray(rt.data?.delegated) ? rt.data.delegated : [])
            .filter((t) => t && t.id != null).map((t) => ({ ...tidy(t, null), access: 'delegated' })));
          if (modeRef.current !== 'new') setMode('new');
        } catch (err) {
          if (!notSetUp(err)) throw err;
          if (!isContractor) { setMode('unavailable'); return; }
          setMode('legacy');
          [o, d, c] = await legacy();
        }
      }
      setOpen(o);
      setDone(d);
      setCompletions(c.filter((x) => x && x.id != null));
      setError(null);
      if (onCountChange) onCountChange(o.filter((t) => !isNote(t)).length);
    } catch (err) {
      console.error('Could not load the to-do list:', err);
      setError(err.response?.data?.error || 'Could not load your to-do list');
    } finally {
      setLoading(false);
    }
  }, [legacyBase, NEW, qs, auth, isContractor, ownerId, onCountChange, tidy]);

  useEffect(() => { load(); }, [load]);

  // 072: what the server can do beyond 060, once it has lists at all - and
  // for staff, the clients a to-do may be for. A server without it answers
  // 404 or 503, and none of it is offered.
  useEffect(() => {
    if (mode !== 'new') return undefined;
    let live = true;
    axios.get(`${NEW}/features${qs()}`, auth)
      .then((r) => {
        if (!live || !r.data || typeof r.data !== 'object') return;
        const f = { // 073: deadlines, durations and the calendar.
                    deadline: r.data.deadline === true, duration: r.data.duration === true, calendar: r.data.calendar === true,
                    clients: r.data.clients === true, dependencies: r.data.dependencies === true,
                    workload: r.data.workload === true,
                    // 097: a private calendar address to subscribe to.
                    calendar_link: r.data.calendar_link === true,
                    // 106: emails waiting to be sorted into to-dos.
                    email_sort: r.data.email_sort === true,
                    email_mode: r.data.email_mode === 'direct' ? 'direct' : 'sort',
                    // 074: the flag, the smart lists and saved filters.
                    flagged: r.data.flagged === true, smart: r.data.smart_lists === true, filters: r.data.filters === true,
                    // Batch C: voice and AI help - read by lib/todoAssistGate.js.
                    ...readAssistFeatures(r.data) };
        setFeatures(f);
        setEmailsWaiting(Number.isInteger(r.data.emails_waiting) ? r.data.emails_waiting : 0);
        if (f.clients && !isContractor) {
          axios.get(`${NEW}/companies${qs()}`, auth)
            .then((c) => { if (live) setCompanies(Array.isArray(c.data) ? c.data : []); })
            .catch(() => { if (live) setCompanies([]); });
        }
      })
      .catch(() => {});
    return () => { live = false; };
  }, [mode, NEW, qs, auth, isContractor]);
  const loadRef = useRef(load);
  loadRef.current = load;

  // 074: saved filters and the sidebar's own choice of smart views - what
  // the server has and says (useTodoSmart), asked again whenever the list is.
  // `filterEdit` is the filter open in the editor on the Filters page.
  const smart = useTodoSmart({ on: mode === 'new' && features.filters, api, open,
                               filterId: sel.kind === 'filter' ? sel.id : null });

  /*
   * LAYOUTS AND DISPLAY CHOICES FOLLOW THE PERSON (26 Oct). Once the server
   * has answered (smart.remote), its copy wins - this browser's was drawn
   * first only so the page did not wait - and is kept here as the offline
   * cache. The first time this browser meets a server that keeps them and
   * the server has nothing yet, what this browser had is sent up, once.
   */
  const adopted = useRef(null);
  useEffect(() => {
    const r = smart.remote;
    if (!r || !r.synced || adopted.current === ownerKey) return;
    adopted.current = ownerKey;
    const theirs = anyPrefs(r.layouts, displayFromServer(r.display));
    if (theirs || readOnce(ownerKey, 'prefs-up')) {
      const l = { ...(r.layouts || {}) };
      const d = displayFromServer(r.display);
      setLayouts(l);
      writeLayouts(ownerKey, l);
      setDisplayState(d);
      writeDisplay(ownerKey, d);
    } else {
      const l = readLayouts(ownerKey);
      const d = readDisplay(ownerKey);
      if (Object.keys(layoutsToServer(l)).length) smart.saveLayouts(layoutsToServer(l));
      const ds = displayToServer(d);
      if (Object.keys(ds).length > 1 || Object.keys(ds.pages).length) smart.saveDisplay(ds);
    }
    writeOnce(ownerKey, 'prefs-up');
    // Once per person, when the server's answer lands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smart.remote, ownerKey]);
  const [filterEdit, setFilterEdit] = useState(null);

  // A reminder card's Done (ReminderAlerts) changed a to-do behind this
  // page's back: read the list again rather than show the old date.
  useEffect(() => {
    const changed = () => loadRef.current();
    window.addEventListener('jarvis:todos-changed', changed);
    return () => window.removeEventListener('jarvis:todos-changed', changed);
  }, []);

  /* ------------------------------------------- undo, and removals that wait */

  const mounted = useRef(true);
  const toastRef = useRef(null);       // { key, commit, timer } - the live toast
  const pending = useRef(new Map());   // to-do id -> { url, headers, ids } not yet DELETEd
  const inflight = useRef(new Set());  // DELETEs on their way

  const unhide = (ids) => setHidden((prev) => {
    const next = new Set(prev);
    ids.forEach((i) => next.delete(i));
    return next;
  });

  /* Send a removal that was waiting for its Undo to run out. fetch with
     keepalive, so one sent as the page goes away still arrives. */
  const commitDelete = (id) => {
    const p = pending.current.get(id);
    if (!p) return Promise.resolve();
    pending.current.delete(id);
    const req = fetch(p.url, { method: 'DELETE', headers: p.headers, keepalive: true })
      .then((r) => { if (!r.ok && r.status !== 404) throw new Error(`DELETE ${r.status}`); })
      .catch(() => {
        if (!mounted.current) return;
        setError('That could not be removed, so it is back');
        unhide(p.ids);
      })
      .finally(() => {
        inflight.current.delete(req);
        if (mounted.current) loadRef.current();
      });
    inflight.current.add(req);
    return req;
  };
  const commitRef = useRef(commitDelete);
  commitRef.current = commitDelete;

  /** The live toast is done with: whatever it was holding back happens now. */
  const settleToast = () => {
    const t = toastRef.current;
    if (!t) return;
    toastRef.current = null;
    clearTimeout(t.timer);
    if (t.commit) t.commit();
  };

  const showToast = (text, { undo, commit } = {}) => {
    settleToast();
    const key = `${Date.now()}-${Math.random()}`;
    const timer = setTimeout(() => {
      if (toastRef.current?.key !== key) return;
      toastRef.current = null;
      if (commit) commit();
      setToast(null);
    }, UNDO_MS);
    toastRef.current = { key, commit, timer };
    setToast({ key, text, undo });
  };

  const undoToast = () => {
    const t = toastRef.current;
    if (!t || !toast) return;
    clearTimeout(t.timer);
    toastRef.current = null;
    const { undo } = toast;
    setToast(null);
    if (undo) undo();
  };

  /** Everything waiting goes now - before a move, and as the page is left. */
  const flushDeletes = () => {
    const t = toastRef.current;
    if (t) { clearTimeout(t.timer); toastRef.current = null; }
    for (const id of [...pending.current.keys()]) commitRef.current(id);
    if (mounted.current) setToast(null);
    return Promise.all([...inflight.current]);
  };
  const flushRef = useRef(flushDeletes);
  flushRef.current = flushDeletes;

  useEffect(() => {
    mounted.current = true;
    const leave = () => flushRef.current();
    const onVis = () => { if (document.visibilityState === 'hidden') flushRef.current(); };
    window.addEventListener('pagehide', leave);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('pagehide', leave);
      document.removeEventListener('visibilitychange', onVis);
      mounted.current = false;
      flushRef.current();
    };
  }, []);

  /* ----------------------------------------------------------- the lists */

  const listById = useMemo(() => new Map(lists.map((l) => [l.id, l])), [lists]);
  const labelById = useMemo(() => new Map(labels.map((b) => [b.id, b])), [labels]);

  // With lists, a row whose list is not one of the person's lists (archived
  // a moment ago, say) is not shown anywhere until the next answer says
  // where it went.
  // The to-dos only: a NOTE (batch 5) is never ticked, counted or dragged
  // in a tree, so it is kept apart - in the Notes view, above its list, and
  // as a card on a board.
  const openShown = useMemo(() => open.filter((t) => !isNote(t) && !hidden.has(t.id)
    && (!withLists || listById.has(t.list_id))), [open, hidden, withLists, listById]);
  const notesShown = useMemo(() => open.filter((t) => isNote(t) && !hidden.has(t.id)
    && (!withLists || listById.has(t.list_id))).sort(noteOrder), [open, hidden, withLists, listById]);
  const doneShown = useMemo(() => done.filter((t) => !hidden.has(t.id)
    && (!withLists || listById.has(t.list_id))), [done, hidden, withLists, listById]);

  const tree = useMemo(() => buildTree(openShown), [openShown]);

  /*
   * COMPLETED, with the times round. A repeating to-do ticked off stays open
   * with its next date, so the done list alone would never show it was done
   * at all; the server keeps each occasion as a completion, and they are
   * shown here as rows of their own (with a ↻), newest first among the done
   * to-dos. A completion that IS a done to-do - the last time round of one
   * completed forever, or a server that records every tick - is the same
   * moment for the same to-do, and is shown once.
   */
  const doneMerged = useMemo(() => {
    const at = (t) => Date.parse(t.done_at || t.completed_at || '') || 0;
    const doneById = new Map(doneShown.map((t) => [String(t.id), t]));
    const known = new Map([...open, ...done].map((t) => [String(t.id), t]));
    const seen = new Set();
    const extra = [];
    for (const c of completions) {
      const key = String(c.id);
      if (seen.has(key)) continue;
      seen.add(key);
      const twin = doneById.get(String(c.todo_id));
      if (twin && (c.advanced === false || Math.abs(at(twin) - at(c)) < 60 * 1000)) continue;
      const of = known.get(String(c.todo_id));
      if (of && hidden.has(of.id)) continue;
      if (withLists && of && !listById.has(of.list_id)) continue;
      extra.push({ id: `c:${key}`, completion: key, todo_id: c.todo_id, title: c.title || of?.title || '',
                   due_date: c.due_date || null, done_at: c.completed_at, _completion: true,
                   advanced: c.advanced !== false,
                   list_id: of?.list_id || null, notes: null, labels: [] });
    }
    if (!extra.length) return doneShown;
    return [...doneShown, ...extra].sort((a, b) => at(b) - at(a));
  }, [doneShown, completions, open, done, hidden, withLists, listById]);

  // What the sidebar has open, resolved: a list that has gone is the Inbox.
  const selList = withLists && sel.kind === 'list' ? (listById.get(sel.id) || listById.get(inboxId) || null) : null;
  const selLabel = withLists && sel.kind === 'label' ? labelById.get(sel.id) || null : null;
  // 072: a client's open to-dos - from a client chip or the client filter.
  // The companies (staff), and whatever the rows carry (a contractor sees
  // the client only on what they can already see).
  const clientById = useMemo(() => {
    const m = new Map((companies || []).map((c) => [c.id, c]));
    for (const t of open) if (t.company && t.company.id && !m.has(t.company.id)) m.set(t.company.id, t.company);
    return m;
  }, [companies, open]);
  const selClient = withLists && sel.kind === 'client' ? clientById.get(sel.id) || null : null;
  // Templates (071) are their own page, for staff; underneath, Today stays
  // what the rest of this works out, so nothing below has to know.
  const templatesOn = withLists && !isContractor && Boolean(meInfo?.id);
  const onTemplates = templatesOn && sel.kind === 'templates';
  // 074: a saved filter, or the page of them - once the server has them.
  // A filter that has gone (deleted on another device) is Today.
  const wantFilter = withLists && smart.on && sel.kind === 'filter' ? sel.id : null;
  const selFilter = wantFilter ? smart.byId.get(wantFilter) || null : null;
  const selFilterId = wantFilter && (selFilter || smart.filters === null) ? wantFilter : null;
  // The Filters & labels page (Oct 2026) - Labels alone before 074.
  const onFilters = withLists && sel.kind === 'filters';
  const smartOff = (k) => SMART_VIEW_KEYS.includes(k)
    && (!features.smart || (k === 'flagged' && !features.flagged) || (k === 'waiting' && !features.dependencies));
  const selView = !withLists ? view
    : sel.kind === 'view' ? ((sel.key === 'workload' && !features.workload) || (sel.key === 'calendar' && !features.calendar)
      || (sel.key === 'emails' && !features.email_sort) || smartOff(sel.key) ? 'today' : sel.key)
      : (sel.kind === 'label' && !selLabel) || (sel.kind === 'client' && !selClient) || sel.kind === 'templates'
        || ((sel.kind === 'filter' || sel.kind === 'filters') && !selFilterId && !onFilters) ? 'today' : null;
  const curView = selView || (selFilterId ? 'filter' : onFilters ? 'filters' : selList ? 'list' : selClient ? 'client' : 'label');

  // Done children per parent, for the "2/3" on a parent.
  const doneUnder = useMemo(() => {
    const m = new Map();
    for (const t of doneShown) if (t.parent_id) m.set(t.parent_id, (m.get(t.parent_id) || 0) + 1);
    return m;
  }, [doneShown]);

  const titleOf = useMemo(() => {
    const m = new Map();
    for (const t of [...open, ...done]) m.set(t.id, t.title);
    return m;
  }, [open, done]);

  const counts = useMemo(() => viewCounts(openShown, doneMerged, todayIso),
    [openShown, doneMerged, todayIso]);
  const lateCount = useMemo(() => openShown.filter((t) => dueState(t, todayIso) === 'late').length,
    [openShown, todayIso]);

  // Me, by key: from who is signed in, or failing that the owner of my Inbox.
  const selfKey = myKey || ((lists.find((l) => l.is_inbox) || {}).members || [])
    .find((m) => m && m.role === 'owner')?.key || null;
  const givenToMe = useMemo(() => openShown.filter((t) => assignedToMe(t, selfKey)), [openShown, selfKey]);
  const delegatedShown = useMemo(() => delegated.filter((t) => !hidden.has(t.id)), [delegated, hidden]);
  const allCounts = useMemo(() => ({
    ...counts, assigned: givenToMe.length, delegated: delegatedShown.filter((t) => !t.done).length,
    notes: notesShown.length, ...smartCounts(openShown, todayIso)
  }), [counts, givenToMe, delegatedShown, notesShown, openShown, todayIso]);
  const listCounts = useMemo(() => {
    const m = new Map();
    for (const t of openShown) m.set(t.list_id, (m.get(t.list_id) || 0) + 1);
    return m;
  }, [openShown]);
  const labelCounts = useMemo(() => {
    const m = new Map();
    for (const t of openShown) for (const b of t.labels || []) m.set(b, (m.get(b) || 0) + 1);
    return m;
  }, [openShown]);

  const searching = query.trim().length > 0;

  /*
   * THE TREE, as one or more sections. The old single list is one section
   * with no heading; a list is "No section" and then each of its sections,
   * each a tree of its own (a sub-task always shares its parent's section).
   * Dragging works across all of them at once - viewRef carries them all.
   */
  const sections = useMemo(() => {
    const make = (key, sectionId, name, items) => {
      const t = buildTree(items);
      const isFolded = Boolean(sectionId) && secFolded.has(sectionId);
      const rows = isFolded ? [] : visibleRows(t, folded).map((r) => ({ ...r, tree: t, secKey: key, sectionId }));
      return { key, sectionId, name, items, tree: t, rows, folded: isFolded };
    };
    if (!withLists) {
      return view === 'all' && !searching ? [make('_', null, null, openShown)] : null;
    }
    if (!selList || searching) return null;
    const mine = openShown.filter((t) => t.list_id === selList.id);
    const known = new Set(selList.sections.map((s) => s.id));
    const out = [make('_', null, null, mine.filter((t) => !t.section_id || !known.has(t.section_id)))];
    for (const s of selList.sections.slice().sort((a, b) => (a.position ?? 0) - (b.position ?? 0))) {
      out.push(make(s.id, s.id, s.name, mine.filter((t) => t.section_id === s.id)));
    }
    return out;
  }, [withLists, view, searching, openShown, selList, secFolded, folded]);
  const isTree = Boolean(sections);
  const rows = useMemo(() => (sections ? sections.flatMap((s) => s.rows) : []), [sections]);
  viewRef.current = { sections: sections || [] };

  /*
   * THE DISPLAY MENU (Oct 2026): Group by and Sort for a flat view, kept per
   * page; and "Show completed", which on Today, a label or a client adds
   * what was done there under its own heading (a list has its own "Show
   * done" at the foot, which the menu also drives).
   */
  const pageKey = selList ? `list:${selList.id}` : selLabel ? `label:${selLabel.id}` : selFilterId ? `filter:${selFilterId}`
    : selClient ? `client:${selClient.id}` : sel.kind === 'filters' ? 'filters' : sel.kind === 'templates' ? 'templates'
      : (selView || 'today');
  const pageDisplay = display[pageKey] || {};
  // Show completed is the page's own choice, like Group by and Sort - so
  // going somewhere else and back finds it as it was left, on any device.
  const showDone = Boolean(pageDisplay.done);
  const setShowDone = (v) => changeDisplay({ done: Boolean(typeof v === 'function' ? v(showDone) : v) });
  /*
   * THE LAYOUT (Oct 2026): List / Board / Calendar in the page's header.
   * A list is List or Board as the server keeps it (list.view), or Calendar
   * here; Today, Upcoming, a label, a filter, a client and the smart views
   * are List or Calendar. Calendar only where the server has it (073).
   */
  const calOn = withLists && features.calendar;
  const layoutOptions = !withLists || sel.kind === 'filters' || sel.kind === 'templates' ? []
    : selList ? (selList.private_hidden ? [] : ['list', 'board', ...(calOn ? ['calendar'] : [])])
      : calOn && (selLabel || selFilterId || selClient || curView === 'today' || curView === 'upcoming'
        || (MORE_VIEWS.includes(curView) && curView !== 'done')) ? ['list', 'calendar'] : [];
  const layout = selList ? (calOn && layouts[pageKey] === 'calendar' ? 'calendar' : selList.view === 'board' ? 'board' : 'list')
    : layoutOptions.includes('calendar') && layouts[pageKey] === 'calendar' ? 'calendar' : 'list';
  const showCal = layout === 'calendar';
  const canArrange = withLists && !isTree && sel.kind !== 'filters' && sel.kind !== 'templates'
    && !['done', 'delegated', 'notes', 'workload', 'calendar', 'filters', 'emails'].includes(curView) && !(selView === 'all' && !searching);
  const doneScope = useMemo(() => {
    if (!withLists || isTree) return null;
    if (selLabel) return doneShown.filter((t) => (t.labels || []).includes(selLabel.id));
    if (selClient) return doneShown.filter((t) => t.company_id === selClient.id);
    if (curView === 'today') {
      return doneShown.filter((t) => t.done_at && isoDay(new Date(t.done_at)) === todayIso);
    }
    return null;
  }, [withLists, isTree, selLabel, selClient, curView, doneShown, todayIso]);

  // Everything else is headed flat groups - pinned to-dos first in each.
  const groups = useMemo(() => {
    // (A list's group under All is its tree, already pinned-first at every level.)
    const floated = (gs) => (withLists ? gs.map((g) => (String(g.key).startsWith('done') || String(g.key).startsWith('list-') ? g
      : { ...g, items: g.items.slice().sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0)) })) : gs);
    if (isTree) return null;
    let gs = floated(flatGroups());
    if (canArrange) {
      gs = arrange(gs, { group: pageDisplay.group, sort: pageDisplay.sort, todayIso,
                         listName: (id) => (lists.find((l) => l.id === id) || {}).name, longDay });
    }
    if (showDone && doneScope && doneScope.length) {
      gs = [...gs, { key: 'done-shown', label: 'Completed', items: doneScope.filter((t) => matches(t, query)), done: true }]
        .filter((g) => g.items.length);
    }
    return gs;
    // flatGroups reads exactly the values listed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTree, withLists, view, openShown, doneMerged, todayIso, query, selList, selLabel, selClient, selView, searching, lists,
      givenToMe, delegatedShown, allOpen, selFilterId, smart.match, canArrange, pageDisplay.group, pageDisplay.sort,
      showDone, doneScope]);
  function flatGroups() {
    if (selView === 'notes' || selView === 'calendar') return [];
    if (curView === 'filters') return [];
    if (selFilterId) {
      // 074: what the server says the filter holds, drawn from the rows in hand.
      const ids = smart.match && smart.match.ids;
      if (!ids) return [];
      return [{ key: `filter-${selFilterId}`, label: null,
                items: openShown.filter((t) => ids.has(String(t.id)) && matches(t, query)).sort(byWhen) }]
        .filter((g) => g.items.length);
    }
    if (SMART_VIEW_KEYS.includes(selView)) return smartGroups(selView, openShown, todayIso, query);
    if (!withLists) return viewGroups(view, openShown, doneMerged, todayIso, query);
    if (selList) {
      return viewGroups('all', openShown.filter((t) => t.list_id === selList.id), [], todayIso, query);
    }
    if (selLabel) {
      return viewGroups('all', openShown.filter((t) => t.labels.includes(selLabel.id)), [], todayIso, query);
    }
    if (selClient) {
      return viewGroups('all', openShown.filter((t) => t.company_id === selClient.id), [], todayIso, query);
    }
    if (selView === 'assigned') {
      return [{ key: 'assigned', label: null, items: givenToMe.filter((t) => matches(t, query)).sort(byWhen) }]
        .filter((g) => g.items.length);
    }
    if (selView === 'delegated') {
      // Still with them first, then the ones they have done.
      const hit = delegatedShown.filter((t) => matches(t, query));
      return [
        { key: 'delegated', label: null, items: hit.filter((t) => !t.done).sort(byWhen) },
        { key: 'delegated-done', label: 'Done', items: hit.filter((t) => t.done) }
      ].filter((g) => g.items.length);
    }
    if (selView === 'all' && !searching) {
      // All, with lists (Gordon: "keep the structure of the list -
      // grandparent, parent, child"): a group per list, in the list's own
      // order, each the list's full tree - "No section" and then each
      // section, each a tree of its own as the list itself draws them - a
      // parent folded unless it has been opened here (allOpen). The rows
      // carry everything a tree row does but the drag: Indent, Outdent and
      // Move up/down in the ⋯ still work, because each tree is one
      // section's, which is what a move is counted in.
      return lists.map((l) => {
        const mine = openShown.filter((x) => x.list_id === l.id);
        const known = new Set((l.sections || []).map((s) => s.id));
        const make = (sectionId, name, items) => {
          const t = buildTree(items);
          const shut = new Set(items.filter((x) => t.childrenOf(x.id).length && !allOpen.has(x.id)).map((x) => x.id));
          const rows = visibleRows(t, shut).map((r) => ({ ...r, tree: t, secKey: `${l.id}:${sectionId || '_'}`,
                                                          sectionId, noDrag: true }));
          return { key: sectionId || '_', name, rows };
        };
        const secs = [make(null, null, mine.filter((x) => !x.section_id || !known.has(x.section_id)))];
        for (const s of (l.sections || []).slice().sort((a, b) => (a.position ?? 0) - (b.position ?? 0))) {
          secs.push(make(s.id, s.name, mine.filter((x) => x.section_id === s.id)));
        }
        const shown = secs.filter((s) => s.rows.length);
        return { key: `list-${l.id}`, label: l.name, color: l.color, secs: shown, total: mine.length,
                 items: shown.flatMap((s) => s.rows.map((r) => r.item)) };
      }).filter((g) => g.items.length);
    }
    return viewGroups(selView, openShown, doneMerged, todayIso, query);
  }

  const flatDone = curView === 'done';
  // The rows in screen order, for the arrow keys.
  const listDone = useMemo(() => (withLists && selList
    ? doneShown.filter((t) => t.list_id === selList.id) : doneShown), [withLists, selList, doneShown]);
  const displayed = useMemo(() => {
    if (isTree) {
      const list = rows.map((r) => ({ id: r.item.id, item: r.item, done: false }));
      if (showDone) listDone.forEach((t) => list.push({ id: t.id, item: t, done: true }));
      return list;
    }
    // A time round of a repeating to-do is a record, not a row to act on;
    // nor is a to-do I have handed to somebody else.
    if (curView === 'delegated') return [];
    return groups.flatMap((g) => g.items.filter((t) => !t._completion)
      .map((t) => ({ id: t.id, item: t, done: flatDone || Boolean(g.done) })));
  }, [isTree, rows, showDone, listDone, groups, flatDone, curView]);

  const chooseView = (v) => {
    setView(v);
    writeView(ownerKey, v);
    setFocusId(null);
  };

  /**
   * Set a page's layout (List / Board / Calendar): kept on the server, so it
   * follows the person, and in this browser for when the server cannot be
   * reached.
   */
  const setLayout = (key, value) => {
    const out = { ...layouts };
    if (!value || value === 'list') delete out[key]; else out[key] = value;
    setLayouts(out);
    writeLayouts(ownerKey, out);
    smart.saveLayouts(layoutsToServer(out));
  };

  const choose = (asked) => {
    // A key from before the redesign (a link, the palette's G C): Calendar
    // is Upcoming in the Calendar layout.
    const { sel: next, layout } = legacySel(asked);
    if (layout) setLayout(layout.key, layout.value);
    pushRecent(ownerKey, next);
    setSel(next);
    setFilterEdit(null);
    writeSel(ownerKey, next);
    setHeadRename(false);
    setNotesArchive(false);
    setDrawer(false);
    setFocusId(null);
    setAddingSection(false);
    setAddingIn(null);
    // Somewhere else: the side panel closes, saving what was typed in it.
    if (panel) closePanel();
  };

  const setFold = (id, fold) => setFolded((prev) => {
    const next = new Set(prev);
    if (fold) next.add(id); else next.delete(id);
    writeSet(foldKey(ownerKey), next);
    return next;
  });
  const setSecFold = (id, fold) => setSecFolded((prev) => {
    const next = new Set(prev);
    if (fold) next.add(id); else next.delete(id);
    writeSet(secFoldKey(ownerKey), next);
    return next;
  });
  // Under All a parent is folded unless it is in the opened set.
  const setAllFold = (ids, fold) => setAllOpen((prev) => {
    const next = new Set(prev);
    for (const id of [].concat(ids)) { if (fold) next.delete(id); else next.add(id); }
    writeSet(allOpenKey(ownerKey), next);
    return next;
  });
  /** Whatever is under `id` must be seen now - in the list and under All. */
  const unfold = (id) => { setFold(id, false); setAllFold(id, false); };
  const isAllTree = withLists && selView === 'all' && !searching;
  // Every open parent, for Expand all / Collapse all.
  const allParents = useMemo(() => (isAllTree
    ? openShown.filter((t) => tree.childrenOf(t.id).length).map((t) => t.id) : []), [isAllTree, openShown, tree]);

  const act = async (fn) => {
    setBusy(true);
    setError(null);
    try { await fn(); await load(); return true; }
    catch (err) { setError(err.response?.data?.error || 'That did not go through'); return false; }
    finally { setBusy(false); }
  };

  /* -------------------------------------------------------------- tickets */

  // A member of staff's tickets come from a search, not a list in hand.
  const searchTickets = useMemo(() => (isContractor ? null
    : (term) => axios.get(`${NEW}/tickets${qs({ q: term })}`, auth).then((r) => r.data)),
  [isContractor, NEW, qs, auth]);

  // Quick add's "#0042" for staff: look the number up as it is typed, so the
  // chip can name the ticket. Only numbers and JAR-style references go out.
  const ticketWord = useMemo(() => {
    const m = /(?<![\w#])#(\d{2,6})(?!\w)|(?<![\w-])([a-z]{2,6}-\d{2,6}(?:-\d{1,6})?)(?![\w-])/i.exec(quick);
    return m ? (m[1] || m[2]) : null;
  }, [quick]);
  useEffect(() => {
    if (!searchTickets || !withLists || !ticketWord) return undefined;
    let live = true;
    const t = setTimeout(() => {
      searchTickets(ticketWord)
        .then((rows2) => {
          if (!live || !Array.isArray(rows2)) return;
          setFoundTickets((prev) => {
            const seen = new Set(prev.map((x) => String(x.id)));
            return [...prev, ...rows2.filter((x) => x && !seen.has(String(x.id)))].slice(-100);
          });
        })
        .catch(() => {});
    }, 300);
    return () => { live = false; clearTimeout(t); };
  }, [searchTickets, withLists, ticketWord]);
  const knownTickets = isContractor ? tickets : foundTickets;

  /* ------------------------------------------------------------ quick add */

  const defaultListId = selList ? selList.id : inboxId;
  // "+Name" needs the directory: fetched the first time a "+" is typed.
  const wantsPerson = withLists && /(?<![\w+])\+\p{L}/u.test(quick);
  useEffect(() => { if (wantsPerson) needPeople(); }, [wantsPerson, needPeople]);
  const reachable = useMemo(() => (people || []).filter((p) => p.key !== myKey), [people, myKey]);

  /* ------------------------------------------------ batch C: say a to-do */

  const canVoice = withLists && voiceAllowed(features);
  const canAi = withLists && aiAllowed(features);
  const languageName = (code) => (features.voice_languages.find((l) => l.code === code) || {}).name || code;
  // Auto (Malti + English) when AI help is on - the default - once the
  // features have said so; the person's own choice otherwise.
  useEffect(() => { setVoiceLang(rememberedLanguage(features.voice_languages, { ai: canAi })); }, [features.voice_languages, canAi]);
  const sendVoice = useCallback((blob, language) => axios.post(
    `${NEW}/voice${qs({ language, ...(language === 'auto' ? { fallback: singleLanguage(features.voice_languages) } : {}) })}`, blob,
    { ...(auth || {}), headers: { ...((auth && auth.headers) || {}), 'Content-Type': 'audio/wav' } }).then((r) => r.data),
  [NEW, qs, auth, features.voice_languages]);
  /*
   * Words to read - heard, or typed and sent with ✨. With AI, Claude reads
   * them (both transcripts, for Auto) against my own lists, labels and
   * people, and may make several to-dos of one sentence; the server works
   * out every day. Without it - or when it fails, or says nothing useful -
   * quick add's parser does, splitting "Tuesday and Wednesday 2 to 5" itself.
   * Either way the preview lists them all and nothing is made yet.
   */
  const readWords = async ({ text, transcripts = null, language = null, typed = false, note: said = null }) => {
    const mine = ++voiceSeq.current;
    const byQuickAdd = () => draftsFromSpeech(text, { now, lists, labels, people: reachable, defaultListId });
    if (!canAi) { setVoiceDraft({ drafts: byQuickAdd(), heard: text, language, typed, note: said }); return; }
    setVoiceReading(true);
    try {
      const r = await api.post('/assist/draft', { text, list_id: defaultListId, ...(transcripts ? { transcripts } : {}) });
      if (mine !== voiceSeq.current) return;
      const drafts = fromAiAnswer(r.data);
      if (drafts.length) setVoiceDraft({ drafts, heard: r.data.heard || text, language, typed, note: said });
      else setVoiceDraft({ drafts: byQuickAdd(), heard: text, language, typed, note: r.data?.error || 'AI could not read that, so quick add did.' });
    } catch (err) {
      if (mine !== voiceSeq.current) return;
      const why = err.response?.status === 429 ? `${err.response.data?.error || 'AI help is resting.'} Quick add read it instead.`
        : 'AI help could not be reached, so quick add read it.';
      setVoiceDraft({ drafts: byQuickAdd(), heard: text, language, typed, note: why });
    } finally {
      if (mine === voiceSeq.current) setVoiceReading(false);
    }
  };
  const heard = (got) => {
    const text = String(got?.text || '').trim();
    if (!text) { setVoiceErr('Nothing was heard. Try again.'); return; }
    const transcripts = Array.isArray(got.transcripts) ? got.transcripts.filter((t) => t && t.text) : null;
    readWords({ text, transcripts: transcripts && transcripts.length ? transcripts : null,
      language: got.language || voiceLang, note: got.auto_off ? got.note || null : null });
  };
  /** ✨ in quick add, or Shift+Enter: the typed sentence, read by AI. */
  const askAi = (line) => {
    const text = String(line || '').trim();
    if (!text || !canAi) return;
    readWords({ text, typed: true });
  };
  const dictation = useDictation({ send: sendVoice, maxSeconds: features.voice_max_seconds, language: voiceLang,
                                   onText: heard, onError: setVoiceErr });
  // "+Name" in the fallback needs the directory: asked for while they talk.
  useEffect(() => { if (dictation.phase === 'recording' && !features.ai_assist) needPeople(); },
    [dictation.phase, features.ai_assist, needPeople]);
  const backToQuick = () => {
    voiceSeq.current += 1;
    setVoiceReading(false);
    setVoiceDraft(null);
    requestAnimationFrame(() => { if (quickRef.current) quickRef.current.focus({ preventScroll: true }); });
  };
  // Cancel while AI reads: stop listening for its answer, too.
  const panelDictation = { ...dictation, cancel: () => { dictation.cancel(); backToQuick(); } };
  /** Whom a to-do in this list can be given to: its other members. */
  const membersOf = useCallback((listId) => ((lists.find((l) => l.id === listId) || {}).members || [])
    .filter((m) => m && m.key && m.key !== myKey).map((m) => ({ key: m.key, name: m.name || 'Somebody' })),
  [lists, myKey]);
  // "note: …" makes a NOTE of the line instead of a to-do (batch 5). Read
  // here rather than in lib/quickAdd.js, which stays about to-dos: the rest
  // of the line is parsed as ever, for its #List, /Section and @labels.
  const asNote = withLists && /^\s*note:/i.test(quick);
  // 073: "{fri}" (a deadline) and "for 2h" are read only where they can be kept.
  const planOn = withLists && features.deadline && features.duration;
  const parsed = useMemo(() => parseQuickAdd(withLists ? quick.replace(/^\s*note:\s*/i, '') : quick, {
    now, tickets: knownTickets, ignore: planOn ? ignore : [...ignore, 'deadline', 'duration'],
    lists: withLists ? lists : [], labels: withLists ? labels : [], defaultListId,
    people: withLists ? reachable : []
  }), [quick, now, knownTickets, ignore, withLists, lists, labels, defaultListId, reachable, planOn]);

  // The reminder quick add gives a to-do with a time: on, until its chip is taken off.
  const formTime = parseTime(form.due_time) || null;
  const quickTime = formTime || parsed.due_time || null;
  const remindAtTime = Boolean(quickTime) && !ignore.includes('reminder');

  const clearQuick = () => {
    setQuick('');
    setQuickFiles([]);
    setIgnore([]);
    setConfirmed(new Set());
    setHint(null);
    setForm(EMPTY_FORM);
    setMore(false);
  };

  const add = (e) => {
    e.preventDefault();
    const title = parsed.title;
    if (!title) return;
    // A label nobody has made is made only once it has been said yes to, in
    // its chip. Enter before that asks rather than guessing.
    const fresh = parsed.labels.filter((b) => b.isNew);
    const unsure = fresh.filter((b) => !confirmed.has(nameKey(b.name)));
    if (unsure.length) {
      setHint(`"${unsure[0].name}" is not a label yet. Press ✓ on it to make it, or × to keep the word.`);
      return;
    }
    // What the fields say wins over what the line said; a time with no day
    // is the next time that time comes round. Added from Today, a to-do
    // with no day is for today - otherwise it would vanish from the view
    // it was typed into, which reads as lost. Priority 1 likewise.
    const due_time = formTime || parsed.due_time || null;
    let due_date = form.due_date || parsed.due_date || null;
    if (!due_date && due_time) due_date = dateForTime(due_time, now);
    if (!due_date && curView === 'today') due_date = todayIso;
    const priority = Number(form.priority) || parsed.priority || (curView === 'p1' ? 1 : 4);
    const body = {
      title,
      due_date,
      due_time,
      priority,
      ticket_id: form.ticket_id || parsed.ticket?.id || null,
      notes: form.notes.trim() || null
    };
    // 073: the deadline and how long it takes, when the line said them.
    if (planOn && parsed.deadline) body.deadline = parsed.deadline;
    if (planOn && parsed.duration_minutes) body.duration_minutes = parsed.duration_minutes;
    // Only what was said: a line with no repeat and no time sends neither,
    // so the server's own defaults stand.
    if (parsed.repeat) {
      body.repeat = parsed.repeat;
      body.repeat_text = repeatText(parsed.repeat, { now, dueDate: due_date });
    }
    // A time means a reminder at that time, unless its chip was taken off -
    // then an empty set, said out loud.
    if (due_time) body.reminders = remindAtTime ? [{ offset_minutes: 0 }] : [];
    if (body.reminders?.length) askToNotify();
    return act(async () => {
      if (withLists) {
        const home = parsed.list ? parsed.list.id : defaultListId;
        const list_id = form.list_id || home;
        const section_id = form.section_id
          || (parsed.section && list_id === home ? parsed.section.id : null);
        const ids = parsed.labels.filter((b) => b.id).map((b) => b.id);
        for (const b of fresh) {
          try {
            const r = await axios.post(`${NEW}/labels${qs()}`, { name: b.name }, auth);
            if (r.data?.id) ids.push(r.data.id);
          } catch (err) {
            // Made meanwhile, in another tab: use that one.
            if (err.response?.status !== 409) throw err;
            const r = await axios.get(`${NEW}/labels${qs()}`, auth);
            const hit = (Array.isArray(r.data) ? r.data : []).find((x) => nameKey(x.name) === nameKey(b.name));
            if (hit) ids.push(hit.id);
          }
        }
        if (selLabel) ids.push(selLabel.id);
        Object.assign(body, { list_id, section_id, labels: [...new Set(ids)] });
        // 072: added from a client's view, it is for that client.
        if (selClient && !isContractor && features.clients) body.company_id = selClient.id;
      }
      if (asNote) {
        // A note has no day, time or priority of its own: just its words,
        // where it goes and its labels.
        const files = quickFiles;
        const r = await axios.post(itemsUrl(), { kind: 'note', title: body.title, notes: body.notes,
          list_id: body.list_id, section_id: body.section_id, labels: body.labels }, auth);
        clearQuick();
        if (files.length && r.data?.id != null) await uploadTo({ id: r.data.id, attachments: [] }, files);
        showToast('Note added');
        return;
      }
      // "+Marco": a member of the list it goes into is given it there; any
      // other "+Name" is somebody outside it, and the to-do is sent to them.
      // A "+Name" read against another list than the one it ended up in
      // (picked by hand under More) is checked again against that one.
      const who = withLists ? parsed.assignee : null;
      const target = who && listById.get(body.list_id);
      const member = Boolean(who && target && target.shared
        && (target.members || []).some((m) => m.key === who.key));
      // 089: into Private nobody is given anything - the "+Name" is left off, and said so.
      const toPrivate = Boolean(who && target && target.is_private);
      if (member) body.assignee_key = who.key;
      const files = quickFiles;
      const r = await axios.post(itemsUrl(), body, auth);
      clearQuick();
      if (files.length && r.data?.id != null) await uploadTo({ id: r.data.id, attachments: [] }, files);
      if (toPrivate) setHint(`"${target.name}" is private - nothing in it can be given to anybody, so the +${who.name} was left off.`);
      if (who && !member && !toPrivate && r.data?.id != null) {
        await axios.post(itemUrl(r.data.id, '/send'), { key: who.key }, auth);
        showToast(`Sent to ${who.name.split(/\s+/)[0]}`);
      }
    });
  };

  /**
   * The preview card's Create: what quick add's Add does, from the card's
   * fields - into its list (or where I am looking), with my labels, a
   * reminder at its time, given to a member of that list or else sent.
   */
  /**
   * One preview row as POST /todos takes it: what quick add's Add does, from
   * the row's fields - into its list (or where I am looking), with my labels,
   * a reminder at its time (or the minutes before it said), its repeat,
   * given to a member of that list or else sent. `who` is a send.
   */
  const bodyOfDraft = (d) => {
    const due_time = d.due_time || null;
    let due_date = d.due_date || null;
    if (!due_date && due_time) due_date = dateForTime(due_time, now);
    const list_id = d.list_id || defaultListId;
    const ids = [...(d.label_ids || [])];
    if (selLabel) ids.push(selLabel.id);
    const body = { title: d.title, notes: d.notes || null, due_date, due_time, priority: Number(d.priority) || 4,
                   list_id, labels: [...new Set(ids)] };
    if (planOn && d.deadline) body.deadline = d.deadline;
    if (planOn && d.duration_minutes) body.duration_minutes = d.duration_minutes;
    if (d.repeat && due_date) { body.repeat = d.repeat; body.repeat_text = repeatText(d.repeat, { now, dueDate: due_date }); }
    const before = Number.isInteger(d.reminder_minutes_before) && due_date ? d.reminder_minutes_before : null;
    if (before !== null) body.reminders = [{ offset_minutes: before }];
    else if (due_time) body.reminders = [{ offset_minutes: 0 }];
    if (selClient && !isContractor && features.clients) body.company_id = selClient.id;
    const who = d.assignee_key && d.assignee_key !== selfKey ? d.assignee_key : null;
    const target = listById.get(list_id);
    const member = Boolean(who && target && target.shared && (target.members || []).some((m) => m.key === who));
    if (member) body.assignee_key = who;
    return { body, send: who && !member ? who : null };
  };

  /**
   * The preview's Create: every kept row, one by one, in the order shown. A
   * failure part-way keeps the preview open with what is left in it and says
   * how many went in, so nothing is made twice by pressing Create again.
   */
  const createFromDrafts = async (kept) => {
    if (!kept.length) return;
    const typedNow = Boolean(voiceDraft?.typed);
    if (kept.some((d) => d.due_time || Number.isInteger(d.reminder_minutes_before))) askToNotify();
    setBusy(true);
    setError(null);
    let made = 0;
    let sentTo = null;
    try {
      for (const d of kept) {
        const { body, send } = bodyOfDraft(d);
        const r = await axios.post(itemsUrl(), body, auth);
        if (send && r.data?.id != null) {
          await axios.post(itemUrl(r.data.id, '/send'), { key: send }, auth);
          sentTo = d.assignee_name || 'them';
        }
        made += 1;
      }
      voiceSeq.current += 1;
      setVoiceDraft(null);
      showToast(made === 1 ? (sentTo ? `Sent to ${String(sentTo).split(/\s+/)[0]}` : 'To-do added') : `${made} to-dos added`);
      if (typedNow) clearQuick();
      setQuickFloat(false);
    } catch (err) {
      const why = err.response?.data?.error || 'That did not go through';
      setVoiceDraft((v) => (v ? { ...v, drafts: kept.slice(made), error: made ? `${made} went in; the rest did not: ${why}` : why } : v));
    } finally {
      setBusy(false);
      await load();
    }
  };

  /*
   * Batch C, AI help: what Break into steps and Checklist from text keep,
   * made one by one through the ordinary routes, in the order shown. A
   * failure part-way says so and keeps the dialog open; what went in stays.
   */
  const addUnder = async (parent, rows, post, words) => {
    setBusy(true);
    setError(null);
    let made = 0;
    let ok = true;
    try {
      for (const r of rows) { await post(r); made += 1; }
    } catch (err) {
      ok = false;
      setError(err.response?.data?.error || 'Not all of them went in');
    } finally {
      setBusy(false);
    }
    await load();
    unfold(parent.id);
    if (ok) { setAssist(null); showToast(words(made)); }
    return ok;
  };
  const addSteps = (parent, steps) => addUnder(parent, steps, (st) => axios.post(itemsUrl(), {
    title: st.title, parent_id: parent.id, due_date: st.due_date || null,
    ...(features.duration && st.duration_minutes ? { duration_minutes: st.duration_minutes } : {})
  }, auth), (n) => (n === 1 ? 'Added 1 sub-task' : `Added ${n} sub-tasks`));
  const addItemsAsSubtasks = (parent, items) => addUnder(parent, items, (it) => axios.post(itemsUrl(), {
    title: it.title.slice(0, 300), notes: it.notes || null, parent_id: parent.id
  }, auth), (n) => (n === 1 ? 'Added 1 sub-task' : `Added ${n} sub-tasks`));
  const addItemsAsLines = (item, items) => addUnder(item, items, (it) => axios.post(itemUrl(item.id, '/checklist'),
    { text: it.notes ? `${it.title} - ${it.notes}`.slice(0, 500) : it.title }, auth),
  (n) => (n === 1 ? 'Added 1 checklist line' : `Added ${n} checklist lines`));
  /** How deep a to-do sits: 0 at the top. */
  const depthOf = (item) => {
    let n = 0;
    for (let p = item?.parent_id; p && n <= MAX_LEVEL; n += 1) p = (findItem(p) || {}).parent_id;
    return n;
  };

  const addSub = (e, parent) => {
    e.preventDefault();
    const title = sub.title.trim();
    if (!title) return;
    return act(async () => {
      await axios.post(itemsUrl(), { title, due_date: sub.due_date || null, parent_id: parent.id }, auth);
      setSub({ title: '', due_date: '' });
      setAddingTo(null);
      unfold(parent.id);
    });
  };

  /* ------------------------------------------------- ticking off, and back */

  const reopen = (ids) => act(() => Promise.all(ids.map((id) =>
    axios.patch(itemUrl(id), { done: false }, auth))));

  /*
   * Ticking off: the PATCH goes at once, the ring fills and the row lingers
   * just long enough to see it, then the list reloads without it. Undo
   * reopens it AND everything that was open under it - the server reopens
   * upwards only, so the sub-tasks are named one by one.
   */
  /*
   * A REPEATING to-do is ticked the same way, and the server answers with
   * `repeated: { from, to }`: it is still open, moved on to its next date.
   * The row plays the same check, then stays where it is with that date, and
   * the toast says when it comes round next; Undo asks for the date back.
   * `repeated: null` is the last time round (an "until" reached) - done for
   * good, like any other. `forever` is "Complete forever": stop_repeat.
   */
  /*
   * 072: a to-do still WAITING on something is ticked only after "complete
   * anyway?" - allowed, but asked (AnywayDialog). Everything that ticks - the
   * ring, Space, a swipe, a board card, its popup - comes through here.
   */
  const complete = (item, opts = {}) => {
    if (!opts.anyway && Array.isArray(item?.waiting_on) && item.waiting_on.length) {
      setAnyway({ item, opts });
      return;
    }
    completeNow(item, opts);
  };

  const completeNow = (item, { forever = false } = {}) => {
    if (completing.has(item.id)) return;
    const ids = [item.id, ...tree.descendantsOf(item.id)];
    setCompleting((prev) => new Set(prev).add(item.id));
    setError(null);
    const sent = axios.patch(itemUrl(item.id), forever ? { done: true, stop_repeat: true } : { done: true }, auth);
    const wait = new Promise((r) => { setTimeout(r, reducedMotion() ? 0 : TICK_MS); });
    Promise.all([sent, wait])
      .then(async ([res]) => {
        await load();
        const moved = !forever && res?.data?.repeated;
        if (moved && moved.to) {
          setFlash(item.id);
          showToast(`Next: ${longDay(String(moved.to).slice(0, 10))}`, { undo: () => undoRepeat(item) });
          return;
        }
        const what = forever ? 'Done for good' : 'Done';
        // A repeating one closed for good (forever, or its "until" reached)
        // is put back by the server's own undo, which restores the repeat
        // and the date it had - a plain reopen would leave it at neither.
        showToast(ids.length > 1 ? `${what}, with ${plural(ids.length - 1, 'sub-task')}` : what,
          { undo: () => (item.repeat ? undoRepeat(item) : reopen(ids)) });
      })
      .catch((err) => setError(err.response?.data?.error || 'That did not go through'))
      .finally(() => setCompleting((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      }));
  };

  /** Undo on "Next: …": the server puts the date it had back (409: nothing to undo). */
  const undoRepeat = (item) => act(() => axios.post(itemUrl(item.id, '/undo-repeat'), {}, auth));

  /*
   * Removing: hidden now, DELETEd when the Undo runs out. A parent still asks
   * first, because its sub-tasks go with it and that is more than one Undo
   * should have to carry unannounced.
   */
  const remove = async (item, list) => {
    const below = underIn(list, item.id);
    if (below.length) {
      const q = `Remove "${item.title}" and its ${plural(below.length, 'sub-task')}?`;
      if (!(await confirm({ title: q, confirmLabel: 'Remove', danger: true }))) return;
    }
    const ids = [item.id, ...below];
    setHidden((prev) => new Set([...prev, ...ids]));
    pending.current.set(item.id, { url: itemUrl(item.id), headers: { ...(auth?.headers || {}) }, ids });
    showToast(below.length ? `Deleted ${ids.length} items` : 'Deleted', {
      undo: () => { pending.current.delete(item.id); unhide(ids); },
      commit: () => commitRef.current(item.id)
    });
  };

  /* --------------------------------------------------------------- editing */

  /** Where in a list and section an item would go last, among the roots. */
  const endOf = (listId, sectionId, id) => buildTree(openShown.filter((t) => t.list_id === listId
    && (t.section_id || null) === (sectionId || null))).childrenOf(null).filter((t) => t.id !== id).length;

  /**
   * 089: a to-do moving INTO Private loses whoever had it (the assignee,
   * the sender's sight of it, its links to other to-dos), and one moving
   * OUT stops being private - both are asked first (PrivateMoveDialog).
   * Answers whether to go on; a move between two ordinary lists is not asked.
   */
  const confirmPrivateMove = (item, toListId) => {
    const from = listById.get(item.list_id) || null;
    const to = listById.get(toListId) || null;
    if (!to || !from || to.id === from.id || (!to.is_private && !from.is_private)) return Promise.resolve(true);
    return new Promise((resolve) => setAskMove({ item, from, to, resolve }));
  };

  const saveEdit = async (item, v, { keep = false } = {}) => {
    const title = String(v.title || '').trim();
    if (!title) return;
    const due_date = v.due_date || null;
    const due_time = v.due_time || null;
    if (due_time && !due_date) { setError('Give it a date as well'); return; }
    const body = {
      title,
      due_date,
      due_time,
      priority: Number(v.priority) || 4,
      ticket_id: v.ticket_id || null,
      notes: String(v.notes || '').trim() || null
    };
    if (withLists) body.labels = v.labels;
    // 072: the client, only when it changed (a contractor is never offered it).
    if (withLists && v.company_id !== undefined && (v.company_id || null) !== (item.company_id || null)) {
      body.company_id = v.company_id || null;
    }
    // 073: the deadline and how long it takes, only when they changed.
    if (withLists && v.deadline !== undefined
        && (v.deadline || null) !== (item.deadline ? String(item.deadline).slice(0, 10) : null)) {
      body.deadline = v.deadline || null;
    }
    if (withLists && v.duration_minutes !== undefined
        && (Number(v.duration_minutes) || null) !== (Number(item.duration_minutes) || null)) {
      body.duration_minutes = Number(v.duration_minutes) || null;
    }
    // Repeat and reminders go only when they changed, so editing a title
    // does not resend (and re-time) every reminder a to-do carries.
    const rep = cleanRepeat(v.repeat);
    if (JSON.stringify(rep) !== JSON.stringify(cleanRepeat(item.repeat))) {
      if (rep && !body.due_date) body.due_date = firstOccurrence(rep, now, due_time);
      body.repeat = rep;
      body.repeat_text = rep ? repeatText(rep, { now, dueDate: body.due_date }) : '';
    }
    const rems = cleanReminders(v.reminders);
    if (JSON.stringify(rems) !== JSON.stringify(cleanReminders(item.reminders))) body.reminders = rems;
    const moved = withLists && v.list_id
      && (v.list_id !== item.list_id || (v.section_id || null) !== (item.section_id || null));
    // 089: into or out of Private is asked first; "no" keeps the rest of the edit, and the place.
    const moveOk = moved && v.list_id !== item.list_id ? await confirmPrivateMove(item, v.list_id) : true;
    return act(async () => {
      await axios.patch(itemUrl(item.id), body, auth);
      // Another list or section is a move: it takes its sub-tasks with it,
      // and arrives last among that section's own.
      if (moved && moveOk) {
        await axios.post(itemUrl(item.id, '/move'), {
          list_id: v.list_id, section_id: v.section_id || null, parent_id: null,
          index: endOf(v.list_id, v.section_id, item.id)
        }, auth);
      }
      if (!keep) setEditing(null);
    });
  };

  // Typing over a title in place: Enter or leaving the field keeps it,
  // Escape puts it back.
  const renameCancel = useRef(false);
  const saveRename = (item, value) => {
    setRenaming(null);
    if (renameCancel.current) { renameCancel.current = false; return; }
    const title = String(value || '').trim();
    if (!title || title === item.title) return;
    setOpen((prev) => prev.map((t) => (t.id === item.id ? { ...t, title } : t)));
    act(() => axios.patch(itemUrl(item.id), { title }, auth));
  };

  const justSwiped = useRef(0);
  const startRename = (item) => {
    if (Date.now() - justSwiped.current < 500) return;
    setEditing(null);
    setRenaming(item.id);
  };

  const schedule = (item, date) => {
    setSheet(null);
    return act(() => axios.patch(itemUrl(item.id), { due_date: date }, auth));
  };

  /*
   * Every move - a drop, an arrow, indent, outdent - is one call: put this
   * item (and everything under it) under that parent at that place. The list
   * is shown moved straight away and replaced by the server's answer; if the
   * server says no, it goes back and the reason is shown. A removal still
   * waiting for its Undo is sent first, so the server is counting the same
   * siblings the screen is.
   *
   * `dest` is the section it lands in, when that is another one than it
   * left - then the call names the list and section too. With lists the
   * server answers with the destination list only, which replaces just that
   * list's rows here.
   */
  const moveTo = async (id, parentId, index, dest = null) => {
    const before = open;
    const item = tree.byId.get(id);
    const scope = withLists && item
      ? { list_id: item.list_id, section_id: dest ? dest.sectionId : item.section_id } : null;
    // Where that is in the server's own order, which also counts the notes
    // in the section and does not float the pinned (notes.js, serverIndex).
    let at = index;
    if (scope) {
      const moving = new Set([id, ...tree.descendantsOf(id)]);
      const inSec = (t) => t.list_id === scope.list_id && (t.section_id || null) === (scope.section_id || null)
        && !moving.has(t.id) && !hidden.has(t.id);
      const shown = buildTree(openShown.filter(inSec)).childrenOf(parentId || null);
      const server = open.filter((t) => inSec(t) && (t.parent_id || null) === (parentId || null)).sort(byPos);
      at = serverIndex(shown, index, server);
    }
    // The notes are not in the tree being moved, so they are kept aside.
    setOpen([...applyMove(openShown, id, parentId, index, scope), ...open.filter(isNote)]);
    if (parentId) unfold(parentId);
    setBusy(true);
    setError(null);
    try {
      await flushDeletes();
      const body = scope && dest
        ? { list_id: scope.list_id, section_id: scope.section_id, parent_id: parentId, index: at }
        : { parent_id: parentId, index: at };
      const r = await axios.post(itemUrl(id, '/move'), body, auth);
      if (Array.isArray(r.data?.open)) {
        if (!scope) setOpen(r.data.open);
        else {
          const back = r.data.open.map((t) => tidy(t, scope.list_id));
          const ids = new Set(back.map((t) => t.id));
          setOpen((prev) => prev.filter((t) => t.list_id !== scope.list_id && !ids.has(t.id)).concat(back));
        }
      }
    } catch (err) {
      setOpen(before);
      setError(err.response?.data?.error || 'That move did not go through');
    } finally {
      setBusy(false);
    }
  };
  moveRef.current = moveTo;

  const moveStep = (row, dir) => moveTo(row.item.id, row.parentId, row.index + dir);
  const indent = (row) => moveTo(row.item.id, row.prev.id, row.tree.childrenOf(row.prev.id).length);
  const outdent = (row) => {
    const grand = row.tree.parentOf(row.parentId);
    const at = row.tree.childrenOf(grand).findIndex((t) => t.id === row.parentId);
    return moveTo(row.item.id, grand, at + 1);
  };

  /* ---------------------------------------------------------------- dragging */

  const startDrag = (e, item, row) => {
    if (busy || editing || dragRef.current) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.preventDefault();
    const rowEl = e.currentTarget.closest('[data-row-id]');
    const rr = rowEl.getBoundingClientRect();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) { /* synthetic */ }
    dragRef.current = {
      id: item.id, title: item.title, pointerId: e.pointerId, type: e.pointerType,
      startX: e.clientX, startY: e.clientY, x: e.clientX, y: e.clientY,
      offX: e.clientX - rr.left, offY: e.clientY - rr.top, width: rr.width,
      started: false, hit: null, raf: 0,
      // Measured in the section it starts in, which is the only tree it is in.
      fromKey: row.secKey, level: row.tree.levelOf(item.id), height: row.tree.heightOf(item.id),
      under: new Set([item.id, ...row.tree.descendantsOf(item.id)])
    };
    setDragId(item.id);
  };

  useEffect(() => {
    if (!dragId) return undefined;

    const indentPx = () => {
      const box = treeBox.current;
      const v = box ? parseFloat(getComputedStyle(box).getPropertyValue('--ctd-indent-px')) : NaN;
      return Number.isFinite(v) && v > 0 ? v : 24;
    };

    // Where would it land if let go at (x, y)? First which section - the
    // last whose top is above the pointer - then where in that section's tree.
    const locate = (x, y) => {
      const d = dragRef.current;
      const box = treeBox.current;
      if (!d || !box) return null;
      const secs = viewRef.current.sections;
      if (!secs.length) return null;
      const secEls = secs.map((s) => box.querySelector(`[data-sec="${s.key}"]`));
      let si = 0;
      secEls.forEach((el, i) => { if (el && el.getBoundingClientRect().top <= y) si = i; });
      const sec = secs[si];
      const secEl = secEls[si];
      if (!secEl) return null;
      const t = sec.tree;
      const list = sec.rows.filter((r) => !d.under.has(r.item.id));
      const boxTop = box.getBoundingClientRect().top;
      const head = secEl.querySelector('[data-sec-head]');
      const where = { secKey: sec.key, sectionId: sec.sectionId };

      // Onto a section's heading, into a folded or an empty one: first among
      // its roots - last, if folded, where the rest cannot be seen.
      if (!list.length || sec.folded || (head && y < head.getBoundingClientRect().bottom)) {
        const roots = t.childrenOf(null).filter((r) => r.id !== d.id);
        const index = sec.folded ? roots.length : 0;
        const line = head ? head.getBoundingClientRect().bottom + 2 : secEl.getBoundingClientRect().top + 3;
        return { ...where, target: { parentId: null, index, depth: 0 }, nestId: null, y: line - boxTop, left: 0 };
      }

      const els = new Map([...secEl.querySelectorAll('[data-row-id]')]
        .map((el) => [el.getAttribute('data-row-id'), el]));
      const rects = list.map((r) => els.get(r.item.id)?.getBoundingClientRect());
      if (rects.some((r) => !r)) return null;
      const ind = indentPx();
      const dx = x - d.startX;
      const want = d.level + Math.round(dx / ind);
      const lineAfter = (a) => (a < 0
        ? rects[0].top - 3
        : rects[a + 1] ? (rects[a].bottom + rects[a + 1].top) / 2 : rects[a].bottom + 3);

      const i = rects.findIndex((r) => y < r.bottom);
      let target = null;
      let after;
      let nestId = null;
      if (i === -1) {
        after = list.length - 1;
        target = gapTarget(t, list, d.id, after, want, d.height);
      } else {
        const r = rects[i];
        const rel = (y - r.top) / r.height;
        const middle = rel > 0.25 && rel < 0.75;
        if (middle && (dx >= ind || x > r.left + r.width / 2)) {
          // Over the middle of a row and to the right: a sub-task of it.
          after = i;
          nestId = list[i].item.id;
          target = nestTarget(t, list[i], d.id, d.height);
        } else {
          after = rel < 0.5 ? i - 1 : i;
          target = gapTarget(t, list, d.id, after, want, d.height);
        }
      }
      if (!target) return null;
      return { ...where, target, nestId, y: lineAfter(after) - boxTop, left: target.depth * ind };
    };

    const update = () => {
      const d = dragRef.current;
      if (!d || !d.started) return;
      const hit = locate(d.x, d.y);
      d.hit = hit;
      setDrop(hit ? { y: hit.y, left: hit.left, nestId: hit.nestId } : null);
    };

    const scrollLoop = () => {
      const d = dragRef.current;
      if (!d || !d.started) return;
      const h = window.innerHeight;
      const speed = d.y < EDGE ? -Math.ceil((EDGE - d.y) / 4)
        : d.y > h - EDGE ? Math.ceil((d.y - (h - EDGE)) / 4) : 0;
      if (!speed) { d.raf = 0; return; }
      window.scrollBy(0, speed);
      update();
      d.raf = requestAnimationFrame(scrollLoop);
    };

    const finish = (commit) => {
      const d = dragRef.current;
      dragRef.current = null;
      if (d?.raf) cancelAnimationFrame(d.raf);
      document.body.classList.remove('ctd-dragging');
      setDrag(null);
      setDrop(null);
      setDragId(null);
      if (!commit || !d?.started || !d.hit) return;
      const { parentId, index } = d.hit.target;
      const across = d.hit.secKey !== d.fromKey;
      if (!across) {
        const sec = viewRef.current.sections.find((s) => s.key === d.fromKey);
        if (!sec || isNoop(sec.tree, d.id, d.hit.target)) return;
      }
      moveRef.current(d.id, parentId || null, index,
        across ? { secKey: d.hit.secKey, sectionId: d.hit.sectionId } : null);
    };

    const onMove = (e) => {
      const d = dragRef.current;
      if (!d || e.pointerId !== d.pointerId) return;
      d.x = e.clientX;
      d.y = e.clientY;
      if (!d.started) {
        if (Math.hypot(d.x - d.startX, d.y - d.startY) < START_AFTER) return;
        d.started = true;
        document.body.classList.add('ctd-dragging');
      }
      e.preventDefault();
      setDrag({ id: d.id, title: d.title, x: d.x - d.offX, y: d.y - d.offY, width: d.width });
      update();
      if (!d.raf && (d.y < EDGE || d.y > window.innerHeight - EDGE)) {
        d.raf = requestAnimationFrame(scrollLoop);
      }
    };
    const onUp = (e) => {
      const d = dragRef.current;
      if (!d || e.pointerId !== d.pointerId) return;
      finish(true);
    };
    const onCancel = (e) => {
      const d = dragRef.current;
      if (!d || e.pointerId !== d.pointerId) return;
      finish(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') finish(false); };

    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      window.removeEventListener('keydown', onKey);
    };
  }, [dragId]);

  // Never leave the page unselectable if the list goes away mid-drag.
  useEffect(() => () => document.body.classList.remove('ctd-dragging'), []);

  /* --------------------------------------------------------------- swiping
   *
   * A phone only (480px and narrower, and a finger): right to tick off, left
   * to reschedule. The row is `touch-action: pan-y`, so an up-and-down
   * gesture stays the browser's - it scrolls, and tells us so with a
   * pointercancel - and only a gesture that is plainly sideways from the
   * start becomes a swipe. The ⠿ handle, the buttons and the fields are left
   * alone: a press there is a drag, a tap or typing.
   */
  const swipeRef = useRef(null);
  const swipeDown = (e, item, isDone) => {
    if (e.pointerType !== 'touch' || isDone || editing === item.id || renaming === item.id) return;
    if (dragRef.current || !phone()) return;
    if (e.target.closest('.ctd-handle, .ctd-actions, .ctd-sub, input, select, textarea, label')) return;
    swipeRef.current = { id: item.id, item, pointerId: e.pointerId,
                         x0: e.clientX, y0: e.clientY, on: false, dx: 0 };
  };
  const swipeMove = (e) => {
    const s = swipeRef.current;
    if (!s || e.pointerId !== s.pointerId) return;
    const dx = e.clientX - s.x0;
    const dy = e.clientY - s.y0;
    if (!s.on) {
      if (Math.abs(dy) > 10 && Math.abs(dy) >= Math.abs(dx)) { swipeRef.current = null; return; }
      if (Math.abs(dx) < 12 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
      s.on = true;
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) { /* synthetic */ }
    }
    s.dx = Math.max(-140, Math.min(140, dx));
    setSwipe({ id: s.id, dx: s.dx });
  };
  const swipeEnd = (e, commit) => {
    const s = swipeRef.current;
    if (!s || e.pointerId !== s.pointerId) return;
    swipeRef.current = null;
    setSwipe(null);
    if (!s.on) return;
    justSwiped.current = Date.now();
    if (!commit) return;
    if (s.dx >= SWIPE_AT) complete(s.item);
    else if (s.dx <= -SWIPE_AT) setSheet(s.item);
  };

  /* ------------------------------------------------------ ?item=, from a reminder */

  /*
   * A reminder's "Open" (and the bell) land on /todos?item=<id>: open the
   * list the to-do is in, unfold whatever hides it, light it up and bring it
   * into view - then drop the ?item= so a reload does not do it again. Not
   * among the open ones? Read the list once more (it may be brand new), then
   * say so rather than land somewhere silently.
   */
  const wantItem = useMemo(() => {
    try { return new URLSearchParams(location.search).get('item'); } catch (err) { return null; }
  }, [location.search]);
  /* ?list=<id> - where the bell's "shared a list with you" lands. */
  const wantList = useMemo(() => {
    try { return new URLSearchParams(location.search).get('list'); } catch (err) { return null; }
  }, [location.search]);
  useEffect(() => {
    if (!wantList || mode !== 'new' || loading) return;
    if (listById.has(wantList)) choose({ kind: 'list', id: wantList });
    else setError('That list is not one of yours any more');
    navigate(location.pathname, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantList, mode, loading, listById]);

  /* ?view=<key> - a link to a view, old keys included (calendar is Upcoming as a calendar). */
  const wantView = useMemo(() => {
    try { return new URLSearchParams(location.search).get('view'); } catch (err) { return null; }
  }, [location.search]);
  useEffect(() => {
    if (!wantView || mode !== 'new') return;
    if ([...VIEW_KEYS, ...SHARE_VIEW_KEYS, ...SMART_VIEW_KEYS, 'notes', 'workload', 'calendar', 'emails'].includes(wantView)) {
      choose({ kind: 'view', key: wantView });
    }
    navigate(location.pathname, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantView, mode]);

  /* ?email=<id> - where the bell's "an email is waiting to be sorted" lands (106): the Emails page, that one open. */
  const wantEmail = useMemo(() => {
    try { return new URLSearchParams(location.search).get('email'); } catch (err) { return null; }
  }, [location.search]);
  useEffect(() => {
    if (!wantEmail || mode !== 'new') return;
    setEmailOpen(wantEmail);
    choose({ kind: 'view', key: 'emails' });
    navigate(location.pathname, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantEmail, mode]);

  /*
   * THE PHONE'S BOTTOM SHEET (Oct 2026): the calm sidebar rises from the
   * foot of the screen under the "Today ▾" switcher. While it is up it has
   * the focus (the current entry first), Tab stays inside, Escape or the
   * scrim closes it and the focus goes back to the switcher; the page
   * behind does not scroll.
   */
  useEffect(() => {
    if (!drawer || !phoneHead) return undefined;
    const box = document.getElementById('tda-side');
    const first = box && (box.querySelector('.tda-nav-item.is-on') || box.querySelector('.tda-nav-item, button'));
    if (first) first.focus({ preventScroll: true });
    const was = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (document.querySelector('.tds-wrap')) return;
        e.preventDefault();
        setDrawer(false);
        return;
      }
      if (e.key !== 'Tab' || !box) return;
      const els = [...box.querySelectorAll('button:not([disabled]), input:not([disabled]), select, [tabindex="0"]')]
        .filter((x) => x.offsetParent !== null);
      if (!els.length) return;
      if (e.shiftKey && document.activeElement === els[0]) { e.preventDefault(); els[els.length - 1].focus(); }
      else if (!e.shiftKey && document.activeElement === els[els.length - 1]) { e.preventDefault(); els[0].focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = was;
      const sw = document.querySelector('.tda-switch');
      if (sw && !document.querySelector('.tds-wrap, .tdk-wrap')) sw.focus({ preventScroll: true });
    };
  }, [drawer, phoneHead]);

  const reloadedFor = useRef(null);
  useEffect(() => {
    if (!wantItem || mode === 'probe' || mode === 'unavailable' || loading) return;
    const it = open.find((t) => String(t.id) === String(wantItem));
    const handed = !it && delegated.find((t) => String(t.id) === String(wantItem));
    if (handed) {
      // One I gave away: under Delegated, open in the panel (its
      // conversation - a handed-on to-do is not mine to edit).
      choose({ kind: 'view', key: 'delegated' });
      openPanel(handed, { scroll: 'talk' });
    } else if (!it) {
      if (reloadedFor.current !== wantItem) { reloadedFor.current = wantItem; load(); return; }
      setError('That to-do is not open any more');
    } else {
      if (withLists) choose({ kind: 'list', id: it.list_id || inboxId });
      else chooseView('all');
      setQuery('');
      for (let p = it.parent_id, guard = 0; p && guard < MAX_LEVEL + 2; guard += 1) {
        setFold(p, false);
        p = open.find((t) => t.id === p)?.parent_id || null;
      }
      if (it.section_id) setSecFold(it.section_id, false);
      setFlash(it.id);
    }
    navigate(location.pathname, { replace: true });
    // The rest are read at the moment it lands; only these say when.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantItem, mode, loading, open]);

  useEffect(() => {
    if (!flash) return undefined;
    const raf = requestAnimationFrame(() => {
      const sel = window.CSS && CSS.escape ? CSS.escape(String(flash)) : String(flash);
      const el = document.querySelector(`.ctd [data-row-id="${sel}"]`);
      if (el && el.scrollIntoView) el.scrollIntoView({ block: 'center' });
    });
    const t = setTimeout(() => setFlash(null), 4000);
    return () => { cancelAnimationFrame(raf); clearTimeout(t); };
  }, [flash]);

  /* -------------------------------------------------------------- keyboard */

  const focusRow = (id) => {
    setFocusId(id);
    requestAnimationFrame(() => {
      // Two arrow presses inside one frame: only the last one's row takes
      // the focus - an earlier frame landing late must not pull it back.
      if (focusIdRef.current !== id) return;
      const el = document.querySelector(`.ctd [data-row-id="${id}"], .ctd [data-done-id="${id}"]`);
      if (el) el.focus();
    });
  };

  const kb = useRef(null);
  kb.current = { displayed, complete, reopen, remove, open, done, focusRow,
                 // Phase 2: while the side panel shows a to-do, the arrows and J/K move it on.
                 panelStep: panelMode && panel ? (item) => { if (item.id !== panel.id) openPanel(item); } : null,
                 edit: (item) => openItem(item),
                 flag: withLists && features.flagged ? (item) => flagItem(item) : null,
                 // Oct 2026: D sets the date, 1-4 the priority.
                 date: (item) => setSheet(item),
                 priority: withLists ? (item, n) => patchItem(item, { priority: n }).catch(showErr) : null,
                 // Q: the box on the page when there is one in sight, else the floating one.
                 quick: () => {
                   const el = quickRef.current;
                   if (el && el.offsetParent !== null && !quickFloat) el.focus();
                   else if (withLists) setQuickFloat(true);
                   else if (el) el.focus();
                 } };

  useEffect(() => {
    const onKey = (e) => {
      if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return;
      const el = e.target;
      if (el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))) return;
      if (dragRef.current || document.querySelector('.ctd-sheet, .tds-dialog, .tdk-wrap')) return;
      const k = kb.current;
      if (e.key === 'q' || e.key === 'Q') {
        e.preventDefault();
        k.quick();
        return;
      }
      const list = k.displayed;
      if (!list.length) return;
      const at = list.findIndex((r) => r.id === focusIdRef.current);
      const down = e.key === 'ArrowDown' || e.key === 'j' || e.key === 'J';
      if (down || e.key === 'ArrowUp' || e.key === 'k' || e.key === 'K') {
        e.preventDefault();
        const step = down ? 1 : -1;
        const next = at < 0 ? (step > 0 ? 0 : list.length - 1)
          : Math.max(0, Math.min(list.length - 1, at + step));
        k.focusRow(list[next].id);
        if (k.panelStep) k.panelStep(list[next].item);
        return;
      }
      if (at < 0) return;
      // Only when the row itself (or nothing in particular) has the focus: a
      // button answers its own Enter and Space.
      const inRow = el === document.body || (el.matches && el.matches('[data-row-id], [data-done-id]'));
      if (!inRow) return;
      const r = list[at];
      const neighbour = (list[at + 1] || list[at - 1] || {}).id || null;
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        if (r.done) k.reopen([r.id]);
        else { k.complete(r.item); k.focusRow(neighbour); }
      } else if (e.key === 'e' || e.key === 'E') {
        e.preventDefault();
        k.edit(r.item);
      } else if ((e.key === 'f' || e.key === 'F') && k.flag && !r.done) {
        // 074: F flags the row, as E edits it.
        e.preventDefault();
        k.flag(r.item);
      } else if ((e.key === 'd' || e.key === 'D') && !r.done) {
        e.preventDefault();
        k.date(r.item);
      } else if (/^[1-4]$/.test(e.key) && k.priority && !r.done) {
        e.preventDefault();
        k.priority(r.item, Number(e.key));
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        k.remove(r.item, r.done ? k.done : k.open);
        k.focusRow(neighbour);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  /*
   * THE PAGE'S OWN KEYS (Oct 2026), before anything else hears them (the
   * capture phase, so the calendar's T does not take "G then T"):
   * Ctrl/Cmd K the palette; ? the shortcuts; / the search; G then a letter
   * goes somewhere (I T U M N A C), V then a letter changes the layout
   * (L B C). Never while typing, and never under a dialog. `navKeys` is
   * filled in where the page knows what it can do (with lists only).
   */
  const navKeys = useRef(null);
  navKeys.current = null;
  useEffect(() => {
    let chord = null;
    let timer = null;
    const onKey = (e) => {
      const k = navKeys.current;
      if (!k) return;
      if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && (e.key === 'k' || e.key === 'K')) {
        if (document.querySelector('.tds-wrap')) return;
        e.preventDefault();
        e.stopPropagation();
        k.palette();
        return;
      }
      if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return;
      const el = e.target;
      if (el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))) { chord = null; return; }
      if (document.querySelector('.tds-wrap, .ctd-sheet, .tdk-wrap, .tda-pop-panel, .tda-menu')) { chord = null; return; }
      if (chord) {
        const c = chord;
        chord = null;
        clearTimeout(timer);
        if (c === 'g' ? k.go(e.key.toLowerCase()) : k.layout(e.key.toLowerCase())) {
          e.preventDefault();
          e.stopPropagation();
        }
        return;
      }
      if (/^[gGvV]$/.test(e.key) && !e.shiftKey) {
        chord = e.key.toLowerCase();
        clearTimeout(timer);
        timer = setTimeout(() => { chord = null; }, 1500);
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      if (e.key === '?') { e.preventDefault(); e.stopPropagation(); k.keys(); return; }
      if (e.key === '/' && k.search) { e.preventDefault(); e.stopPropagation(); k.search(); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => { window.removeEventListener('keydown', onKey, true); clearTimeout(timer); };
  }, []);

  // Escape closes the schedule sheet.
  useEffect(() => {
    if (!sheet) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setSheet(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sheet]);

  /* ------------------------------------------------- lists, labels, sections */

  const allOf = (pred) => [...open, ...done].filter(pred).length;

  const listActions = {
    addList: (name, color) => act(async () => {
      const r = await axios.post(`${NEW}/lists${qs()}`, { name, color }, auth);
      if (r.data?.id) choose({ kind: 'list', id: r.data.id });
    }),
    renameList: (list, name) => {
      setLists((prev) => prev.map((l) => (l.id === list.id ? { ...l, name } : l)));
      return act(() => axios.patch(`${NEW}/lists/${list.id}${qs()}`, { name }, auth));
    },
    colorList: (list, color) => {
      setLists((prev) => prev.map((l) => (l.id === list.id ? { ...l, color } : l)));
      return act(() => axios.patch(`${NEW}/lists/${list.id}${qs()}`, { color }, auth));
    },
    // Among the lists below the Inbox, which cannot move.
    moveList: (from, to) => {
      const others = lists.filter((l) => !l.is_inbox && !l.is_private);
      const moving = others[from];
      if (!moving || from === to) return undefined;
      const next = others.filter((l) => l.id !== moving.id);
      next.splice(to, 0, moving);
      setLists((prev) => [...prev.filter((l) => l.is_inbox || l.is_private), ...next]);
      return act(() => axios.patch(`${NEW}/lists/${moving.id}${qs()}`, { position: to }, auth));
    },
    shareList: (list) => shareList(list),
    // 072: the list's default client - the owner's to set, staff only.
    clientList: features.clients && !isContractor
      ? (list) => setListClient({ id: list.id, value: list.company_id || '' }) : null,
    // Templates (071): the list's open to-dos, as a draft in the editor.
    saveAsTemplate: templatesOn ? (list) => {
      setTplIntent({ kind: 'fromList', listId: list.id, key: `${list.id}:${Date.now()}` });
      choose({ kind: 'templates' });
    } : null,
    archiveList: (list) => act(async () => {
      await axios.patch(`${NEW}/lists/${list.id}${qs()}`, { archived: true }, auth);
      if (selList?.id === list.id) choose({ kind: 'list', id: null });
      setArchived(null);
      showToast(`Archived "${list.name}"`, {
        undo: () => act(() => axios.patch(`${NEW}/lists/${list.id}${qs()}`, { archived: false }, auth))
      });
    }),
    // The server hides archived lists unless asked; asked only when the
    // sidebar's "Archived lists" is opened.
    loadArchived: async () => {
      setArchived(null);
      try {
        const r = await axios.get(`${NEW}/lists${qs({ include_archived: 1 })}`, auth);
        setArchived((Array.isArray(r.data) ? r.data : []).filter((l) => l.archived_at && !l.is_inbox));
      } catch (err) {
        setArchived([]);
        setError(err.response?.data?.error || 'Could not load the archived lists');
      }
    },
    unarchiveList: (list) => act(async () => {
      await axios.patch(`${NEW}/lists/${list.id}${qs()}`, { archived: false }, auth);
      setArchived((prev) => (prev || []).filter((l) => l.id !== list.id));
      showToast(`"${list.name}" is back`);
    }),
    deleteList: async (list) => {
      const n = allOf((t) => t.list_id === list.id);
      const q = n
        ? { title: `Delete the list "${list.name}" and its ${plural(n, 'to-do')}?`, message: 'This cannot be undone.' }
        : { title: `Delete the list "${list.name}"?`, message: 'It has no to-dos.' };
      if (!(await confirm({ ...q, confirmLabel: 'Delete', danger: true }))) return undefined;
      return act(async () => {
        await flushDeletes();
        const r = await axios.delete(`${NEW}/lists/${list.id}${qs()}`, auth);
        if (selList?.id === list.id) choose({ kind: 'list', id: null });
        const gone = Number(r.data?.removed ?? n) || 0;
        showToast(`Deleted "${list.name}"${gone ? ` and ${plural(gone, 'to-do')}` : ''}`);
      });
    },
    // 089: Private cannot be deleted - it is emptied instead, after asking.
    emptyList: (list) => setAskEmpty({ list }),
    addLabel: (name, color) => act(async () => {
      try {
        await axios.post(`${NEW}/labels${qs()}`, { name, color }, auth);
      } catch (err) {
        if (err.response?.status === 409) {
          const e2 = new Error('duplicate');
          e2.response = { data: { error: err.response.data?.error || `There is already a label called "${name}"` } };
          throw e2;
        }
        throw err;
      }
    }),
    renameLabel: (label, name) => {
      setLabels((prev) => prev.map((b) => (b.id === label.id ? { ...b, name } : b)));
      return act(() => axios.patch(`${NEW}/labels/${label.id}${qs()}`, { name }, auth));
    },
    colorLabel: (label, color) => {
      setLabels((prev) => prev.map((b) => (b.id === label.id ? { ...b, color } : b)));
      return act(() => axios.patch(`${NEW}/labels/${label.id}${qs()}`, { color }, auth));
    },
    deleteLabel: async (label) => {
      const n = allOf((t) => (t.labels || []).includes(label.id));
      const q = n
        ? { title: `Delete the label "${label.name}"?`, message: `It comes off ${plural(n, 'to-do')}; the to-dos stay.` }
        : { title: `Delete the label "${label.name}"?` };
      if (!(await confirm({ ...q, confirmLabel: 'Delete', danger: true }))) return undefined;
      return act(async () => {
        await axios.delete(`${NEW}/labels/${label.id}${qs()}`, auth);
        if (selLabel?.id === label.id) choose({ kind: 'list', id: null });
      });
    }
  };

  const sectionsSorted = selList
    ? selList.sections.slice().sort((a, b) => (a.position ?? 0) - (b.position ?? 0)) : [];
  const addSection = (name) => act(async () => {
    await axios.post(`${NEW}/lists/${selList.id}/sections${qs()}`, { name }, auth);
    setAddingSection(false);
  });
  const renameSection = (s, name) => {
    setRenamingSection(null);
    if (!name || name === s.name) return undefined;
    setLists((prev) => prev.map((l) => (l.id !== selList.id ? l
      : { ...l, sections: l.sections.map((x) => (x.id === s.id ? { ...x, name } : x)) })));
    return act(() => axios.patch(`${NEW}/sections/${s.id}${qs()}`, { name }, auth));
  };
  const moveSection = (from, to) => {
    const moving = sectionsSorted[from];
    if (!moving || from === to || to < 0 || to >= sectionsSorted.length) return undefined;
    const next = sectionsSorted.filter((x) => x.id !== moving.id);
    next.splice(to, 0, moving);
    setLists((prev) => prev.map((l) => (l.id !== selList.id ? l
      : { ...l, sections: next.map((x, i) => ({ ...x, position: i })) })));
    return act(() => axios.patch(`${NEW}/sections/${moving.id}${qs()}`, { position: to }, auth));
  };
  const deleteSection = async (s) => {
    const n = allOf((t) => t.section_id === s.id);
    const q = n
      ? { title: `Delete the section "${s.name}"?`, message: `Its ${plural(n, 'to-do')} move to "No section".` }
      : { title: `Delete the section "${s.name}"?` };
    if (!(await confirm({ ...q, confirmLabel: 'Delete', danger: true }))) return undefined;
    return act(() => axios.delete(`${NEW}/sections/${s.id}${qs()}`, auth));
  };
  const secReorder = useReorder((group, from, to) => { if (group === 'sections') moveSection(from, to); });

  /* ----------------------------------------------------------------- display */

  const ticketOf = (item) => (item.ticket_id
    ? { id: item.ticket_id, number: item.ticket_number, title: item.ticket_title }
    : null);

  const openTicket = (id) => navigate(isContractor ? `/my-tickets/${id}` : `/tickets/${id}`);

  /* ------------------------------------------------- sharing and giving */

  /*
   * PHASE 2: ONE PLACE FOR A TO-DO. E, the ⋯'s Edit, a row's comments and
   * checklist, a board card and a calendar block all open the side panel -
   * the only place a to-do opens since the classic inline editor went (26
   * Oct). What was typed in it is saved when it closes or steps to another
   * to-do (flushPanel).
   */
  const flushPanel = () => {
    const l = panelLive.current;
    panelLive.current = null;
    if (!l || !l.dirty || !l.ok || !panel) return;
    const it = findItem(panel.id);
    if (it) saveEdit(it, l.values, { keep: true });
  };
  const openPanel = (item, { tab = 'comments', at = null, scroll = null } = {}) => {
    if (!item) return;
    if (panel && panel.id !== item.id) flushPanel();
    setRenaming(null);
    setEditing(null);
    setPanel({ id: item.id, tab, at, scroll, key: `${item.id}:${Date.now()}` });
  };
  const closePanel = () => { flushPanel(); setPanel(null); };

  /** Open a to-do to edit it: E, the ⋯'s Edit, its deadline chip. */
  const openItem = (item) => {
    if (panelMode) { openPanel(item); return; }
    setRenaming(null);
    setEditing(item.id);
  };

  /** A to-do's comments, checklist and files: the panel, scrolled to them. */
  const openDetail = (item, tab = 'comments') => {
    if (!panelMode) return;
    // 'parts' lands on the checklist and files; 'attach' (a row's Attach…) there too, with the keyboard on the Attach row.
    const files = tab === 'parts' || tab === 'attach';
    openPanel(item, { tab: files ? 'comments' : tab, scroll: files ? 'parts' : 'talk', at: tab === 'attach' ? 'attach' : null });
  };

  /** The to-do behind an id, wherever it is: open, done, or handed on. */
  const findItem = (id) => open.find((t) => t.id === id) || done.find((t) => t.id === id)
    || delegated.find((t) => t.id === id) || null;

  // The thread changed its count: the row's 💬 follows, without a reload.
  const setCount = useCallback((id, n) => {
    const bump = (list) => list.map((t) => (t.id === id ? { ...t, comment_count: n } : t));
    setOpen(bump);
    setDone(bump);
    setDelegated(bump);
  }, []);

  /*
   * GIVING IT TO A MEMBER of a shared list: changed at once. A list of my own
   * cannot have anybody else in it, and the server says so (400, can_send) -
   * then it is offered as a send instead, with whoever was chosen.
   */
  const assign = (item, key) => {
    setError(null);
    const list = listById.get(item.list_id);
    const m = (list?.members || []).find((x) => x.key === key) || null;
    setOpen((prev) => prev.map((t) => (t.id === item.id
      ? { ...t, assignee_key: key, assignee_name: m ? m.name : null, assigned_by_key: key ? selfKey : null } : t)));
    return axios.patch(itemUrl(item.id), { assignee_key: key }, auth)
      .then(() => load())
      .catch((err) => {
        load();
        if (err.response?.status === 400 && err.response?.data?.can_send) {
          needPeople();
          setDialogErr(null);
          setSendFor({ item, preset: m ? { key: m.key, name: m.name, is_contractor: m.is_contractor } : null });
          return;
        }
        setError(err.response?.data?.error || 'That did not go through');
      });
  };

  const startSend = (item) => {
    needPeople();
    setDialogErr(null);
    setSendFor({ item, preset: null });
  };
  const cancelSend = () => setSendFor(null);

  /** Sent: it leaves my lists for their Inbox, and is under Delegated from now on. */
  const send = async (to, note) => {
    const { item } = sendFor;
    setBusy(true);
    setDialogErr(null);
    try {
      await flushDeletes();
      await axios.post(itemUrl(item.id, '/send'), note ? { key: to.key, note } : { key: to.key }, auth);
      setSendFor(null);
      setEditing(null);
      await load();
      showToast(`Sent to ${String(to.name || 'them').split(/\s+/)[0]}`);
    } catch (err) {
      setDialogErr(err.response?.data?.error || 'That could not be sent');
    } finally {
      setBusy(false);
    }
  };

  /** A list's members, as the server answered them, kept on the list. */
  const setMembers = (listId, members) => setLists((prev) => prev.map((l) => (l.id !== listId ? l
    : { ...l, members, shared: members.length > 1 })));

  const shareList = (list) => {
    if (!list || list.is_inbox) return;
    needPeople();
    setDialogErr(null);
    setSharing(list.id);
  };
  const shareCall = async (fn, fail) => {
    setBusy(true);
    setDialogErr(null);
    try { await fn(); } catch (err) { setDialogErr(err.response?.data?.error || fail); } finally { setBusy(false); }
  };
  const addMember = (list, person) => shareCall(async () => {
    const r = await axios.post(`${NEW}/lists/${list.id}/members${qs()}`, { key: person.key, role: 'editor' }, auth);
    if (Array.isArray(r.data?.members)) setMembers(list.id, r.data.members);
    else await load();
  }, `${person.name} could not be added`);
  const removeMember = (list, m) => shareCall(async () => {
    const r = await axios.delete(`${NEW}/lists/${list.id}/members/${encodeURIComponent(m.key)}${qs()}`, auth);
    if (Array.isArray(r.data?.members)) setMembers(list.id, r.data.members);
    else await load();
  }, `${m.name} could not be taken off`);
  const leaveList = async (list) => {
    if (!selfKey) return;
    if (!(await confirm({ title: `Leave "${list.name}"?`, message: 'Its to-dos go from your lists; the others keep them.',
      confirmLabel: 'Leave', danger: true }))) return;
    shareCall(async () => {
      await axios.delete(`${NEW}/lists/${list.id}/members/${encodeURIComponent(selfKey)}${qs()}`, auth);
      setSharing(null);
      if (selList?.id === list.id) choose({ kind: 'list', id: null });
      await load();
      showToast(`You left "${list.name}"`);
    }, 'You could not leave it');
  };

  /** 089: every to-do in a list gone, the list kept (DELETE /lists/:id/items). */
  const emptyList = (list) => act(async () => {
    await flushDeletes();
    const r = await axios.delete(`${NEW}/lists/${list.id}/items${qs()}`, auth);
    await load();
    const gone = Number(r.data?.removed) || 0;
    showToast(`Emptied "${list.name}"${gone ? ` - ${plural(gone, 'to-do')} gone` : ''}`);
  });

  /**
   * Whether the list is mine to run: my own, or a shared one I own. An
   * editor of somebody else's may not move to-dos out of it or send them
   * away (the server answers 403), so those are not offered.
   */
  const ownsList = (list) => {
    if (!list) return true;
    const mine = (list.members || []).find((m) => m.key === selfKey);
    if (mine) return mine.role === 'owner';
    return list.role ? list.role === 'owner' : true;
  };

  /* ------------------------------------------------------- notes (batch 5) */

  /** One item as the server now has it, put back wherever it is shown. */
  const putItem = (row) => {
    if (!row || row.id == null) return;
    const t = tidy(row, inboxId);
    const swap = (list) => list.map((x) => (x.id === t.id ? { ...x, ...t } : x));
    setOpen(swap);
    setDone(swap);   // a done to-do's checklist ticks too
    setArchivedNotes((prev) => (prev ? swap(prev) : prev));
  };
  /** Changed here at once, before the server answers. */
  const patchLocal = (id, fn) => {
    const swap = (list) => list.map((x) => (x.id === id ? fn(x) : x));
    setOpen(swap);
    setDone(swap);
    setArchivedNotes((prev) => (prev ? swap(prev) : prev));
  };
  /** A failure from a card: said at the top of the page. */
  const showErr = (err) => setError(err?.response?.data?.error || 'That did not go through');

  /*
   * A PATCH of one item - a note's words, its colour, its pin; a to-do's
   * colour and pin too. Shown at once; the answer replaces it, and a refusal
   * reads the list again so nothing is left showing what did not happen.
   */
  const patchItem = async (item, body) => {
    setError(null);
    patchLocal(item.id, (x) => ({ ...x, ...body }));
    try {
      const r = await axios.patch(itemUrl(item.id), body, auth);
      putItem(r.data);
      return r.data;
    } catch (err) {
      load();
      throw err;
    }
  };
  const pinItem = (item) => {
    if (!item.pinned) patchLocal(item.id, (x) => ({ ...x, pinned_at: new Date().toISOString() }));
    return patchItem(item, { pinned: !item.pinned });
  };
  /** 074: flag it, or take the flag off - shown at once. */
  const flagItem = (item) => patchItem(item, { flagged: !item.flagged }).catch(showErr);

  /*
   * A CHECKLIST, a line at a time: the server's per-line routes, so two
   * people ticking one shared list do not overwrite each other's ticks. A
   * reorder - and only a reorder - sends the list whole.
   */
  const lineCall = async (item, fn, local) => {
    if (local) patchLocal(item.id, (x) => ({ ...x, checklist: local(x.checklist || []) }));
    try {
      const r = await fn();
      putItem(r.data);
      return r.data;
    } catch (err) {
      load();
      throw err;
    }
  };
  const lineUrl = (item, line) => itemUrl(item.id, `/checklist/${encodeURIComponent(line.id)}`);
  const tickLine = (item, line, done) => lineCall(item,
    () => axios.patch(lineUrl(item, line), { done }, auth),
    (list) => list.map((l) => (l.id === line.id ? { ...l, done } : l)));
  const lineText = (item, line, text) => lineCall(item,
    () => axios.patch(lineUrl(item, line), { text }, auth),
    (list) => list.map((l) => (l.id === line.id ? { ...l, text } : l)));
  const addLine = (item, text, index = null) => lineCall(item,
    () => axios.post(itemUrl(item.id, '/checklist'), index === null ? { text } : { text, index }, auth));
  const delLine = (item, line) => lineCall(item,
    () => axios.delete(lineUrl(item, line), auth),
    (list) => list.filter((l) => l.id !== line.id));
  /** "Show checkboxes" turns the body's lines into a checklist; "Hide" turns them back. */
  const toggleChecklist = (item, toList, body) => (toList
    ? patchItem(item, { checklist: bodyToChecklist(body), notes: null })
    : patchItem(item, { checklist: null, notes: checklistToBody(item.checklist) || null }));

  /*
   * PHOTOS AND VOICE NOTES go up one at a time as multipart `file` (a voice
   * note with how long it is), and are checked against the server's limits
   * first - 10 MB, twenty to a note - so a refusal comes before the upload
   * rather than after it.
   */
  const uploadTo = async (item, files, durationMs = null) => {
    setError(null);
    let have = (item.attachments || []).length;
    for (const f of files) {
      if (f.size > MAX_FILE_BYTES) throw said(`${f.name || 'That file'} is ${fileSize(f.size)}. Files can be up to 10 MB.`);
      if (have >= MAX_FILES) throw said(`${isNote(item) ? 'A note' : 'A to-do'} can carry ${MAX_FILES} files at most.`);
      const fd = new FormData();
      fd.append('file', f, f.name || 'file');
      if (durationMs !== null && durationMs !== undefined) fd.append('duration_ms', String(Math.round(durationMs)));
      let r;
      try {
        r = await axios.post(itemUrl(item.id, '/attachments'), fd, auth);
      } catch (err) {
        if (err.response?.status === 413 && !err.response?.data?.error) throw said('That file is too large: 10 MB at most.');
        throw err;
      }
      have += 1;
      const att = r.data;
      if (att && att.id != null) {
        patchLocal(item.id, (x) => ({ ...x, attachments: [...(x.attachments || []), att],
                                      attachment_count: (x.attachments || []).length + 1 }));
      }
    }
  };
  const removeAttachment = async (item, att) => {
    await axios.delete(`${NEW}/attachments/${encodeURIComponent(att.id)}${qs()}`, auth);
    patchLocal(item.id, (x) => {
      const left = (x.attachments || []).filter((a) => a.id !== att.id);
      return { ...x, attachments: left, attachment_count: left.length };
    });
  };
  // The bytes, with the same sign-in and `as=` as every other call (an <img
  // src> or <audio src> could carry neither).
  const attBlob = useCallback((att) => axios.get(`${NEW}/attachments/${encodeURIComponent(att.id)}${qs()}`,
    { ...(auth || {}), responseType: 'blob' }).then((r) => r.data), [NEW, qs, auth]);
  const attSave = useCallback(async (att) => {
    const blob = await attBlob(att);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = att.file_name || 'file';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  }, [attBlob]);

  /** Where a note goes when it is made: the list open, or the Inbox. */
  const noteHome = () => (selList ? selList.id : inboxId);
  const createNote = (fields, files = []) => act(async () => {
    const body = {
      kind: 'note',
      // A note may go without a title when it has words or a checklist; a
      // photo on its own is called what it is.
      title: fields.title || (fields.notes || fields.checklist ? '' : 'Photo'),
      notes: fields.notes || null,
      checklist: fields.checklist || null,
      color: fields.color || null,
      pinned: Boolean(fields.pinned),
      list_id: noteHome(),
      labels: selLabel ? [selLabel.id] : []
    };
    const r = await axios.post(itemsUrl(), body, auth);
    if (files.length && r.data?.id != null) await uploadTo({ id: r.data.id, attachments: [] }, files);
  });

  const copyNote = (item) => act(async () => {
    await axios.post(itemsUrl(), {
      kind: 'note',
      title: item.title || '',
      notes: item.notes || null,
      checklist: Array.isArray(item.checklist)
        ? item.checklist.map((l) => ({ id: lineId(), text: l.text, done: Boolean(l.done) })) : null,
      color: item.color || null,
      pinned: false,
      list_id: item.list_id,
      labels: item.labels || []
    }, auth);
    showToast('Copied');
  });

  /** Archived: out of every view, into "Archived notes", with an Undo. */
  const archiveNote = (item, on = true) => act(async () => {
    if (on) setOpen((prev) => prev.filter((t) => t.id !== item.id));
    await axios.patch(itemUrl(item.id), { archived: on }, auth);
    if (on) {
      setArchivedNotes((prev) => (prev ? [{ ...item, archived_at: new Date().toISOString() }, ...prev] : prev));
      showToast('Note archived', { undo: () => archiveNote(item, false) });
    } else {
      setArchivedNotes((prev) => (prev ? prev.filter((t) => t.id !== item.id) : prev));
      showToast('Note unarchived');
    }
  });
  const loadArchivedNotes = async () => {
    setArchivedNotes(null);
    try {
      const r = await axios.get(`${NEW}${qs({ include_archived: 1 })}`, auth);
      setArchivedNotes((Array.isArray(r.data?.archived) ? r.data.archived : [])
        .map((t) => tidy(t, inboxId)).filter(isNote));
    } catch (err) {
      setArchivedNotes([]);
      setError(err.response?.data?.error || 'Could not load the archived notes');
    }
  };

  /*
   * A note made a to-do, or a to-do (nothing under it, not done) made a note.
   * IN PLACE: the server flips the kind on the same row, so everything on it
   * - words, checklist, photos, voice notes, files, labels, list, colour,
   * pin, reminders, comments, who it is shared with - stays (Gordon: "it
   * loses all the details, attachments, etc."). The toast says what came
   * with it, and Undo turns it back: a title made up for an untitled note
   * goes again, and an archived note goes back in the archive.
   */
  const convert = (item, kind) => act(async () => {
    setNoteOpen(null);
    const r = await axios.patch(itemUrl(item.id), { kind }, auth);
    const after = r.data && r.data.id ? tidy(r.data, inboxId) : { ...item, kind };
    const back = { kind: kind === 'note' ? 'task' : 'note' };
    if (kind === 'task' && !String(item.title || '').trim()) back.title = '';
    if (kind === 'task' && item.archived_at) back.archived = true;
    showToast(convertedToast(item, after, kind), {
      undo: () => act(async () => { await axios.patch(itemUrl(item.id), back, auth); })
    });
  });

  /** Last among the roots of a list's "No section", whatever their kind. */
  const endOfAll = (listId, id) => open.filter((t) => t.list_id === listId
    && !t.section_id && !t.parent_id && t.id !== id).length;
  const moveToList = async (item, listId) => {
    if (!listId || listId === item.list_id) return undefined;
    if (!(await confirmPrivateMove(item, listId))) return undefined;
    return act(async () => {
      await axios.post(itemUrl(item.id, '/move'), {
        list_id: listId, section_id: null, parent_id: null, index: endOfAll(listId, item.id)
      }, auth);
    });
  };

  const noteRemove = (item) => {
    setNoteOpen(null);
    remove(item, open);
  };

  /** What a card's tools do. */
  const noteActions = {
    open: (n) => { setRenaming(null); setNoteOpen(n.id); },
    pin: (n) => pinItem(n).catch(showErr),
    color: (n, c) => patchItem(n, { color: c }).catch(showErr),
    images: (n, files) => uploadTo(n, files).catch(showErr),
    archive: (n) => archiveNote(n, true),
    unarchive: (n) => archiveNote(n, false),
    remove: noteRemove,
    copy: copyNote,
    toTask: (n) => convert(n, 'task'),
    moveList: (n) => setNoteMeta({ id: n.id, mode: 'list' }),
    labels: labels.length ? (n) => setNoteMeta({ id: n.id, mode: 'labels' }) : null,
    tick: (n, l, done) => tickLine(n, l, done).catch(showErr)
  };

  /** A to-do's checklist and files (TodoParts): the same calls a note's are. */
  const partsOn = { tick: tickLine, lineText, addLine, delLine, upload: uploadTo, removeAttachment };

  /** What the editor's controls do; each answers with a promise. */
  const noteOn = {
    patch: patchItem,
    pin: pinItem,
    color: (n, c) => patchItem(n, { color: c }),
    tick: tickLine,
    lineText,
    addLine,
    delLine,
    reorder: (n, list) => patchItem(n, { checklist: list }),
    toggleChecklist,
    upload: uploadTo,
    removeAttachment,
    archive: (n, on) => { setNoteOpen(null); return archiveNote(n, on); },
    remove: noteRemove,
    copy: (n) => { setNoteOpen(null); return copyNote(n); },
    toTask: (n) => convert(n, 'task'),
    moveList: (n, listId) => moveToList(n, listId),
    labels: (n, ids) => patchItem(n, { labels: ids }),
    reminders: (n, rems) => patchItem(n, { reminders: rems })
  };

  /** A note's card, wherever one is drawn. */
  const noteCard = (n, { archived = false, compact = false } = {}) => {
    const home = listById.get(n.list_id);
    return (
      <NoteCard key={n.id} item={n} actions={noteActions} labelById={labelById}
                list={!compact && !selList && home && !home.is_inbox ? home : null}
                load={attBlob} download={attSave} onError={setError} archived={archived}
                busy={busy} compact={compact} />
    );
  };

  /* ------------------------------------------------------- the board (batch 5) */

  /** The cards of a column as the board draws them: its roots, pinned first. */
  const columnCards = (listId, sectionId, exceptId = null) => {
    const l = listById.get(listId);
    const known = new Set((l?.sections || []).map((s) => s.id));
    return open.filter((t) => t.list_id === listId && !t.parent_id && !hidden.has(t.id) && t.id !== exceptId
      && (sectionId ? t.section_id === sectionId : !t.section_id || !known.has(t.section_id)))
      .sort((a, b) => ((b.pinned ? 1 : 0) - (a.pinned ? 1 : 0)) || byPos(a, b));
  };

  /*
   * A CARD MOVED, by a drag or "Move to column…": the /move every item has,
   * with the place worked out in the server's order. Shown moved at once;
   * the server's answer for the list replaces it, and a refusal puts it
   * back with the reason.
   */
  const boardMove = async (item, sectionId, index, shown = null) => {
    const before = open;
    const others = shown || columnCards(item.list_id, sectionId, item.id);
    const server = open.filter((t) => t.list_id === item.list_id && (t.section_id || null) === (sectionId || null)
      && !t.parent_id && t.id !== item.id && !hidden.has(t.id)).sort(byPos);
    const at = serverIndex(others, index, server);
    const order = server.slice();
    order.splice(at, 0, item);
    const pos = new Map(order.map((t, i) => [t.id, i]));
    const under = new Set(underIn(open, item.id));
    setOpen((prev) => prev.map((t) => {
      if (t.id === item.id || under.has(t.id)) {
        return { ...t, section_id: sectionId || null, ...(pos.has(t.id) ? { position: pos.get(t.id) } : {}) };
      }
      return pos.has(t.id) ? { ...t, position: pos.get(t.id) } : t;
    }));
    setBusy(true);
    setError(null);
    try {
      await flushDeletes();
      const r = await axios.post(itemUrl(item.id, '/move'),
        { section_id: sectionId || null, parent_id: null, index: at }, auth);
      if (Array.isArray(r.data?.open)) {
        const back = r.data.open.map((t) => tidy(t, item.list_id));
        const ids = new Set(back.map((t) => t.id));
        setOpen((prev) => prev.filter((t) => t.list_id !== item.list_id && !ids.has(t.id)).concat(back));
      } else {
        await load();
      }
    } catch (err) {
      setOpen(before);
      setError(err.response?.data?.error || 'That move did not go through');
    } finally {
      setBusy(false);
    }
  };

  /** "+ Add a card": the quick-add line, read for one column of the open list. */
  const addCard = (sectionId, text) => {
    if (!selList) return undefined;
    const p = parseQuickAdd(text, { now, tickets: knownTickets, ignore: [], lists: [], labels,
                                     defaultListId: selList.id, people: reachable });
    if (!p.title) return undefined;
    let due_date = p.due_date || null;
    if (!due_date && p.due_time) due_date = dateForTime(p.due_time, now);
    const body = { title: p.title, due_date, due_time: p.due_time || null, priority: p.priority || 4,
                   ticket_id: p.ticket?.id || null, list_id: selList.id, section_id: sectionId || null };
    if (p.repeat) {
      body.repeat = p.repeat;
      body.repeat_text = repeatText(p.repeat, { now, dueDate: due_date });
    }
    if (p.due_time) body.reminders = [{ offset_minutes: 0 }];
    if (p.assignee && selList.shared && (selList.members || []).some((m) => m.key === p.assignee.key)) {
      body.assignee_key = p.assignee.key;
    }
    return act(async () => {
      const ids = p.labels.filter((b) => b.id).map((b) => b.id);
      for (const b of p.labels.filter((x) => x.isNew)) {
        try {
          const r = await axios.post(`${NEW}/labels${qs()}`, { name: b.name }, auth);
          if (r.data?.id) ids.push(r.data.id);
        } catch (err) {
          if (err.response?.status !== 409) throw err;
        }
      }
      body.labels = [...new Set(ids)];
      await axios.post(itemsUrl(), body, auth);
    });
  };

  /** How a list is shown, List or Board: remembered on the server, per list. */
  const setListView = (list, v) => {
    if ((list.view || 'list') === v) return undefined;
    setLists((prev) => prev.map((l) => (l.id === list.id ? { ...l, view: v } : l)));
    return act(() => axios.patch(`${NEW}/lists/${list.id}${qs()}`, { view: v }, auth));
  };

  /** 072: what a to-do may be linked to - the open to-dos on my lists. */
  const depCandidates = useMemo(() => open.filter((t) => !isNote(t)).map((t) => ({
    id: t.id, title: t.title, listName: listById.get(t.list_id)?.name || null })), [open, listById]);

  /** The conversation of one to-do, as a panel or under its editor. */
  const detailFor = (item, { inline = false, tab = 'comments' } = {}) => (
    <TodoDetail key={`${item.id}-${inline ? 'in' : 'panel'}`} item={item} list={listById.get(item.list_id) || null}
                myKey={selfKey} api={api} inline={inline} busy={busy} startOn={tab === 'attach' ? 'comments' : tab}
                canSend={ownsList(listById.get(item.list_id)) && !listById.get(item.list_id)?.is_private}
                deps={features.dependencies} candidates={depCandidates} onDepsChanged={load}
                clients={features.clients && !isContractor ? companies : null} isAdmin={Boolean(meInfo?.isAdmin)}
                onClient={(it, id) => patchItem(it, { company_id: id }).catch(showErr)}
                onFlag={features.flagged ? flagItem : null}
                assist={canAi && item.access !== 'delegated' && !item.done_at ? (
                  <AssistRow busy={busy}
                             onSteps={!isNote(item) && depthOf(item) < MAX_LEVEL ? () => setAssist({ kind: 'steps', id: item.id }) : null}
                             onChecklist={() => setAssist({ kind: 'checklist', id: item.id })} />) : null}
                parts={isNote(item) ? null : (
                  <TodoParts item={item} on={partsOn} load={attBlob} download={attSave} busy={busy}
                             readOnly={item.access === 'delegated'} focusAttach={tab === 'attach'}
                             canRemove={(a) => !a.uploaded_by_key || a.uploaded_by_key === selfKey
                               || ownsList(listById.get(item.list_id))} />)}
                onClose={() => {}} onCount={setCount}
                onAssign={assign} onSend={startSend}
                onGo={(href) => { closePanel(); navigate(href); }} />
  );

  /*
   * A ROW UNDER DELEGATED: somebody else's now. Who has it and whether it is
   * done, its date, and a way into its comments - nothing to tick, drag or
   * edit, because it is in a list I am not in.
   */
  const renderDelegated = (item) => {
    const when = item.due_date ? dueLabel(String(item.due_date).slice(0, 10), item.due_time, now) : null;
    const state = item.done ? null : dueState(item, todayIso);
    const talk = Number(item.comment_count) || 0;
    return (
      <li key={item.id} className={`ctd-item lvl-0 is-delegated${item.done ? ' is-done' : ''}`}
          data-delegated-id={item.id}>
        <span className="ctd-handle-gap" aria-hidden="true" />
        <span className="ctd-twist-gap" aria-hidden="true" />
        <Avatar name={item.assignee_name} who={item.assignee_key}
                label={`With ${item.assignee_name || 'somebody else'}`} />
        <div className="ctd-body">
          <span className="ctd-title">{item.title}</span>
          <span className="ctd-meta">
            <span className="tds-with">With {item.assignee_name || 'somebody else'}</span>
            <span className={`tds-state is-${item.done ? 'done' : 'open'}`}>{item.done ? 'Done' : 'Open'}</span>
            {when && (
              <span className={`ctd-due is-${state || 'later'} is-static`}>
                <Icon name="calendar" size={12} />{when}
              </span>
            )}
            {talk > 0 && (
              <span className="tds-talk-n" data-comments={talk} title={plural(talk, 'comment')}>
                <Icon name="comment" size={12} />{talk}
              </span>
            )}
          </span>
        </div>
        {/* The 💬 under a mouse; under a finger the ⋯ alone, as on every
            other row, with Comments inside it. */}
        <div className="ctd-actions">
          <button type="button" className="ctd-icon ctd-quick tds-comment-btn"
                  onClick={() => openDetail(item)} aria-label={`Comment on "${item.title}"`}
                  title="Comments and activity"><Icon name="comment" /></button>
          <MoreMenu label={`More for "${item.title}"`} disabled={busy} items={[
            { label: 'Comments', icon: 'comment', onClick: () => openDetail(item) }
          ]} />
        </div>
      </li>
    );
  };

  const moving = useMemo(() => (drag ? new Set([drag.id, ...tree.descendantsOf(drag.id)])
    : null), [drag, tree]);

  // Where a row's list chip shows: wherever rows from several lists meet.
  const chipLists = withLists && !selList && !(selView === 'all' && !searching);
  // Under Completed the day is the group's heading, so a row says only the time.
  const doneText = (v) => {
    if (!v) return 'Done';
    if (curView === 'done') {
      const d = new Date(v);
      return Number.isNaN(d.getTime()) ? 'Done' : `Done ${timeLabel(d)}`;
    }
    return `Done ${day(v)}`;
  };
  // Assignees sit in a column of their own at the right, the same x on every
  // row: reserved on every row of a view where anybody has one. Not under
  // "Assigned to me", where every face would be mine - those say who it is from.
  const showHolder = withLists && curView !== 'assigned';
  const holderCol = showHolder && displayed.some((d) => d.item && d.item.assignee_key);

  /** The fields behind "More": the by-hand version of the quick-add line. */
  const moreFields = () => {
    const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });
    return (
      <>
        <label><span>Due</span>
          <input type="date" aria-label="Due date" value={form.due_date} onChange={on('due_date')} />
        </label>
        <label><span>Time</span>
          <TimeField value={form.due_time} onChange={(t) => setForm((f) => ({ ...f, due_time: t }))} />
        </label>
        <label><span>Priority</span>
          <select aria-label="Priority" value={form.priority} onChange={on('priority')}>
            <option value="">As typed</option>
            {PRIORITIES.map((p) => <option key={p.value} value={String(p.value)}>{p.label}</option>)}
          </select>
        </label>
        {withLists && (
          <ListPicker lists={lists} listId={form.list_id || (parsed.list ? parsed.list.id : defaultListId)}
                      sectionId={form.section_id}
                      onChange={(list_id, section_id) => setForm({ ...form, list_id, section_id })} />
        )}
        <TicketPicker value={form.ticket_id} tickets={tickets} search={withLists ? searchTickets : null}
                      onChange={(ticket_id) => setForm({ ...form, ticket_id })} />
        <label className="ctd-grow"><span>Notes</span>
          <textarea rows={2} maxLength={4000} aria-label="Notes" value={form.notes} onChange={on('notes')} />
        </label>
      </>
    );
  };

  /**
   * One row, in any view. `row` is a tree row (and brings the handle, the
   * fold and the move buttons with it); without one the row is flat, and a
   * sub-task says which task it is in.
   */
  const renderItem = (item, { row = null, isDone = false } = {}) => {
    if (item._completion) {
      return (
        <li key={item.id} className="ctd-item lvl-0 is-done is-occurrence" data-completion-id={item.completion}>
          <span className="ctd-handle-gap" aria-hidden="true" />
          <span className="ctd-twist-gap" aria-hidden="true" />
          <span className="ctd-check p4" aria-hidden="true">
            <input type="checkbox" checked readOnly disabled tabIndex={-1} />
            <span className="ctd-ring" />
          </span>
          <div className="ctd-body">
            <span className="ctd-title">{item.title}</span>
            <span className="ctd-meta">
              {item.advanced && (
                <span className="tdr-mark" title="One time round of a repeating to-do"
                      aria-label="One time round of a repeating to-do"><Icon name="repeat" size={13} /></span>
              )}
              <span className="ctd-done-at">{doneText(item.done_at)}</span>
              {item.due_date && <span className="ctd-in">was due {day(item.due_date)}</span>}
            </span>
          </div>
        </li>
      );
    }
    const level = row ? row.level : 0;
    const t = ticketOf(item);
    const state = isDone ? null : dueState(item, todayIso);
    const p = Number(item.priority) || 4;
    const kidsOpen = isDone ? 0 : tree.childrenOf(item.id).length;
    const kidsDone = doneUnder.get(item.id) || 0;
    const parentTitle = !row && item.parent_id ? titleOf.get(item.parent_id) : null;
    const ticking = completing.has(item.id);
    const sw = swipe && swipe.id === item.id ? swipe.dx : 0;
    const cls = ['ctd-item', `lvl-${level}`, `is-p${p}`];
    if (state === 'late') cls.push('is-late');
    if (isDone) cls.push('is-done');
    if (ticking) cls.push('is-completing');
    if (ticking && item.repeat) cls.push('is-cycling');
    if (flash === item.id) cls.push('is-flash');
    if (moving?.has(item.id)) cls.push('is-moving');
    if (drop?.nestId === item.id) cls.push('is-drop-into');
    if (focusId === item.id) cls.push('is-focused');
    if (editing === item.id) cls.push('is-editing');
    if (sw > 0) cls.push('is-swipe-done');
    if (sw < 0) cls.push('is-swipe-plan');
    if (Math.abs(sw) >= SWIPE_AT) cls.push('is-swipe-armed');
    // Batch 5: a to-do's colour is a stripe down its left edge, and a pinned
    // one says so (it already sits at the top of its group).
    if (withLists && item.color) cls.push('has-color', swatch(item.color));
    if (withLists && item.pinned) cls.push('is-pinned');
    // 072: waiting on something that is still open.
    const waiting = !isDone && withLists && Array.isArray(item.waiting_on) && item.waiting_on.length > 0;
    if (waiting) cls.push('is-blocked');
    // 074: flagged.
    const flagged = !isDone && withLists && features.flagged && Boolean(item.flagged);
    if (flagged) cls.push('is-flagged');
    if (panelMode && panel && panel.id === item.id) cls.push('is-open-in-panel');
    const ids = isDone ? { 'data-done-id': item.id } : { 'data-row-id': item.id, 'data-level': level };
    const when = item.due_date ? dueLabel(String(item.due_date).slice(0, 10), item.due_time, now) : null;
    // 073: how long it takes ("2 h", beside the time), and the deadline.
    const takes = !isDone && withLists && Number(item.duration_minutes) > 0 ? durationLabel(item.duration_minutes) : null;
    // Oct 2026, the chip budget: a deadline shows on the row when it is
    // within a week or has passed (the detail always has it).
    const dlAt = !isDone && withLists && item.deadline ? String(item.deadline).slice(0, 10) : null;
    const dl = dlAt && dlAt <= weekOut ? dlAt : null;
    const dlState = dl ? deadlineState(item, todayIso) : null;
    const canIndent = row && Boolean(row.prev) && level + 1 + tree.heightOf(item.id) <= MAX_LEVEL;
    const canOutdent = row && level > 0;
    const allTags = withLists ? (item.labels || []).map((id) => labelById.get(id)).filter(Boolean) : [];
    // Two labels on the row, then "+1" (the rest are in its tooltip and the detail).
    const tags = allTags.slice(0, 2);
    const moreTags = allTags.slice(2);
    const home = chipLists ? listById.get(item.list_id) : null;
    const homeSection = home && item.section_id
      ? (home.sections || []).find((s) => s.id === item.section_id) : null;
    const rText = item.repeat
      ? (item.repeat_text || repeatText(item.repeat, { now, dueDate: item.due_date ? String(item.due_date).slice(0, 10) : null }))
      : '';
    const rems = !isDone && Array.isArray(item.reminders) ? item.reminders : [];
    const remText = rems.map(reminderLabel).filter(Boolean).join(', ');
    const talk = withLists ? Number(item.comment_count) || 0 : 0;
    const holder = showHolder && item.assignee_key ? item : null;
    const from = withLists && curView === 'assigned' && !isDone ? item.assigned_by_name || null : null;
    // 072: its client - not where every row would say the same (the client's
    // own view, or a list that is that client's).
    const client = withLists && item.company && curView !== 'client'
      && !(selList && selList.company_id && selList.company_id === item.company_id) ? item.company : null;

    return (
      <li key={item.id} className={cls.join(' ')} {...ids} tabIndex={-1}
          style={sw ? { transform: `translateX(${sw}px)` } : undefined}
          onFocus={(e) => { if (e.target === e.currentTarget) setFocusId(item.id); }}
          onPointerDown={(e) => swipeDown(e, item, isDone)}
          onPointerMove={swipeMove}
          onPointerUp={(e) => swipeEnd(e, true)}
          onPointerCancel={(e) => swipeEnd(e, false)}>
        {editing === item.id ? (
          <>
            <TodoEditor item={item} withLists={withLists} lists={lists} labels={labels}
                        companies={features.clients && !isContractor ? companies : null} isAdmin={Boolean(meInfo?.isAdmin)}
                        fixedList={withLists && !ownsList(listById.get(item.list_id))}
                        tickets={tickets} searchTickets={withLists ? searchTickets : null} busy={busy}
                        now={now} onCompleteForever={() => { setEditing(null); complete(item, { forever: true }); }}
                        plan={withLists && features.deadline}
                        onSave={(v) => saveEdit(item, v)} onCancel={() => setEditing(null)} />

          </>
        ) : (
          <>
            {sw !== 0 && (
              <span className="ctd-swipe-hint" aria-hidden="true">{sw > 0 ? '✓ Done' : 'Schedule…'}</span>
            )}
            {/* Under All (row.noDrag) the tree is shown but not dragged:
                the handle's room is kept, so the rows line up with the
                other views', and the fold is All's own. */}
            {row && !row.noDrag ? (
              <span className="ctd-handle" title="Drag to move" aria-hidden="true"
                    onPointerDown={(e) => startDrag(e, item, row)}><Icon name="grip" /></span>
            ) : <span className="ctd-handle-gap" aria-hidden="true" />}
            {row && row.hasKids ? (
              <button type="button" className={`ctd-twist${row.collapsed ? ' is-folded' : ''}`}
                      aria-expanded={!row.collapsed}
                      aria-label={`${row.collapsed ? 'Show' : 'Hide'} sub-tasks of "${item.title}"`}
                      onClick={() => (row.noDrag ? setAllFold(item.id, !row.collapsed) : setFold(item.id, !row.collapsed))}>
                <Icon name="chevronDown" size={14} /></button>
            ) : <span className="ctd-twist-gap" aria-hidden="true" />}
            <label className={`ctd-check p${p}`} title={isDone ? 'Not done after all' : `Done${p < 4 ? ` · P${p}` : ''}`}>
              <input type="checkbox" checked={isDone || ticking} disabled={busy}
                     onChange={() => (isDone ? reopen([item.id]) : complete(item))}
                     aria-label={isDone ? `Mark "${item.title}" as not done` : `Mark "${item.title}" as done`} />
              <span className="ctd-ring" aria-hidden="true" />
            </label>
            <div className="ctd-body">
              {/* The title's own box takes what the line leaves, so the chips
                  sit at its right when they fit and start a line of their
                  own, from the left, when they do not (Oct 2026). */}
              <span className="ctd-head">
              {renaming === item.id ? (
                <input className="ctd-rename" type="text" defaultValue={item.title} maxLength={300}
                       aria-label={`Title of "${item.title}"`} autoFocus
                       onKeyDown={(e) => {
                         if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
                         if (e.key === 'Escape') { e.preventDefault(); renameCancel.current = true; e.currentTarget.blur(); }
                       }}
                       onBlur={(e) => saveRename(item, e.currentTarget.value)} />
              ) : isDone ? (
                <span className="ctd-title">{item.title}</span>
              ) : (
                <button type="button" className="ctd-title" title="Click to rename"
                        onClick={() => startRename(item)}>{item.title}</button>
              )}
              </span>
              {(when || t || item.notes || kidsOpen + kidsDone > 0 || parentTitle || isDone
                || allTags.length || home || rText || rems.length > 0 || talk > 0 || from || (withLists && item.pinned)
                || takes || dl || client || waiting || flagged
                // Its files and checklist (PartsChips) - a to-do with only a file still shows its chip.
                || Boolean(withLists && ((item.attachments || []).length || Number(item.attachment_count) || (item.checklist || []).length))) && (
                <span className="ctd-meta">
                  {flagged && (
                    <button type="button" className="tdf-flag-mark" data-flagged=""
                            aria-label={`Flagged. Take the flag off "${item.title}"`} title="Flagged - click to unflag"
                            onClick={() => flagItem(item)}><Icon name="flagged" size={12} /></button>
                  )}
                  {withLists && item.pinned && !isDone && (
                    <span className="tdn-pin-mark" data-pinned="" title="Pinned" aria-label="Pinned"><PinIcon on /></span>
                  )}
                  {isDone && <span className="ctd-done-at">{doneText(item.done_at)}</span>}
                  {when && !isDone && (
                    <button type="button" className={`ctd-due is-${state}`}
                            onClick={() => setSheet(item)}
                            title={state === 'late' ? 'Overdue - change the date' : 'Change the date'}
                            aria-label={`Due ${when}${state === 'late' ? ', overdue' : ''}. Change the date`}>
                      <Icon name="calendar" size={12} className="ctd-due-icon" />{when}
                    </button>
                  )}
                  {takes && (
                    <span className="ctd-takes" data-duration={item.duration_minutes} title={`Takes ${takes}`}>
                      <Icon name="duration" size={12} />{takes}
                    </span>
                  )}
                  {dl && (
                    <button type="button" className={`ctd-deadline is-${dlState}`} data-deadline={dl}
                            onClick={() => openItem(item)}
                            title={dlState === 'late' ? 'The deadline has passed - change it' : 'The date it must be done by - change it'}
                            aria-label={`Deadline ${longDay(dl)}${dlState === 'late' ? ', passed' : ''}. Change it`}>
                      <Icon name="deadline" size={12} />{`Deadline ${longDay(dl)}`}
                    </button>
                  )}
                  {rText && (
                    <span className="tdr-mark" data-repeat="" title={rText} aria-label={`Repeats: ${rText}`}>
                      <Icon name="repeat" size={13} />
                    </span>
                  )}
                  {rems.length > 0 && (
                    <span className="tdr-mark is-bell" data-reminders={rems.length}
                          title={`Reminders: ${remText}`} aria-label={`Reminders: ${remText}`}>
                      <Icon name="bell" size={13} />
                    </span>
                  )}
                  {waiting && <WaitingChip item={item} onOpen={(it) => openDetail(it)} />}
                  {parentTitle && <span className="ctd-in">in {parentTitle}</span>}
                  {t && (
                    <button type="button" className="ctd-ticket"
                            onClick={() => openTicket(t.id)}
                            title={t.title ? `${t.number} · ${t.title}` : `Open ${t.number}`}>
                      <Icon name="ticket" size={12} />{t.number}
                    </button>
                  )}
                  {withLists && <PartsChips item={item} onOpen={() => openDetail(item, 'parts')} />}
                  {item.notes && (
                    <span className={`ctd-notes${withLists ? ' is-icon' : ''}`} title={item.notes}>
                      <span className="ctd-notes-icon" aria-label="Has notes"><Icon name="note" size={12} /></span>
                      {/* With lists the words are the tooltip's and the detail's (Oct 2026). */}
                      {!withLists && <span className="ctd-notes-text">{item.notes}</span>}
                    </span>
                  )}
                  {/* 097: it came in by email - who sent it, and when. */}
                  {withLists && item.origin === 'email' && (
                    <span className="ctd-origin" data-origin="email" data-origin-from={item.origin_from || ''}
                          title={`From an email${item.origin_from ? ` sent by ${item.origin_from}` : ''}${item.origin_at ? ` on ${fmtDateTime(item.origin_at)}` : ''}`}>
                      <Icon name="send" size={11} />
                      {item.origin_from ? item.origin_from.replace(/@.*$/, '') : 'email'}
                      {item.origin_at ? ` · ${fmtDate(item.origin_at)}` : ''}
                    </span>
                  )}
                  {from && (
                    <span className="tds-from" data-assigned-by={item.assigned_by_key}>
                      <Avatar name={from} who={item.assigned_by_key} size="xs" />
                      from {from}
                    </span>
                  )}
                  {!isDone && kidsOpen + kidsDone > 0 && (
                    <span className="ctd-count"
                          title={`${kidsDone} of ${kidsDone + kidsOpen} sub-tasks done`}>
                      {kidsDone}/{kidsDone + kidsOpen}
                    </span>
                  )}
                  {/* A label chip opens the label, as a client chip opens the client (Oct 2026). */}
                  {tags.map((b) => (
                    <button key={b.id} type="button" className="tda-tag" data-label-chip={b.id}
                            title={`Show everything labelled @${b.name}`} aria-label={`Label ${b.name}: show everything with it`}
                            onClick={() => choose({ kind: 'label', id: b.id })}>
                      <Icon name="tag" size={12} className={`tda-tag-ic ${swatch(b.color)}`} />{b.name}
                    </button>
                  ))}
                  {moreTags.length > 0 && (
                    <span className="tda-tag is-more" title={moreTags.map((b) => `@${b.name}`).join(' ')}
                          data-more-labels={moreTags.length}>{`+${moreTags.length}`}</span>
                  )}
                  {client && <ClientChip company={client} onClick={(c) => choose({ kind: 'client', id: c.id })} />}
                  {talk > 0 && (
                    <button type="button" className="tds-talk" data-comments={talk}
                            aria-label={`${plural(talk, 'comment')} on "${item.title}"`}
                            title={plural(talk, 'comment')}
                            onClick={() => openDetail(item)}><Icon name="comment" size={12} />{talk}</button>
                  )}
                  {home && (
                    <span className="tda-home" data-list-chip={home.id}
                          title={`In ${home.name}${homeSection ? ` › ${homeSection.name}` : ''}`}>
                      <span className={`tda-dot ${swatch(home.color)}`} aria-hidden="true" />
                      <span className="tda-home-name">{home.name}</span>
                      {homeSection && <span className="tda-home-sec">{` › ${homeSection.name}`}</span>}
                    </span>
                  )}
                </span>
              )}
            </div>
            {holder ? (
              <span className="tds-holder" data-assignee={holder.assignee_key}>
                <Avatar name={holder.assignee_name} who={holder.assignee_key}
                        label={`Assigned to ${holder.assignee_key === selfKey ? 'you' : holder.assignee_name || 'somebody'}`} />
              </span>
            ) : holderCol && <span className="tds-holder-gap" aria-hidden="true" />}
            {/* Oct 2026: three tools under a mouse - Schedule, Flag and the ⋯ -
                and the ⋯ in groups. Edit is E or the ⋯; add a sub-task and
                the comments are in the ⋯. Under a finger, the ⋯ alone. */}
            <div className={`ctd-actions${holderCol ? ' has-holder' : ''}`}>
              {!isDone && (
                <button type="button" className="ctd-icon ctd-quick" disabled={busy}
                        onClick={() => setSheet(item)}
                        aria-label={`Schedule "${item.title}"`} title="Schedule (d)"><Icon name="calendar" /></button>
              )}
              {!isDone && withLists && features.flagged && (
                <button type="button" className={`ctd-icon ctd-quick tdf-flag-btn${item.flagged ? ' is-on' : ''}`} disabled={busy}
                        aria-pressed={Boolean(item.flagged)} onClick={() => flagItem(item)}
                        aria-label={item.flagged ? `Unflag "${item.title}"` : `Flag "${item.title}"`}
                        title={item.flagged ? 'Unflag (f)' : 'Flag (f)'}><Icon name={item.flagged ? 'flagged' : 'flag'} /></button>
              )}
              <MoreMenu label={`More for "${item.title}"`} disabled={busy} items={[
                !isDone && { label: 'Edit', icon: 'edit', hint: 'E', onClick: () => openItem(item) },
                !isDone && { label: 'Schedule…', icon: 'calendar', hint: 'D', onClick: () => setSheet(item) },
                !isDone && withLists && { label: 'Priority', icon: 'p1', sub: (close) => (
                  <div className="tda-prio-pick" role="group" aria-label={`Priority of "${item.title}"`}>
                    {[1, 2, 3, 4].map((n) => (
                      <button key={n} type="button" className={`tda-prio p${n}`} aria-pressed={p === n}
                              aria-label={`Priority ${n}`} title={`P${n} (${n})`}
                              onClick={() => { close(); if (p !== n) patchItem(item, { priority: n }).catch(showErr); }}>
                        {`P${n}`}
                      </button>
                    ))}
                  </div>) },
                !isDone && withLists && features.flagged && { label: item.flagged ? 'Unflag' : 'Flag', icon: 'flagged', hint: 'F',
                                                              onClick: () => flagItem(item) },
                { sep: true },
                row && level < MAX_LEVEL && { label: 'Add sub-task', icon: 'plus', onClick: () => {
                  setAddingTo(item.id); setSub({ title: '', due_date: '' }); } },
                withLists && { label: 'Comments', icon: 'comment', onClick: () => openDetail(item) },
                !isDone && withLists && { label: 'Attach…', icon: 'paperclip', onClick: () => openDetail(item, 'attach') },
                // Batch C: AI help (lib/todoAssistGate.js).
                !isDone && canAi && level < MAX_LEVEL && { label: 'Break into steps', icon: 'sparkle',
                                                          onClick: () => setAssist({ kind: 'steps', id: item.id }) },
                !isDone && canAi && { label: 'Checklist from text', icon: 'steps',
                                      onClick: () => setAssist({ kind: 'checklist', id: item.id }) },
                !isDone && item.repeat && { label: 'Complete forever', icon: 'done', onClick: () => complete(item, { forever: true }) },
                row && { sep: true },
                row && row.index > 0 && { label: 'Move up', icon: 'arrowUp', onClick: () => moveStep(row, -1) },
                row && row.index < row.count - 1 && { label: 'Move down', icon: 'arrowDown', onClick: () => moveStep(row, 1) },
                canIndent && { label: 'Indent', icon: 'indent', title: 'A sub-task of the one above', onClick: () => indent(row) },
                canOutdent && { label: 'Outdent', icon: 'outdent', title: 'Out of its parent', onClick: () => outdent(row) },
                !isDone && withLists && { sep: true },
                !isDone && withLists && { label: item.pinned ? 'Unpin' : 'Pin to the top', icon: 'pin',
                                          onClick: () => pinItem(item).catch(showErr) },
                !isDone && withLists && { label: 'Colour', icon: 'tag', sub: (close) => (
                  <ColorPicker name={`"${item.title}"`} value={item.color}
                               onPick={(c) => { close(); patchItem(item, { color: c }).catch(showErr); }} />) },
                !isDone && withLists && kidsOpen === 0 && !item.parent_id
                  && { label: 'Convert to note', icon: 'note', onClick: () => convert(item, 'note') },
                { sep: true },
                { label: 'Delete', icon: 'trash', danger: true, hint: 'Del', onClick: () => remove(item, isDone ? done : open) }
              ]} />
            </div>
            {row && addingTo === item.id && (
              <form className="ctd-sub" onSubmit={(e) => addSub(e, item)}>
                <input type="text" value={sub.title} placeholder="Sub-task…" autoFocus
                       aria-label={`New sub-task of "${item.title}"`} maxLength={300}
                       onChange={(e) => setSub({ ...sub, title: e.target.value })} />
                <input type="date" value={sub.due_date} aria-label="Sub-task due"
                       onChange={(e) => setSub({ ...sub, due_date: e.target.value })} />
                <button type="submit" className="btn btn-primary btn-small"
                        disabled={busy || !sub.title.trim()}>Add</button>
                <button type="button" className="btn btn-secondary btn-small"
                        onClick={() => setAddingTo(null)}>Cancel</button>
              </form>
            )}
          </>
        )}
      </li>
    );
  };

  // An empty view: a small drawing, a headline, one line of help and - where
  // there is something obvious to do - the button that does it.
  const EMPTY = {
    today: { icon: 'done', title: "You're all caught up", text: 'Nothing due today or overdue. Enjoy the clear list.', add: true,
             example: 'Call the bank today 10am' },
    upcoming: { icon: 'upcoming', title: 'Nothing scheduled',
                text: 'Give a to-do a day - type "tomorrow" or "fri 3pm" when you add it.', add: true,
                example: 'Payroll run fri 9am every month' },
    p1: { icon: 'p1', title: 'No Priority 1 to-dos', text: 'Type "p1" when you add one that cannot wait.', add: true,
          example: 'Fix the printer today p1' },
    done: { icon: 'done', title: 'Nothing completed yet', text: 'What you tick off shows here, by day.' },
    all: { icon: 'all', title: 'Nothing to do',
           text: 'Add a part to order, a call to return, a tool to bring.', add: true, example: 'Order toner #Office @supplies' },
    assigned: { icon: 'assigned', title: 'Nothing assigned to you',
                text: 'When somebody gives you a to-do, it shows here as well as in its list.' },
    delegated: { icon: 'delegated', title: 'Nothing handed on',
                 text: 'Send a to-do to somebody - "+Name" as you add it - and follow it here.' },
    // 074's smart lists
    flagged: { icon: 'flagged', title: 'Nothing flagged',
               text: 'Flag a to-do that matters - the flag on its row, or press F - and it shows here.' },
    overdue: { icon: 'done', title: 'Nothing overdue', text: 'Every date and deadline is still ahead. Well done.' },
    scheduled: { icon: 'calendar', title: 'Nothing scheduled', text: 'To-dos with a date or a deadline show here, soonest first.', add: true },
    nodate: { icon: 'nodate', title: 'Everything has a date', text: 'To-dos with no date and no deadline would show here.' },
    waiting: { icon: 'waiting', title: 'Nothing waiting', text: 'A to-do held up by another one shows here until it is free.' }
  };
  // Phase 2: an empty page offers one line of what to try (`example`).
  const emptyState = ({ icon, title, text, add: offerAdd = false, example = null }) => (
    <div className="ctd-empty ctd-empty-state" role="status">
      <span className="ctd-empty-ic" aria-hidden="true"><Icon name={icon} size={28} /></span>
      <p className="ctd-empty-title">{title}</p>
      <p className="ctd-empty-text">{text}</p>
      {withLists && example && <p className="ctd-empty-example">Try <q>{example}</q></p>}
      {offerAdd && (
        <button type="button" className="btn btn-primary ctd-empty-add"
                onClick={() => kb.current.quick()}>
          <Icon name="plus" size={14} />Add a to-do
        </button>
      )}
    </div>
  );

  const nextMonday = isoDay(addDays(now, ((8 - now.getDay()) % 7) || 7));
  const viewLabel = selView === 'notes' && notesArchive ? 'Archived notes'
    // Oct 2026: Assigned to me, Delegated and Workload are one page's tabs.
    : withLists && ASSIGNED_TABS.includes(selView) ? 'Assigned'
    : selView === 'all' && withLists ? 'All to-dos'
    : selView === 'workload' ? 'Workload' : selView === 'calendar' ? 'Calendar' : selView === 'emails' ? 'Emails'
      : [...VIEWS, ...SHARE_VIEWS, NOTES_VIEW, ...SMART_VIEWS].find((v) => v.key === selView)?.label || 'To do';
  const headTitle = !withLists ? viewLabel
    : onTemplates ? 'Templates'
      : onFilters ? (filterEdit ? (filterEdit.id ? 'Edit filter' : 'New filter') : smart.on ? 'Filters & labels' : 'Labels')
        : selFilterId ? (selFilter ? selFilter.name : 'Filter')
          : selList ? selList.name : selLabel ? `@${selLabel.name}` : selClient ? selClient.name : viewLabel;
  const headCount = !withLists ? null
    : onTemplates || onFilters ? null
    : selFilterId ? (groups || []).reduce((k, g) => k + g.items.length, 0)
    : selList ? listCounts.get(selList.id) || 0
      : selLabel ? labelCounts.get(selLabel.id) || 0
        : selClient ? openShown.filter((t) => t.company_id === selClient.id).length
          : selView === 'workload' || selView === 'calendar' ? null : selView === 'emails' ? (emailsWaiting || null) : allCounts[selView];

  /* ---------------------------------------------- not set up, or not yet known */

  if (mode === 'unavailable') {
    return (
      <div className="ctd tda">
        <div className="ctd-empty tda-unset" role="status">
          <p className="tda-unset-title">To-dos are not set up on this server yet.</p>
          <p>
            Your own lists, sections and labels arrive as soon as the server has
            been updated. Nothing needs doing on your side, and nothing is lost.
          </p>
        </div>
      </div>
    );
  }
  if (mode === 'probe') {
    return (
      <div className="ctd tda">
        {error ? <p className="ctd-error" role="alert">{error}</p> : <p className="ctd-quiet">Loading…</p>}
      </div>
    );
  }

  /* --------------------------------------------------------------- pieces */

  const chipsBlock = (parsed.chips.length > 0 || remindAtTime || asNote) && (
    <div className="ctd-chips" aria-label="Understood from what you typed">
      {asNote && <span data-chip="note" className="ctd-chip is-note"><Icon name="note" size={12} />Note</span>}
      {parsed.chips.map((c) => {
        const key = c.key || c.kind;
        const isNew = c.kind === 'label' && c.isNew;
        const yes = isNew && confirmed.has(nameKey(c.label));
        const text = c.kind === 'label' ? `@${c.label}` : c.kind === 'section' ? `/ ${c.label}`
          : c.kind === 'repeat' ? String(c.label).replace(/^↻\s*/, '')
            : c.kind === 'deadline' ? `Deadline ${c.label}` : c.label;
        const icon = { date: 'calendar', time: 'clock', repeat: 'repeat', assignee: 'person', ticket: 'ticket',
                       deadline: 'deadline', duration: 'duration',
                       priority: 'flag', section: null, label: null, list: null }[c.kind] || null;
        return (
          <span key={key} data-chip={c.kind}
                title={c.kind === 'repeat' && c.first ? `Repeats; the first is ${longDay(c.first)}` : undefined}
                className={`ctd-chip is-${c.kind}${c.kind === 'priority' ? ` p${parsed.priority}` : ''}${isNew ? ` is-new${yes ? ' is-confirmed' : ''}` : ''}${c.color ? ` ${swatch(c.color)}` : ''}`}>
            {icon && <Icon name={icon} size={12} />}
            {text}
            {isNew && !yes && <span className="tda-chip-note">new?</span>}
            {c.kind === 'assignee' && !c.member && (
              <span className="tda-chip-note" title={`Not in this list: it will be sent to ${c.label}'s own Inbox`}>send</span>
            )}
            {isNew && !yes && (
              <button type="button" className="tda-chip-yes" aria-label={`Create label ${c.label}`}
                      title={`Make "${c.label}" a label`}
                      onClick={() => { setConfirmed((prev) => new Set(prev).add(nameKey(c.label))); setHint(null); }}><Icon name="check" size={12} /></button>
            )}
            <button type="button" aria-label={`Remove ${c.kind} ${c.label}`}
                    title={`Not a ${c.kind}: keep "${c.text}" in the title`}
                    onClick={() => { setIgnore((prev) => [...prev, key]); setHint(null); }}><Icon name="close" size={12} /></button>
          </span>
        );
      })}
      {/* Not read from the words but added because of them: a time gets a
          reminder at that time. Taking it off puts no words back. */}
      {remindAtTime && !asNote && (
        <span data-chip="reminder" className="ctd-chip is-reminder"
              title="You will be reminded when it is due">
          <Icon name="bell" size={12} />{`At ${quickTime}`}
          <button type="button" aria-label="Remove the reminder at the time" title="No reminder for this one"
                  onClick={() => { setIgnore((prev) => [...prev, 'reminder']); setHint(null); }}><Icon name="close" size={12} /></button>
        </span>
      )}
    </div>
  );

  // Batch C: while the mic is on, and while its to-do is previewed, quick
  // add's box is the recording panel and then the preview card.
  const voiceBox = (canVoice || canAi) && (dictation.busy || voiceReading || voiceDraft) ? (
    <div className="ctd-add card tva-voice-host" data-voice-host="">
      {voiceDraft ? (
        <VoiceDraftList drafts={voiceDraft.drafts} heard={voiceDraft.heard} typed={Boolean(voiceDraft.typed)} note={voiceDraft.note || null}
                        languageName={voiceDraft.typed ? null : languageName(voiceDraft.language)} lists={lists} labels={labels} membersOf={membersOf}
                        defaultListId={defaultListId} now={now} plan={planOn} busy={busy} error={voiceDraft.error || null}
                        onCreate={createFromDrafts} onCancel={backToQuick}
                        onAgain={canVoice && !voiceDraft.typed ? () => { voiceSeq.current += 1; setVoiceDraft(null); dictation.start(); } : null} />
      ) : (
        <VoicePanel dictation={panelDictation} languages={languagesFor(features)} language={voiceLang}
                    onLanguage={setVoiceLang} reading={voiceReading} />
      )}
    </div>
  ) : null;

  /*
   * QUICK ADD. With lists (Oct 2026) it is slim: one line - a "+", the box,
   * the mic - with Add and More beside it, and the syntax shown under it
   * only while the box is focused and empty. The same form is what Q, the
   * sidebar's "+ Add to-do" and the phone's + open floating over any page
   * (`floating`); it closes once the to-do is in.
   */
  const quickForm = ({ floating = false } = {}) => voiceBox || (
    <form className={`ctd-add${withLists ? ' is-slim' : ' card'}${floating ? ' is-floating' : ''}`}
          onSubmit={(e) => {
            const p = add(e);
            if (floating && p && p.then) p.then((ok) => { if (ok) setQuickFloat(false); });
          }}>
      <div className="ctd-add-row">
        {withLists && <span className="ctd-add-plus" aria-hidden="true"><Icon name="plus" size={16} /></span>}
        <input
          ref={quickRef}
          type="text"
          value={quick}
          autoFocus={floating}
          placeholder={narrow || withLists ? 'Add a to-do' : 'Add a to-do - try "Order cable tomorrow 3pm p1"'}
          aria-label="What needs doing"
          aria-describedby={withLists ? 'ctd-add-hint' : undefined}
          title="Press q to come back here"
          maxLength={300}
          onChange={(e) => { setQuick(e.target.value); setHint(null); if (!e.target.value) { setIgnore([]); setConfirmed(new Set()); } }}
          onKeyDown={(e) => {
            if (e.key === 'Escape' && (quick || more || !floating)) { e.preventDefault(); e.stopPropagation(); clearQuick(); }
            // Shift+Enter: let AI read the sentence (several to-dos, the days worked out).
            if (e.key === 'Enter' && e.shiftKey && canAi && quick.trim() && !e.nativeEvent.isComposing) { e.preventDefault(); askAi(quick); }
          }}
        />
        {canVoice && !quick && <MicButton dictation={dictation} disabled={busy} />}
        {canAi && quick.trim() && withLists && (
          <button type="button" className="tva-mic tva-ask" data-ask-ai="" disabled={busy || voiceReading}
                  aria-label="Read with AI" title="Read with AI (Shift+Enter): one sentence can be several to-dos, with the days worked out"
                  onClick={() => askAi(quick)}>
            <Icon name="sparkle" size={16} />
          </button>
        )}
        {withLists && (
          <>
            <button type="button" className={`ctd-attach-btn${quickFiles.length ? ' has-files' : ''}`} data-quick-attach="" disabled={busy}
                    aria-label={quickFiles.length ? `${plural(quickFiles.length, 'file')} to attach - add more` : 'Attach a file or picture to this to-do'}
                    title="Attach a file or picture - added once the to-do is in"
                    onClick={() => { if (quickFileInput.current) quickFileInput.current.click(); }}>
              <Icon name="paperclip" size={16} />
              {quickFiles.length > 0 && <span className="ctd-attach-n">{quickFiles.length}</span>}
            </button>
            <input ref={quickFileInput} type="file" multiple hidden tabIndex={-1} aria-label="Files to attach to the new to-do"
                   onChange={(e) => {
                     const list = [...(e.target.files || [])];
                     e.target.value = '';
                     if (list.length) setQuickFiles((prev) => [...prev, ...list].slice(0, MAX_FILES));
                   }} />
          </>
        )}
        <button type="submit" className="btn btn-primary ctd-add-go" disabled={busy || !parsed.title}>
          Add
        </button>
        <button type="button" className="ctd-more" aria-expanded={more}
                onClick={() => setMore((v) => !v)}>
          {more ? 'Less' : 'More'}
        </button>
      </div>
      {withLists && !quick && (
        <p className="ctd-add-hint" id="ctd-add-hint">
          Try <q>Order cable tomorrow 3pm p1 #List @label +Name</q>
        </p>
      )}
      {chipsBlock}
      {quickFiles.length > 0 && (
        <ul className="ctd-quick-files" aria-label="Files to attach once the to-do is in">
          {quickFiles.map((f, i) => (
            <li key={`${f.name}-${i}`} className="ctd-quick-file" data-quick-file={f.name}>
              <Icon name={/^image\//.test(f.type) ? 'image' : 'paperclip'} size={12} />
              <span className="ctd-quick-file-name">{f.name}</span>
              <span className="ctd-quick-file-size">{fileSize(f.size)}</span>
              <button type="button" className="tdn-remove" aria-label={`Don't attach ${f.name}`} title="Remove"
                      onClick={() => setQuickFiles((prev) => prev.filter((_, j) => j !== i))}><Icon name="close" size={12} /></button>
            </li>
          ))}
        </ul>
      )}
      {hint && <p className="tda-hint" role="status">{hint}</p>}
      {voiceErr && canVoice && <p className="tda-hint is-voice" role="alert" data-voice-error="">{voiceErr}</p>}
      {more && <div className="ctd-add-more">{moreFields()}</div>}
    </form>
  );
  const addForm = quickForm();

  /*
   * 072: THE CLIENT FILTER, over All and a client's own view: "everything
   * open for Alpha". The companies for staff; for a contractor, the clients
   * on what they can already see.
   */
  const clientOptions = [...clientById.values()].filter((c) => !c.archived || open.some((t) => t.company_id === c.id))
    .sort((a, b) => String(a.name).localeCompare(String(b.name)));
  const clientBar = withLists && features.clients && clientOptions.length > 0
    && (curView === 'all' || curView === 'client') && (
    <div className="tdc-bar" role="group" aria-label="Filter by client">
      <label className="tdc-bar-pick">
        <span className="tdc-bar-label">Client</span>{' '}
        <select aria-label="Show the to-dos for a client" value={selClient ? selClient.id : ''}
                onChange={(e) => choose(e.target.value ? { kind: 'client', id: e.target.value } : { kind: 'view', key: 'all' })}>
          <option value="">All clients</option>
          {clientOptions.map((c) => (
            <option key={c.id} value={c.id}>{c.name}{c.archived ? ' (archived)' : ''}</option>
          ))}
        </select>
      </label>
      {selClient && (
        <button type="button" className="tda-add-line tdc-clear" onClick={() => choose({ kind: 'view', key: 'all' })}>
          Show every client
        </button>
      )}
    </div>
  );

  /*
   * 073: THE CALENDAR (TodoCalendar.js). The to-dos are the ones this page
   * already has (searched, if a search is typed); a move or a new length is
   * shown at once and sent as a PATCH; a click on an empty slot opens the
   * editor for a new to-do there; a click on a block opens it.
   */
  const planAt = (item, fields) => {
    setOpen((prev) => prev.map((t) => (t.id === item.id ? { ...t, ...fields } : t)));
    return act(() => axios.patch(itemUrl(item.id), fields, auth));
  };
  const draftAt = (d) => setDrafting({
    title: '', due_date: d.due_date, due_time: d.due_time || '', priority: 4,
    list_id: d.list_id || defaultListId, section_id: null, labels: d.labels || [],
    company_id: d.company_id || null,
    duration_minutes: d.due_time && features.duration ? 30 : null
  });
  const createFrom = (v) => {
    const title = String(v.title || '').trim();
    if (!title) return undefined;
    const body = {
      title, due_date: v.due_date || null, due_time: v.due_time || null, priority: Number(v.priority) || 4,
      ticket_id: v.ticket_id || null, notes: String(v.notes || '').trim() || null,
      list_id: v.list_id || defaultListId, section_id: v.section_id || null, labels: v.labels || []
    };
    if (body.due_time && !body.due_date) body.due_date = todayIso;
    if (v.company_id && features.clients && !isContractor) body.company_id = v.company_id;
    if (features.deadline && v.deadline) body.deadline = v.deadline;
    if (features.duration && Number(v.duration_minutes)) body.duration_minutes = Number(v.duration_minutes);
    const rep = cleanRepeat(v.repeat);
    if (rep) {
      if (!body.due_date) body.due_date = firstOccurrence(rep, now, body.due_time);
      body.repeat = rep;
      body.repeat_text = repeatText(rep, { now, dueDate: body.due_date });
    }
    const rems = cleanReminders(v.reminders);
    if (rems.length) body.reminders = rems;
    return act(async () => {
      await axios.post(itemsUrl(), body, auth);
      showToast('Added');
    });
  };
  // 077: "Add to my to-dos" on an event's card - the ordinary create, with
  // the event's day and time and its link in the notes. It throws when the
  // create fails, so the card can say so.
  const addFromEvent = async (draft) => {
    const title = String(draft.title || '').trim();
    if (!title) return;
    const body = { title, due_date: draft.due_date || null, due_time: draft.due_time || null, priority: 4,
                   notes: draft.notes || null, list_id: defaultListId, section_id: null, labels: [] };
    if (features.duration && Number(draft.duration_minutes)) body.duration_minutes = Number(draft.duration_minutes);
    await axios.post(itemsUrl(), body, auth);
    showToast('Added to your to-dos');
    loadRef.current();
  };
  const calendarBlock = ({ store = '', defaultView = null, preset = null, only = null } = {}) => (
    <TodoCalendar key={`cal-${store}`} store={store} defaultView={defaultView} preset={preset}
                  items={(only || openShown).filter((t) => !searching || matches(t, query))}
                  lists={lists} labels={labels} companies={features.clients ? clientOptions : null}
                  todayIso={todayIso} now={now} loadFeed={calendarFeed} canDuration={features.duration}
                  narrow={narrow} busy={busy}
                  onMove={(item, f) => planAt(item, f)}
                  onResize={(item, n) => planAt(item, { duration_minutes: n })}
                  onCreate={draftAt} onOpen={(it) => (panelMode ? openPanel(it, { scroll: null }) : openDetail(it))}
                  onGo={(href) => navigate(href)}
                  saveLayers={saveLayers} onAddEvent={addFromEvent}
                  subscribe={features.calendar_link ? subscribe : null} />
  );

  /*
   * 073: TODAY'S PLAN. How long today's to-dos add up to, against a working
   * day (8 hours unless the person says otherwise, kept in this browser), as
   * a calm bar - the ones with no estimate are counted, not guessed at.
   */
  const plannedBar = ({ chip = false } = {}) => {
    if (!features.duration) return null;
    const mine = openShown.filter((t) => t.due_date && String(t.due_date).slice(0, 10) === todayIso);
    if (!mine.length) return null;
    const plan = planOf(mine, workday);
    const pct = Math.min(100, Math.round((plan.minutes / plan.capacity) * 100));
    const over = plan.minutes > plan.capacity;
    // Oct 2026: folded into Today's heading as "2 h of 8 h planned"; a press
    // unfolds the bar with the length of the day to set.
    if (chip) {
      return (
        <button type="button" className={`tpl-chip${over ? ' is-over' : ''}`} aria-expanded={planOpen}
                aria-controls="tpl-bar" data-planned={plan.minutes} data-capacity={plan.capacity}
                title="Today’s plan - press to set the length of your day" onClick={() => setPlanOpen((v) => !v)}>
          {`${plan.minutes ? hoursLabel(plan.minutes) : 'Nothing'} of ${hoursLabel(plan.capacity)} planned`}
          {over && <span className="tpl-chip-over">{` · ${hoursLabel(plan.minutes - plan.capacity)} over`}</span>}
        </button>
      );
    }
    return (
      <div className={`tpl-bar${over ? ' is-over' : ''}`} role="group" aria-label="Today’s plan" id="tpl-bar"
           data-planned={plan.minutes} data-capacity={plan.capacity}>
        <span className="tpl-text">
          <strong>{plan.minutes ? hoursLabel(plan.minutes) : 'Nothing'}</strong> planned of a{' '}
          <select className="tpl-day" aria-label="How long your working day is" value={String(workday)}
                  onChange={(e) => setWorkday(Number(e.target.value))}>
            {WORKDAYS.map((n) => <option key={n} value={String(n)}>{hoursLabel(n * 60)}</option>)}
          </select>{' '}day
          {plan.unsized > 0 && (
            <span className="tpl-unsized">{` · ${plural(plan.unsized, 'to-do')} with no estimate`}</span>
          )}
        </span>
        <span className="tpl-meter" role="meter" aria-valuemin={0} aria-valuemax={plan.capacity}
              aria-valuenow={Math.min(plan.minutes, plan.capacity)}
              aria-label={`${hoursLabel(plan.minutes)} of ${hoursLabel(plan.capacity)} planned`}>
          <span className="tpl-fill" style={{ width: `${pct}%` }} />
        </span>
        {over && <span className="tpl-over">{`${hoursLabel(plan.minutes - plan.capacity)} over`}</span>}
      </div>
    );
  };

  /** 072: the team's workload (TodoWorkload.js). Opening a to-do lands on it in its list. */
  const workloadBlock = () => (
    <TodoWorkload api={api} me={selfKey} companies={features.clients && !isContractor ? companies : null}
                  narrow={narrow} onOpen={(t) => navigate(`${location.pathname}?item=${t.id}`)} onChanged={load} />
  );
  // 106: the emails waiting to be sorted into to-dos (TodoEmails.js). What it
  // makes goes through the ordinary routes, so the list reloads behind it.
  const emailsBlock = () => (
    <TodoEmails api={api} lists={lists} narrow={narrow} plan={features.deadline} now={now} wantEmail={emailOpen}
                onWaiting={(n) => setEmailsWaiting(n)} onCreated={load} showToast={showToast} confirm={confirm}
                isAdmin={Boolean(meInfo?.isAdmin)} isContractor={isContractor} />
  );

  /*
   * THE SEARCH ON A PHONE. Gordon: "the To Do search bar should be on the
   * right side of the title To Do and be slightly smaller so they fit on
   * the same row - maybe put a magnifying glass icon next to it so the
   * search bar is hidden - vertical optimization." So where the screen is
   * narrow the search box is gone from its own row: a magnifier sits at the
   * right of the title row (the "Today ▾" switcher with lists, the view
   * pills without), and a press on it unfolds the search field across that
   * row, with × (or Escape) to fold it away again - which also clears the
   * query, so nothing stays filtered out of sight. On a desk nothing moves.
   */
  const closeSearch = () => { setSearchOpen(false); setQuery(''); };
  const searchButton = (
    <button type="button" className="ctd-search-btn" aria-label="Search to-dos" title="Search"
            aria-expanded={false} onClick={() => setSearchOpen(true)}><Icon name="search" size={18} /></button>
  );
  const phoneSearch = (
    <div className="ctd-psearch" role="search">
      <Icon name="search" size={16} className="ctd-psearch-ic" />
      <input type="search" className="ctd-search" value={query} placeholder="Search to-dos"
             aria-label="Search to-dos" autoFocus
             onChange={(e) => setQuery(e.target.value)}
             onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); closeSearch(); } }} />
      <button type="button" className="ctd-psearch-x" aria-label="Close search" title="Close search"
              onClick={closeSearch}><Icon name="close" size={16} /></button>
    </div>
  );

  const doneBlock = (items) => items.length > 0 && (
    <div className="ctd-done">
      <button type="button" className="ctd-done-toggle" aria-expanded={showDone}
              onClick={() => setShowDone((v) => !v)}>
        {showDone ? 'Hide' : 'Show'} done ({items.length})
      </button>
      {showDone && (
        <ul className="ctd-list is-done" aria-label="Done">
          {items.map((item) => renderItem(item, { isDone: true }))}
        </ul>
      )}
    </div>
  );

  const flatBlock = () => (groups.length === 0 ? (
    searching ? emptyState({ icon: 'search', title: 'No matches', text: `Nothing in ${headTitle} matches "${query.trim()}".` })
      : selList ? emptyState({ icon: 'list', title: 'This list is empty', text: `Nothing in ${selList.name} yet.`, add: true })
        : selLabel ? emptyState({ icon: 'tag', title: `Nothing labelled @${selLabel.name}`,
                                  text: `Nothing labelled @${selLabel.name} that is still open.`, add: true })
          : selClient ? emptyState({ icon: 'all', title: `Nothing open for ${selClient.name}`,
                                     text: 'Choose this client in a to-do’s editor, or add one here.', add: true })
            : selFilterId ? (smart.match && smart.match.error ? null
              : smart.match && smart.match.ids ? emptyState({ icon: 'filter', title: 'Nothing matches',
                                                               text: 'No open to-do answers this filter right now.' })
                : <p className="ctd-quiet">Loading…</p>)
            : emptyState(EMPTY[selView] || EMPTY.all)
  ) : (
    <div className={`ctd-flat${isAllTree ? ' ctd-tree is-all' : ''}`}>
      {/* All: every parent at once, open or shut. The counts say what the
          press would do; the one that would do nothing is greyed. */}
      {isAllTree && allParents.length > 0 && (
        <div className="tda-all-bar" role="group" aria-label="Every parent at once">
          <span className="tda-all-hint">{plural(allParents.length, 'parent')} with sub-tasks</span>
          <button type="button" className="tda-add-line tda-all-expand"
                  disabled={allParents.every((id) => allOpen.has(id))}
                  onClick={() => setAllFold(allParents, false)}>Expand all</button>
          <button type="button" className="tda-add-line tda-all-collapse"
                  disabled={!allParents.some((id) => allOpen.has(id))}
                  onClick={() => setAllFold(allParents, true)}>Collapse all</button>
        </div>
      )}
      {groups.map((g) => (
        <section key={g.key} className={`ctd-group is-${g.key}`}>
          {g.label && (
            <h3 className="ctd-group-head">
              {g.color !== undefined && <span className={`tda-dot ${swatch(g.color)}`} aria-hidden="true" />}
              {g.label}<span className="ctd-group-n">{g.total ?? g.items.length}</span>
              {/* Today's plan, folded into its heading (Oct 2026). */}
              {g.key === 'today' && curView === 'today' && display.planned !== false && plannedBar({ chip: true })}
            </h3>
          )}
          {g.key === 'today' && curView === 'today' && display.planned !== false && planOpen && plannedBar()}
          {g.secs ? g.secs.map((s) => (
            <div key={s.key} className={`tda-all-sec${s.name ? '' : ' is-none'}`} data-all-sec={s.key}>
              {s.name && <h4 className="tda-all-sec-name">{s.name}</h4>}
              <ul className="ctd-list" aria-label={s.name ? `${g.label} › ${s.name}` : g.label}>
                {s.rows.map((r) => renderItem(r.item, { row: r }))}
              </ul>
            </div>
          )) : (
            <ul className={`ctd-list${flatDone ? ' is-done' : ''}`} aria-label={g.label || headTitle}>
              {g.items.map((item) => (curView === 'delegated' ? renderDelegated(item)
                : renderItem(item, { isDone: flatDone || Boolean(g.done) })))}
            </ul>
          )}
        </section>
      ))}
    </div>
  ));

  const dropLine = drop && (
    <div className={`ctd-drop${drop.nestId ? ' is-nest' : ''}`} aria-hidden="true"
         style={{ top: drop.y, left: drop.left }} />
  );

  /** A list, as its sections. */
  const sectionsBlock = () => {
    const empty = sections.every((s) => !s.items.length) && !sectionsSorted.length;
    return (
      <>
        {empty ? (
          emptyState(selList.is_inbox
            ? { icon: 'inbox', title: 'Your Inbox is empty',
                text: 'Add a to-do above, or make a list of its own in the sidebar.', add: true,
                example: 'Ring Rita back tomorrow 3pm' }
            : { icon: 'list', title: 'This list is empty', text: `Nothing in ${selList.name} yet.`, add: true,
                example: 'Draft the letter tomorrow p2' })
        ) : (
          <div className="ctd-tree tda-sections" ref={treeBox} data-reorder="sections">
            {sections.map((s, i) => {
              const at = i - 1;   // its place among the sections ("No section" is not one)
              const named = Boolean(s.sectionId);
              const secObj = named ? sectionsSorted[at] : null;
              return (
                <section key={s.key} data-sec={s.key}
                         className={`tda-sec${named ? '' : ' is-none'}${s.folded ? ' is-folded' : ''}${named ? secReorder.markOf('sections', at, sectionsSorted.length) : ''}`}
                         {...(named ? { 'data-reorder-item': '', 'data-section': s.sectionId } : {})}>
                  {named && (
                    <header className="tda-sec-head" data-sec-head="">
                      <span className="tda-grip" aria-hidden="true" title="Drag to reorder sections"
                            onPointerDown={(e) => secReorder.start(e, 'sections', at)}><Icon name="grip" size={14} /></span>
                      <button type="button" className={`tda-sec-twist${s.folded ? ' is-folded' : ''}`}
                              aria-expanded={!s.folded}
                              aria-label={`${s.folded ? 'Show' : 'Hide'} section "${s.name}"`}
                              onClick={() => setSecFold(s.sectionId, !s.folded)}><Icon name="chevronDown" size={14} /></button>
                      {renamingSection === s.sectionId ? (
                        <NameForm label={`New name for section "${s.name}"`} initial={s.name} submit="Save"
                                  className="tda-rename-form"
                                  onSave={(name) => renameSection(secObj, name)}
                                  onCancel={() => setRenamingSection(null)} />
                      ) : (
                        <h3 className="tda-sec-name">
                          {s.name}<span className="tda-sec-n">{s.items.length || ''}</span>
                        </h3>
                      )}
                      <MoreMenu label={`More for section "${s.name}"`} disabled={busy} items={[
                        { label: 'Rename', onClick: () => setRenamingSection(s.sectionId) },
                        at > 0 && { label: 'Move up', onClick: () => moveSection(at, at - 1) },
                        at < sectionsSorted.length - 1 && { label: 'Move down', onClick: () => moveSection(at, at + 1) },
                        { label: 'Delete…', danger: true, onClick: () => deleteSection(secObj) }
                      ]} />
                    </header>
                  )}
                  {!s.folded && (s.rows.length ? (
                    <ul className="ctd-list" aria-label={s.name || 'No section'}>
                      {s.rows.map((r) => renderItem(r.item, { row: r }))}
                    </ul>
                  ) : named ? (
                    <p className="tda-sec-empty">Nothing here yet - drag a to-do in, or add one.</p>
                  ) : <div className="tda-sec-gap" />)}
                  {/* Phase 2: "+ Add to-do" at the end of each section, Todoist's. */}
                  {!s.folded && (named || s.rows.length > 0) && (addingIn === s.key ? (
                    <form className="tda-inline-form" data-add-in={s.key}
                          onSubmit={(e) => {
                            e.preventDefault();
                            const box = e.currentTarget.elements.title;
                            const text = box.value.trim();
                            if (!text) return;
                            box.value = '';
                            addCard(s.sectionId || null, text);
                          }}
                          onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setAddingIn(null); } }}>
                      <input name="title" type="text" autoFocus maxLength={300} placeholder="Add a to-do - quick-add words work too"
                             aria-label={`New to-do in ${s.name ? `section "${s.name}"` : selList.name}`} />
                      <button type="submit" className="btn btn-primary btn-small" disabled={busy}>Add</button>
                      <button type="button" className="btn btn-secondary btn-small" onClick={() => setAddingIn(null)}>Cancel</button>
                    </form>
                  ) : (
                    <button type="button" className="tda-add-inline" data-add-in={s.key} disabled={busy}
                            onClick={() => setAddingIn(s.key)}>
                      <Icon name="plus" size={14} /><span>Add to-do</span>
                    </button>
                  ))}
                </section>
              );
            })}
            {dropLine}
          </div>
        )}
        {addingSection ? (
          <NameForm label="Name of the new section" placeholder="Section name"
                    className="tda-section-form"
                    onSave={(name) => addSection(name)} onCancel={() => setAddingSection(false)} />
        ) : (
          <button type="button" className="tda-add-line tda-add-section" disabled={busy}
                  onClick={() => setAddingSection(true)}>+ Add section</button>
        )}
        {doneBlock(listDone)}
      </>
    );
  };

  /*
   * THE NOTES VIEW (batch 5): Keep's "Take a note…" bar and its grid, every
   * list's notes together, "Pinned" first. The search box narrows it by
   * title, body and checklist; "Archived notes" opens what was put away.
   */
  const notesBlock = () => {
    if (notesArchive) {
      const shelf = (archivedNotes || []).filter((n) => !hidden.has(n.id) && noteMatches(n, query));
      return (
        <div className="tdn-view is-archive">
          <div className="tdn-bar">
            <button type="button" className="tda-add-line tdn-back" onClick={() => setNotesArchive(false)}>
              ← Back to notes
            </button>
          </div>
          {archivedNotes === null ? <p className="ctd-quiet">Loading…</p>
            : shelf.length === 0 ? (
              searching ? emptyState({ icon: 'search', title: 'No matches', text: `No archived note matches "${query.trim()}".` })
                : emptyState({ icon: 'note', title: 'Nothing archived',
                               text: 'A note archived from its card or its editor waits here, out of the way.' })
            ) : (
              <div className="tdn-grid" aria-label="Archived notes">
                {shelf.map((n) => noteCard(n, { archived: true }))}
              </div>
            )}
        </div>
      );
    }
    const shown = notesShown.filter((n) => noteMatches(n, query));
    return (
      <div className="tdn-view">
        <TakeNote onCreate={createNote} busy={busy} />
        <NotesGrid notes={shown} render={(n) => noteCard(n)} empty={(
          searching ? emptyState({ icon: 'search', title: 'No matches', text: `No note matches "${query.trim()}".` })
            : emptyState({ icon: 'note', title: 'Notes you add appear here',
                           text: 'A few lines, a checklist, a photo of the meter - take one above.' })
        )} />
        <div className="tdn-bar">
          <span className="tdn-grow" />
          <button type="button" className="tda-add-line tdn-to-archive"
                  onClick={() => { setNotesArchive(true); loadArchivedNotes(); }}>Archived notes</button>
        </div>
      </div>
    );
  };

  /** A list's (or a label's) notes, as a small grid above its to-dos. */
  const notesStrip = () => {
    if (searching) return null;
    const mine = selList ? notesShown.filter((n) => n.list_id === selList.id)
      : selLabel ? notesShown.filter((n) => (n.labels || []).includes(selLabel.id)) : [];
    if (!mine.length) return null;
    const stripId = selList ? `list:${selList.id}` : `label:${selLabel.id}`;
    return <NotesStrip key={stripId} id={stripId} title={headTitle} notes={mine} render={(n) => noteCard(n)} />;
  };

  /** A list as a board: its sections as columns (TodoBoard.js). */
  const boardBlock = () => {
    const items = open.filter((t) => t.list_id === selList.id && !hidden.has(t.id));
    const cardMenu = (item) => (isNote(item) ? [
      { label: 'Open', onClick: () => noteActions.open(item) },
      { label: item.pinned ? 'Unpin' : 'Pin', onClick: () => noteActions.pin(item) },
      { label: 'Archive', onClick: () => noteActions.archive(item) },
      { label: 'Delete note', danger: true, onClick: () => noteRemove(item) }
    ] : [
      { label: 'Edit…', onClick: () => openPanel(item) },
      { label: item.pinned ? 'Unpin' : 'Pin to the top', onClick: () => pinItem(item).catch(showErr) },
      { label: 'Delete', danger: true, onClick: () => remove(item, open) }
    ]);
    return (
      <>
        <TodoBoard list={selList} sections={sectionsSorted} items={items} folded={secFolded}
                   doneUnder={doneUnder} labelById={labelById} now={now} todayIso={todayIso} busy={busy}
                   selfKey={selfKey} renderNote={(n) => noteCard(n, { compact: true })} cardMenu={cardMenu}
                   on={{
                     open: (item) => (isNote(item) ? noteActions.open(item) : openPanel(item, { scroll: null })),
                     openSubs: (item) => openPanel(item, { at: 'subtasks', scroll: 'subtasks' }),
                     complete: (item) => complete(item),
                     moveCard: (item, sectionId, index, shown) => boardMove(item, sectionId, index, shown),
                     moveColumn: (from, to) => moveSection(from, to),
                     renameColumn: (s, name) => renameSection(s, name),
                     deleteColumn: (s) => deleteSection(s),
                     foldColumn: (id, fold) => setSecFold(id, fold),
                     addCard,
                     addColumn: (name) => addSection(name),
                     askColumn: (item) => setAskCol(item.id)
                   }} />
        {doneBlock(listDone)}
      </>
    );
  };

  /** The floating copy while dragging, the schedule sheet, and Undo: in both layouts. */
  const ghostSheetToast = () => (
    <>
      {drag && (
        <div className="ctd-ghost" aria-hidden="true"
             style={{ left: drag.x, top: drag.y, width: drag.width }}>
          <span className="ctd-handle"><Icon name="grip" /></span>
          <span className="ctd-title">{drag.title}</span>
        </div>
      )}

      {sheet && (
        <div className="ctd-sheet-wrap" onClick={(e) => { if (e.target === e.currentTarget) setSheet(null); }}>
          <div className="ctd-sheet" role="dialog" aria-modal="true" aria-label={`Schedule "${sheet.title}"`}>
            <p className="ctd-sheet-title">{sheet.title}</p>
            {[
              { label: 'Today', date: todayIso },
              { label: 'Tomorrow', date: isoDay(addDays(now, 1)) },
              { label: 'Next week', date: nextMonday }
            ].map((o) => (
              <button key={o.label} type="button" className="ctd-sheet-opt"
                      onClick={() => schedule(sheet, o.date)}>
                <span>{o.label}</span>
                <span className="ctd-sheet-hint">{longDay(o.date)}</span>
              </button>
            ))}
            <button type="button" className="ctd-sheet-opt" onClick={() => schedule(sheet, null)}>
              <span>No date</span>
            </button>
            <label className="ctd-sheet-pick">
              <span>Pick a date</span>
              <input type="date" aria-label="Pick a date"
                     defaultValue={sheet.due_date ? String(sheet.due_date).slice(0, 10) : ''}
                     onChange={(e) => { if (e.target.value) schedule(sheet, e.target.value); }} />
            </label>
            <button type="button" className="ctd-sheet-cancel" onClick={() => setSheet(null)}>Cancel</button>
          </div>
        </div>
      )}


      {/* Phase 2: the side panel. */}
      {panelMode && panel && (() => {
        const it = findItem(panel.id);
        if (!it || hidden.has(it.id) || isNote(it)) return null;
        const l = listById.get(it.list_id) || null;
        const parent = it.parent_id ? findItem(it.parent_id) : null;
        const idx = displayed.findIndex((d) => d.id === it.id);
        const position = idx >= 0 ? { prev: displayed[idx - 1] || null, next: displayed[idx + 1] || null } : null;
        const live = it.access !== 'delegated' && !it.done_at && !it.done;
        return (
          <TodoPanel key={panel.key} item={it} list={l} parent={parent} phone={narrow} liveRef={panelLive}
                     editable={it.access !== 'delegated'} position={position} scrollTo={panel.scroll}
                     editorProps={{
                       withLists: true, lists, labels, tickets, searchTickets, busy, now, plan: features.deadline,
                       companies: features.clients && !isContractor ? companies : null, isAdmin: Boolean(meInfo?.isAdmin),
                       fixedList: !ownsList(l), onSave: (v) => saveEdit(it, v, { keep: true })
                     }}
                     subtasks={live && depthOf(it) < MAX_LEVEL ? (
                       <SubtaskTree item={it} level={depthOf(it)} now={now} todayIso={todayIso} busy={busy} completing={completing}
                                    kidsOf={(id) => tree.childrenOf(id)} doneKidsOf={(id) => doneShown.filter((t) => t.parent_id === id)}
                                    startAt={panel.at}
                                    on={{ complete: (t) => complete(t), reopen: (t) => reopen([t.id]), open: (t) => openPanel(t),
                                          addSub: (p, title) => act(() => axios.post(itemsUrl(), { title, parent_id: p.id }, auth)) }} />
                     ) : null}
                     detail={detailFor(it, { inline: true, tab: panel.at === 'attach' ? 'attach' : panel.tab })}
                     onFiles={live ? (files) => uploadTo(it, files).catch(showErr) : null}
                     menu={live ? panelMenu({ item: it, withLists: true, canConvert: tree.childrenOf(it.id).length === 0 && !it.parent_id, on: {
                       pin: (t) => pinItem(t).catch(showErr), color: (t, c) => patchItem(t, { color: c }).catch(showErr),
                       convert: (t) => { setPanel(null); convert(t, 'note'); },
                       forever: (t) => { closePanel(); complete(t, { forever: true }); },
                       remove: (t) => { panelLive.current = null; setPanel(null); remove(t, open); }
                     } }) : [{ label: 'Delete', icon: 'trash', danger: true,
                                onClick: () => { panelLive.current = null; setPanel(null); remove(it, it.done ? done : open); } }]}
                     on={{
                       close: closePanel, complete: (t) => complete(t), reopen: (t) => reopen([t.id]), ticking: completing.has(it.id),
                       goList: (x) => { closePanel(); choose({ kind: 'list', id: x.id }); },
                       open: (t) => openPanel(t),
                       step: (d) => {
                         const n = d < 0 ? position && position.prev : position && position.next;
                         if (n) { setFocusId(n.id); openPanel(n.item); }
                       }
                     }} />
        );
      })()}

      {withLists && sharing && listById.get(sharing) && (() => {
        const l = listById.get(sharing);
        const mine = (l.members || []).find((m) => m.key === selfKey);
        const owner = mine ? mine.role === 'owner' : (l.role ? l.role === 'owner' : true);
        return (
          <ShareDialog list={l} myKey={selfKey} people={people ? reachable : null} owner={owner} busy={busy}
                       error={dialogErr} onClose={() => setSharing(null)}
                       onAdd={(p) => addMember(l, p)} onRemove={(m) => removeMember(l, m)}
                       onLeave={() => leaveList(l)} />
        );
      })()}

      {withLists && sendFor && (
        <SendDialog item={sendFor.item} preset={sendFor.preset} people={people ? reachable : null}
                    busy={busy} error={dialogErr} onSend={send} onClose={cancelSend} />
      )}

      {/* Batch 5: a note open in its editor; a board card's to-do in the
          full editor; a card sent to another column by its ⋯; a note's list
          or labels chosen from its card. A note's file comes off by whoever
          put it on or the list's owner - anybody else would be told 403, so
          is not offered it. */}
      {withLists && noteOpen && (() => {
        const n = open.find((t) => t.id === noteOpen) || (archivedNotes || []).find((t) => t.id === noteOpen);
        if (!n || !isNote(n) || hidden.has(n.id)) return null;
        return (
          <NoteEditor key={n.id} item={n} on={noteOn} lists={lists} labels={labels} load={attBlob}
                      download={attSave} fixedList={!ownsList(listById.get(n.list_id))} now={now}
                      canRemove={(a) => !a.uploaded_by_key || a.uploaded_by_key === selfKey
                        || ownsList(listById.get(n.list_id))}
                      onClose={() => setNoteOpen(null)} />
        );
      })()}


      {withLists && drafting && (
        <Dialog label="New to-do" onClose={() => setDrafting(null)} className="tdb-edit tcal-draft">
          <TodoEditor item={drafting} withLists lists={lists} labels={labels}
                      companies={features.clients && !isContractor ? companies : null} isAdmin={Boolean(meInfo?.isAdmin)}
                      tickets={tickets} searchTickets={searchTickets} busy={busy} now={now} plan={features.deadline}
                      submitLabel="Add"
                      onSave={(v) => { if (!String(v.title || '').trim()) return; setDrafting(null); createFrom(v); }}
                      onCancel={() => setDrafting(null)} />
        </Dialog>
      )}


      {withLists && askCol && (() => {
        const it = open.find((t) => t.id === askCol);
        const l = it && listById.get(it.list_id);
        if (!it || !l) return null;
        const secs = (l.sections || []).slice().sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
        const here = it.section_id && secs.some((s) => s.id === it.section_id) ? it.section_id : null;
        return (
          <Dialog label={`Move "${it.title || 'note'}" to…`} onClose={() => setAskCol(null)}>
            <div className="tdb-pick" role="group" aria-label="Columns">
              {[{ id: null, name: 'No section' }, ...secs].map((c) => (
                <button key={c.id || 'none'} type="button" className="btn btn-secondary" data-col-choice={c.id || '_'}
                        disabled={c.id === here || busy} aria-current={c.id === here ? 'true' : undefined}
                        onClick={() => { setAskCol(null); boardMove(it, c.id, columnCards(it.list_id, c.id, it.id).length); }}>
                  {c.name}{c.id === here ? ' (here)' : ''}
                </button>
              ))}
            </div>
          </Dialog>
        );
      })()}

      {withLists && noteMeta && (() => {
        const n = open.find((t) => t.id === noteMeta.id);
        if (!n) return null;
        const name = n.title ? `"${n.title}"` : 'this note';
        const fixed = !ownsList(listById.get(n.list_id));
        return (
          <Dialog label={noteMeta.mode === 'list' ? `Move ${name} to a list` : `Labels on ${name}`}
                  onClose={() => setNoteMeta(null)}>
            {noteMeta.mode === 'list' ? (
              <div className="tdb-pick" role="group" aria-label="Lists">
                {fixed && <p className="tds-quiet">Only the owner of this list can move its notes out of it.</p>}
                {lists.map((l) => (
                  <button key={l.id} type="button" className="btn btn-secondary" data-list-choice={l.id}
                          disabled={fixed || l.id === n.list_id || busy}
                          onClick={() => { setNoteMeta(null); moveToList(n, l.id); }}>
                    {l.name}{l.id === n.list_id ? ' (here)' : ''}
                  </button>
                ))}
              </div>
            ) : (
              <LabelPicker labels={labels} value={n.labels || []}
                           onChange={(ids) => patchItem(n, { labels: ids }).catch(showErr)} />
            )}
          </Dialog>
        );
      })()}

      {/* Batch C: AI help on one to-do. */}
      {withLists && canAi && assist && findItem(assist.id) && (() => {
        const it = findItem(assist.id);
        const close = () => setAssist(null);
        return assist.kind === 'steps' ? (
          <StepsDialog item={it} api={api} plan={features.duration} onClose={close} onAdd={(steps) => addSteps(it, steps)} />
        ) : (
          <ChecklistDialog item={it} api={api} canSub={!isNote(it) && depthOf(it) < MAX_LEVEL} canLines={'checklist' in it}
                           onClose={close} onAddLines={(rows) => addItemsAsLines(it, rows)}
                           onAddSubtasks={(rows) => addItemsAsSubtasks(it, rows)} />
        );
      })()}

      {/* 089: a to-do into or out of Private, and "empty it?". */}
      {askMove && (
        <PrivateMoveDialog item={askMove.item} into={askMove.to.is_private ? askMove.to : null} to={askMove.to}
                           onConfirm={() => { const a = askMove; setAskMove(null); a.resolve(true); }}
                           onClose={() => { const a = askMove; setAskMove(null); a.resolve(false); }} />
      )}
      {askEmpty && (
        <EmptyListDialog list={askEmpty.list} count={allOf((t) => t.list_id === askEmpty.list.id)}
                         onConfirm={() => { const a = askEmpty; setAskEmpty(null); emptyList(a.list); }}
                         onClose={() => setAskEmpty(null)} />
      )}
      {/* 072: "complete anyway?", and a list's client. */}
      {anyway && (
        <AnywayDialog item={anyway.item} onClose={() => setAnyway(null)}
                      onConfirm={() => { const a = anyway; setAnyway(null); complete(a.item, { ...a.opts, anyway: true }); }} />
      )}
      {listClient && listById.get(listClient.id) && (() => {
        const l = listById.get(listClient.id);
        return (
          <Dialog label={`Client for “${l.name}”`} onClose={() => setListClient(null)} className="tdc-dialog">
            <div className="tdc-dialog-body">
              <p className="tds-quiet">New to-dos added to this list are for this client. Each to-do can still say otherwise.</p>
              <ClientPicker companies={companies} value={listClient.value} current={l.company || null}
                            isAdmin={Boolean(meInfo?.isAdmin)}
                            onChange={(v) => setListClient((x) => ({ ...x, value: v || '' }))} />
              <div className="tdc-dialog-buttons">
                <button type="button" className="btn btn-secondary" onClick={() => setListClient(null)}>Cancel</button>
                <button type="button" className="btn btn-primary" disabled={busy}
                        onClick={() => {
                          const value = listClient.value || null;
                          setListClient(null);
                          act(() => axios.patch(`${NEW}/lists/${l.id}${qs()}`, { company_id: value }, auth));
                        }}>Save</button>
              </div>
            </div>
          </Dialog>
        );
      })()}

      {toast && (
        <div className="ctd-toast" role="status" key={toast.key}>
          <span>{toast.text}</span>
          {toast.undo && (
            <button type="button" className="ctd-toast-undo" onClick={undoToast}>Undo</button>
          )}
        </div>
      )}
    </>
  );

  /* ------------------------------------------------------------- the old one */

  if (!withLists) {
    return (
      <div className={`ctd${swipe ? ' is-swiping' : ''}`}>
        {error && <p className="ctd-error" role="alert">{error}</p>}

        {/* The box. One line is the normal case: what it understood shows as
            chips under it as it is typed. Every field is still behind "More". */}
        {addForm}

        <div className="ctd-bar">
          {narrow && searchOpen ? phoneSearch : (
            <>
              <div className="ctd-views" role="tablist" aria-label="Views">
                {VIEWS.map((v) => (
                  <button key={v.key} type="button" role="tab" data-view={v.key}
                          aria-selected={view === v.key}
                          className={`ctd-view${view === v.key ? ' is-on' : ''}${v.key === 'today' && lateCount ? ' is-late' : ''}`}
                          onClick={() => chooseView(v.key)}>
                    {v.label}
                    <span className="ctd-view-n">{counts[v.key]}</span>
                  </button>
                ))}
              </div>
              {narrow ? searchButton : (
                <input type="search" className="ctd-search" value={query} placeholder="Search"
                       aria-label="Search to-dos"
                       onChange={(e) => setQuery(e.target.value)}
                       onKeyDown={(e) => { if (e.key === 'Escape') setQuery(''); }} />
              )}
            </>
          )}
        </div>

        {loading && !open.length && !done.length ? (
          <p className="ctd-quiet">Loading…</p>
        ) : isTree ? (
          openShown.length === 0 ? (
            emptyState(EMPTY.all)
          ) : (
            <div className="ctd-tree" ref={treeBox}>
              <section data-sec="_" className="tda-sec is-none">
                <ul className="ctd-list" aria-label="To do">
                  {rows.map((r) => renderItem(r.item, { row: r }))}
                </ul>
              </section>
              {dropLine}
            </div>
          )
        ) : flatBlock()}

        {ghostSheetToast()}
        {isTree && doneBlock(doneShown)}
        {/* The older view asks "remove it and its sub-tasks?" too. */}
        {confirmDialog}
      </div>
    );
  }

  /* ------------------------------------------------------- with lists */

  /*
   * THE SIDEBAR'S ENTRIES (Oct 2026, todo-ui-proposal.md 3.1): the six fixed
   * ones, what More holds, and Favourites - worked out here from what the
   * server can do (smart lists, filters, the workload, templates) and who
   * this is (staff or contractor), so the sidebar only draws them.
   */
  const smartOn = Boolean(features.smart && smart.on);
  const canPin = Boolean(smart.on);
  const favKeys = favouritesFrom(smart.prefs);
  const viewEntry = (key, label, icon, extra = {}) => ({
    id: key, favKey: key, sel: { kind: 'view', key }, label, icon, count: allCounts[key], late: false,
    attrs: { 'data-view': key }, ...extra
  });
  const inboxList = listById.get(inboxId) || null;
  // 089: the Private list, pinned under the Inbox with its padlock. Seen
  // through View as it is there with no count - its page says why.
  const privList = lists.find((l) => l.is_private) || null;
  const fixed = [
    inboxList && { id: 'inbox', sel: { kind: 'list', id: inboxList.id }, label: inboxList.name || 'Inbox', icon: 'inbox',
                   count: listCounts.get(inboxList.id) || 0, attrs: { 'data-list': inboxList.id } },
    privList && { id: 'private', sel: { kind: 'list', id: privList.id }, label: privList.name || 'Private', icon: 'lock',
                  count: privList.private_hidden ? null : (listCounts.get(privList.id) || 0),
                  title: privList.private_hidden ? 'Private - only its owner can see this' : 'Private - only you can see this list',
                  attrs: { 'data-list': privList.id, 'data-private': privList.private_hidden ? 'hidden' : 'mine' } },
    // 106: emails waiting to be sorted, with how many - beside the Inbox while
    // something waits or this person's emails wait by rule; under More otherwise.
    features.email_sort && (emailsWaiting > 0 || features.email_mode === 'sort')
      && { id: 'emails', sel: { kind: 'view', key: 'emails' }, label: 'Emails', icon: 'mail', count: emailsWaiting,
           title: 'Emails sent to jarvis@todo.ncmb.ai, waiting to be sorted into to-dos', attrs: { 'data-view': 'emails', 'data-emails-waiting': String(emailsWaiting) } },
    viewEntry('today', 'Today', 'today', { favKey: null, late: lateCount > 0 }),
    viewEntry('upcoming', 'Upcoming', 'upcoming', { favKey: null }),
    viewEntry('assigned', 'Assigned', 'assigned', { favKey: null, title: 'Assigned to me, by me, and the team' }),
    viewEntry('notes', 'Notes', 'note', { favKey: null }),
    { id: 'more' }
  ].filter(Boolean);
  const moreAll = [
    viewEntry('all', 'All to-dos', 'all'),
    smartOn && !smartOff('flagged') && viewEntry('flagged', 'Flagged', 'flagged'),
    viewEntry('p1', 'Priority 1', 'p1'),
    smartOn && !smartOff('waiting') && viewEntry('waiting', 'Waiting', 'waiting'),
    smartOn && viewEntry('overdue', 'Overdue', 'overdue', { late: allCounts.overdue > 0 }),
    smartOn && viewEntry('scheduled', 'Scheduled', 'calendar'),
    smartOn && viewEntry('nodate', 'No date', 'nodate'),
    viewEntry('done', 'Completed', 'done', { count: null }),
    { id: 'filters', favKey: 'filters', sel: { kind: 'filters' }, label: smart.on ? 'Filters & labels' : 'Labels',
      icon: smart.on ? 'filter' : 'tag', count: null, attrs: { 'data-view': 'filters' } },
    templatesOn && { id: 'templates', favKey: 'templates', sel: { kind: 'templates' }, label: 'Templates', icon: 'template',
                     count: null, attrs: { 'data-view': 'templates' } },
    features.workload && !isContractor && { id: 'workload', favKey: 'workload', sel: { kind: 'view', key: 'workload' },
                                            label: 'Team workload', icon: 'team', count: null, attrs: { 'data-view': 'workload' } },
    features.email_sort && !fixed.some((e) => e.id === 'emails')
      && { id: 'emails', favKey: null, sel: { kind: 'view', key: 'emails' }, label: 'Emails', icon: 'mail', count: null,
           attrs: { 'data-view': 'emails', 'data-emails-waiting': '0' } }
  ].filter(Boolean).map((e) => ({ ...e, pinned: favKeys.includes(e.favKey) }));
  const labelByFav = new Map(labels.map((b) => [labelKey(b.id), b]));
  const favViews = favKeys.map((k) => {
    const m = moreAll.find((e) => e.favKey === k);
    if (m) return { ...m, id: `fav-${k}` };
    const b = labelByFav.get(k);
    if (b) {
      return { id: `fav-${k}`, favKey: k, sel: { kind: 'label', id: b.id }, label: b.name, icon: 'tag', tag: true, color: b.color,
               count: labelCounts.get(b.id) || 0, attrs: { 'data-label': b.id } };
    }
    return null;
  }).filter(Boolean);
  const favFilters = smart.on ? (smart.filters || []).filter((f) => f.pinned).map((f) => ({
    id: `filter-${f.id}`, sel: { kind: 'filter', id: f.id }, label: f.name, mark: <FilterMark filter={f} size={13} />,
    count: f.count, title: f.query, attrs: { 'data-filter': f.id },
    unpin: () => smart.update(f, { pinned: false }).catch(showErr)
  })) : [];
  const favourites = [...favViews, ...favFilters];

  /** ☆: in or out of Favourites (the view-prefs store; todoNav.js). */
  const pin = (key, on, entry = null) => {
    const keys = favouritesFrom(smart.prefs);
    smart.savePrefs(prefsFor(on ? [...keys.filter((k) => k !== key), key] : keys.filter((k) => k !== key)));
    if (entry) showToast(on ? `“${entry.label}” is in Favourites` : `“${entry.label}” left Favourites`);
  };
  const moveFav = (from, to) => {
    const shownKeys = favViews.map((e) => e.favKey);
    const next = shownKeys.slice();
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    smart.savePrefs(prefsFor([...next, ...favouritesFrom(smart.prefs).filter((k) => !shownKeys.includes(k))]));
  };

  // Which entry is lit: Assigned for all three of its tabs.
  const here = onTemplates ? { kind: 'templates' } : onFilters ? { kind: 'filters' }
    : selFilterId ? { kind: 'filter', id: selFilterId } : selList ? { kind: 'list', id: selList.id }
      : selLabel ? { kind: 'label', id: selLabel.id } : selClient ? { kind: 'client', id: selClient.id }
        : { kind: 'view', key: ASSIGNED_TABS.includes(selView) ? 'assigned' : selView };
  const isOn = (e) => Boolean(e && e.sel && e.sel.kind === here.kind && (e.sel.key || null) === (here.key || null)
    && (e.sel.id || null) === (here.id || null));
  // This page's ☆, for the palette's "Pin this view".
  const hereFav = (moreAll.find((e) => isOn(e)) || null)
    || (selLabel ? { favKey: labelKey(selLabel.id), label: selLabel.name } : null);

  // What the filter builder's pickers offer: the names there are.
  const filterCtx = {
    lists, labels, companies: features.clients && !isContractor ? (companies || []) : [],
    people: [...new Set(lists.flatMap((l) => (l.members || []).filter((m) => m.key !== selfKey).map((m) => m.name))
      .filter(Boolean))].sort((a, b) => a.localeCompare(b))
  };
  const openById = new Map(openShown.map((t) => [String(t.id), t]));
  const isBoard = Boolean(selList && layout === 'board' && isTree);
  const headDot = selList ? (selList.is_inbox || selList.is_private ? null : swatch(selList.color))
    : selLabel ? swatch(selLabel.color) : selClient ? swatch(selClient.color) : null;

  /** List / Board / Calendar, from the header or V then a letter. */
  const changeLayout = (v) => {
    if (!layoutOptions.includes(v)) return false;
    if (selList) {
      if (v === 'calendar') setLayout(pageKey, 'calendar');
      else { setLayout(pageKey, null); setListView(selList, v); }
    } else setLayout(pageKey, v);
    return true;
  };
  // The calendar, as this page's layout: Today on its Day, a list or label
  // with its filter set, a filter or smart view with just its own to-dos.
  const calendarOpts = curView === 'today' ? { store: ':today', defaultView: 'day' }
    : curView === 'upcoming' ? {}
      : selList ? { preset: { list: selList.id } }
        : selLabel ? { preset: { label: selLabel.id }, store: ':label' }
          : { store: ':view', only: (groups || []).flatMap((g) => g.items.filter((t) => !t._completion && !g.done)) };

  /** The Display menu: what it offers on this page, and its changes. */
  const canDisplay = withLists && !onFilters && !onTemplates && curView !== 'workload' && curView !== 'emails' && !showCal && !isBoard
    && !(selList && selList.private_hidden)
    && curView !== 'notes';
  const displayCan = {
    done: Boolean(isTree || doneScope),
    group: canArrange, sort: canArrange,
    planned: curView === 'today' && features.duration
  };
  const displayValue = { group: pageDisplay.group, sort: pageDisplay.sort, done: showDone, density: display.density,
                         planned: display.planned };
  /** A Display choice: shown at once, kept here, and sent to the server a moment later. */
  function changeDisplay(patch) {
    const next = { ...display };
    delete next.panel;   // where a to-do opens is not a choice any more (26 Oct)
    if ('group' in patch || 'sort' in patch || 'done' in patch) next[pageKey] = { ...(display[pageKey] || {}), ...patch };
    else Object.assign(next, patch);
    setDisplayState(next);
    writeDisplay(ownerKey, next);
    smart.saveDisplay(displayToServer(next));
  }

  const searchable = !onFilters && !onTemplates && curView !== 'workload' && curView !== 'emails';
  const dismissTip = () => { setTipMoved(false); writeOnce(ownerKey, 'moved'); };
  const showTip = tipMoved && returning.current;
  const mac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '');
  const paletteKeys = mac ? '⌘K' : 'Ctrl K';

  /* The keys G, V, ?, / and Ctrl K reach the page through this. */
  navKeys.current = {
    palette: () => { setDrawer(false); setPalette(true); },
    keys: () => setKeysOpen(true),
    search: searchable ? () => setSearchOpen(true) : null,
    go: (k) => {
      const to = { i: inboxList ? { kind: 'list', id: inboxList.id } : null, t: { kind: 'view', key: 'today' },
                   u: { kind: 'view', key: 'upcoming' }, m: { kind: 'view', key: 'assigned' },
                   n: { kind: 'view', key: 'notes' }, a: { kind: 'view', key: 'all' },
                   c: { kind: 'view', key: calOn ? 'calendar' : 'upcoming' } }[k];
      if (!to) return false;
      choose(to);
      return true;
    },
    layout: (k) => changeLayout({ l: 'list', b: 'board', c: 'calendar' }[k])
  };

  /** Ctrl K's contents, made only while it is open. */
  const paletteGroups = () => {
    const allEntries = [...fixed.filter((e) => e.id !== 'more'), ...moreAll];
    const lists2 = lists.filter((l) => !l.is_inbox);
    const asItem = (e) => ({ id: `${e.sel.kind}:${e.sel.key || e.sel.id || ''}`, label: e.label, icon: e.icon,
                             mark: e.mark || (e.tag ? <Icon name="tag" size={15} /> : null),
                             sub: e.count ? String(e.count) : null, run: () => choose(e.sel) });
    const listItem = (l) => ({ id: `list:${l.id}`, label: l.name, keywords: 'list',
                               mark: <span className={`tda-dot ${swatch(l.color)}`} aria-hidden="true" />,
                               sub: listCounts.get(l.id) ? String(listCounts.get(l.id)) : null,
                               run: () => choose({ kind: 'list', id: l.id }) });
    const resolve = (s) => {
      if (s.kind === 'list') { const l = listById.get(s.id); return l ? listItem(l) : null; }
      if (s.kind === 'label') {
        const b = labelById.get(s.id);
        return b ? { id: `label:${b.id}`, label: `@${b.name}`, icon: 'tag', run: () => choose(s) } : null;
      }
      if (s.kind === 'filter') {
        const f = smart.byId.get(s.id);
        return f ? { id: `filter:${f.id}`, label: f.name, icon: 'filter', run: () => choose(s) } : null;
      }
      const e = allEntries.find((x) => x.sel.kind === s.kind && (x.sel.key || null) === (s.key || null));
      if (e) return asItem(e);
      if (s.kind === 'view' && s.key === 'delegated') return { id: 'view:delegated', label: 'Assigned › By me', icon: 'delegated', run: () => choose(s) };
      return null;
    };
    const assignedTabs = [
      { id: 'tab:delegated', label: 'Assigned › By me', icon: 'delegated', keywords: 'delegated sent handed', run: () => choose({ kind: 'view', key: 'delegated' }) },
      features.workload && !isContractor && { id: 'tab:workload', label: 'Assigned › Team', icon: 'team', keywords: 'workload',
                                              run: () => choose({ kind: 'view', key: 'workload' }) }
    ].filter(Boolean);
    const can = (v) => layoutOptions.includes(v) && layout !== v;
    const actions = [
      { id: 'act:add', label: 'Add a to-do', icon: 'plus', hint: 'Q', run: () => setQuickFloat(true) },
      { id: 'act:note', label: 'New note', icon: 'note', run: () => choose({ kind: 'view', key: 'notes' }) },
      { id: 'act:list', label: 'New list', icon: 'list', run: () => setNewList(true) },
      templatesOn && { id: 'act:tpl', label: 'New list from template…', icon: 'template', run: () => choose({ kind: 'templates' }) },
      smart.on && { id: 'act:filter', label: 'New filter', icon: 'filter',
                    run: () => { choose({ kind: 'filters' }); setFilterEdit({ key: 'new' }); } },
      { id: 'act:label', label: 'New label', icon: 'tag', run: () => choose({ kind: 'filters' }) },
      displayCan.done && canDisplay && { id: 'act:done', label: showDone ? 'Hide completed' : 'Show completed', icon: 'done',
                                         run: () => setShowDone((v) => !v) },
      can('list') && { id: 'act:v-list', label: 'Switch to List', icon: 'list', hint: 'V L', run: () => changeLayout('list') },
      can('board') && { id: 'act:v-board', label: 'Switch to Board', icon: 'board', hint: 'V B', run: () => changeLayout('board') },
      can('calendar') && { id: 'act:v-cal', label: 'Switch to Calendar', icon: 'calendarView', hint: 'V C', run: () => changeLayout('calendar') },
      { id: 'act:archived', label: 'Archived lists', icon: 'empty', run: () => { setShowArchived(true); listActions.loadArchived(); if (phoneHead) setDrawer(true); } },
      canPin && hereFav && hereFav.favKey && { id: 'act:pin', label: favKeys.includes(hereFav.favKey) ? 'Unpin this view' : 'Pin this view',
                                               icon: 'star', run: () => pin(hereFav.favKey, !favKeys.includes(hereFav.favKey), hereFav) },
      { id: 'act:keys', label: 'Keyboard shortcuts', icon: 'keyboard', hint: '?', run: () => setKeysOpen(true) }
    ].filter(Boolean);
    return [
      { key: 'recent', recent: true, label: 'Recent', items: readRecent(ownerKey).map(resolve).filter(Boolean).slice(0, 5) },
      { key: 'goto', label: 'Go to', items: [...allEntries.map(asItem), ...assignedTabs,
        calOn && { id: 'view:calendar', label: 'Calendar', icon: 'calendarView', keywords: 'upcoming week month',
                   hint: 'G C', run: () => choose({ kind: 'view', key: 'calendar' }) }].filter(Boolean) },
      favourites.length > 0 && { key: 'favs', label: 'Favourites', items: favourites.map((e) => ({ ...asItem(e), id: `fav:${e.id}` })) },
      { key: 'lists', label: 'My lists', items: lists2.map(listItem), limit: 8, searchLimit: 8 },
      { key: 'labels', label: 'Labels', searchOnly: true, items: labels.map((b) => ({
        id: `label:${b.id}`, label: `@${b.name}`, keywords: 'label', icon: 'tag', run: () => choose({ kind: 'label', id: b.id }) })) },
      smart.on && { key: 'filters', label: 'Filters', searchOnly: true, items: (smart.filters || []).map((f) => ({
        id: `filter:${f.id}`, label: f.name, keywords: `filter ${f.query}`, mark: <FilterMark filter={f} size={14} />,
        run: () => choose({ kind: 'filter', id: f.id }) })) },
      { key: 'actions', label: 'Actions', items: actions }
    ].filter(Boolean).filter((g) => g.items.length);
  };
  const paletteTodos = () => [
    ...openShown.map((t) => ({ id: t.id, title: t.title,
      where: [listById.get(t.list_id)?.name, t.due_date ? dueLabel(String(t.due_date).slice(0, 10), t.due_time, now) : null]
        .filter(Boolean).join(' › '),
      run: () => navigate(`${location.pathname}?item=${t.id}`) })),
    ...notesShown.map((n) => ({ id: n.id, title: n.title || String(n.notes || '').slice(0, 60), note: true,
      where: 'Note', run: () => { choose({ kind: 'view', key: 'notes' }); noteActions.open(n); } }))
  ];
  const paletteAdd = (text) => {
    setQuick(text);
    setIgnore([]);
    setConfirmed(new Set());
    setHint(null);
    setQuickFloat(true);
  };

  /*
   * PHASE 2, A FIRST VISIT: three short tips in the same place - quick
   * add's words, Ctrl K, More - and, at the end, an offer of a "Getting
   * started" list to play with (made only when asked, and deletable like
   * any other).
   */
  const ONBOARD = [
    { title: 'Add things in plain words', text: <>Type <q>Call Rita tomorrow 3pm p1 #List @label</q> - the day, time, priority, list and label are read for you. <kbd>Q</kbd> adds from anywhere.</> },
    { title: 'Find anything', text: <>Press <kbd>{paletteKeys}</kbd> to jump to a view, list, label or to-do, or to run an action. <kbd>?</kbd> lists every key.</> },
    { title: 'The rest is under More', text: <>Completed, filters and labels, and templates live under <strong>More</strong>. Press ☆ on any of them to keep it in Favourites.</> }
  ];
  const onboardStep = !returning.current && onboard !== 'done' && !showTip ? (Number(onboard) || 0) : null;
  const setStep = (n) => { setOnboard(n); writeOnce(ownerKey, 'onboard', n); };
  const addSample = () => act(async () => {
    const r = await axios.post(`${NEW}/lists${qs()}`, { name: 'Getting started', color: 'teal' }, auth);
    const id = r.data?.id;
    if (!id) return;
    for (const title of ['Tick me off - press the ring', 'Open me with E: every detail of a to-do is in one panel',
      'Add one with quick add: "Call Rita tomorrow 3pm p1"', 'Press Ctrl K and type "notes"',
      'Delete this list from its ⋯ when you are done']) {
      await axios.post(itemsUrl(), { title, list_id: id }, auth);
    }
    choose({ kind: 'list', id });
  });
  const onboardTip = onboardStep !== null && onboardStep < ONBOARD.length ? (
    <div className="tda-tip is-onboard" role="note" aria-label="Getting started" data-tip="onboard" data-step={onboardStep}>
      <p className="tda-tip-title"><Icon name="sparkle" size={14} />{ONBOARD[onboardStep].title}</p>
      <p className="tda-tip-text">{ONBOARD[onboardStep].text}</p>
      <div className="tda-tip-buttons">
        <span className="tda-tip-steps" aria-label={`Tip ${onboardStep + 1} of ${ONBOARD.length}`}>
          {ONBOARD.map((x, i) => <span key={x.title} className={`tda-tip-step${i === onboardStep ? ' is-on' : ''}`} />)}
        </span>
        {onboardStep < ONBOARD.length - 1 ? (
          <>
            <button type="button" className="tda-add-line" onClick={() => setStep('done')}>Skip</button>
            <button type="button" className="btn btn-secondary btn-small" onClick={() => setStep(onboardStep + 1)}>Next</button>
          </>
        ) : (
          <>
            <button type="button" className="tda-add-line" disabled={busy}
                    onClick={() => { setStep('done'); addSample(); }}>Add a sample list</button>
            <button type="button" className="btn btn-secondary btn-small" onClick={() => setStep('done')}>Done</button>
          </>
        )}
      </div>
    </div>
  ) : null;

  // The one-time tip, for people who knew the old sidebar (todo-ui-proposal.md 5.1).
  const tip = onboardTip || (showTip ? (
    <div className="tda-tip" role="note" aria-label="Where things went" data-tip="moved">
      <p className="tda-tip-title"><Icon name="sparkle" size={14} />The sidebar is calmer</p>
      <p className="tda-tip-text">
        Calendar is now a layout at the top of each page. Assigned has To me, By me and Team. Completed, labels,
        filters and templates are under <strong>More</strong> - or press <kbd>{paletteKeys}</kbd>.
      </p>
      <div className="tda-tip-buttons">
        <button type="button" className="tda-add-line" onClick={() => { setMoreOpen(true); dismissTip(); }}>Open More</button>
        <button type="button" className="btn btn-secondary btn-small" onClick={dismissTip}>Got it</button>
      </div>
    </div>
  ) : null);

  const sidebar = (
    <TodoSidebar who={ownerKey} fixed={fixed} more={moreAll} favourites={favourites} lists={lists}
                 listCount={(id) => listCounts.get(id) || 0} isOn={isOn}
                 onSelect={choose} busy={busy} actions={listActions} archived={archived}
                 showArchived={showArchived} setShowArchived={setShowArchived}
                 canPin={canPin} onPin={pin} onMoveFav={canPin ? moveFav : null}
                 moreOpen={moreOpen} onMore={setMoreOpen}
                 onAddTodo={() => { setDrawer(false); setQuickFloat(true); }}
                 onPalette={() => { setDrawer(false); setPalette(true); }}
                 onShortcuts={() => { setDrawer(false); setKeysOpen(true); }}
                 onFromTemplate={templatesOn ? () => choose({ kind: 'templates' }) : null}
                 folds={smart.on} tip={tip} paletteKeys={paletteKeys} />
  );

  // The open list's own ⋯ (in its header), and the Inbox's (its colour) -
  // and a label's, now that labels have left the sidebar for More.
  const labelPinned = selLabel ? favKeys.includes(labelKey(selLabel.id)) : false;
  const headListMenu = selList && !selList.private_hidden ? (
    <MoreMenu label={`More for list "${selList.name}"`} disabled={busy} items={selList.is_inbox ? [
      { label: 'Colour', icon: 'tag', sub: (close) => (
        <ColorPicker name={selList.name} value={selList.color} onPick={(c) => { close(); listActions.colorList(selList, c); }} />) }
    ] : listMenuItems(selList, { actions: listActions, onRename: () => setHeadRename(true) })} />
  ) : selLabel ? (
    <MoreMenu label={`More for label "${selLabel.name}"`} disabled={busy} items={[
      { label: 'Rename', icon: 'edit', onClick: () => setHeadRename(true) },
      { label: 'Colour', icon: 'tag', sub: (close) => (
        <ColorPicker name={selLabel.name} value={selLabel.color} onPick={(c) => { close(); listActions.colorLabel(selLabel, c); }} />) },
      canPin && { label: labelPinned ? 'Unpin from Favourites' : 'Pin to Favourites', icon: 'star',
                  onClick: () => pin(labelKey(selLabel.id), !labelPinned, { label: selLabel.name }) },
      { sep: true },
      { label: 'Delete…', icon: 'trash', danger: true, onClick: () => listActions.deleteLabel(selLabel) }
    ]} />
  ) : null;
  const layoutSwitch = (compact) => (
    <LayoutSwitch options={layoutOptions} value={layout} onChange={changeLayout} name={headTitle} compact={compact}
                  disabled={busy} />
  );
  const displayMenu = () => canDisplay && (
    <DisplayMenu value={displayValue} can={displayCan} onChange={changeDisplay} />
  );
  // On a phone one ⋯ holds both: Display, and the list's own menu.
  const phoneMenu = (canDisplay || selList || selLabel) && (
    <MoreMenu label={`Options for "${headTitle}"`} disabled={busy} items={[
      canDisplay && { label: 'Display', icon: 'sliders', sub: () => (
        <DisplayBody value={displayValue} can={displayCan} onChange={changeDisplay} />) },
      (selList || selLabel) && { sep: true },
      ...(selLabel && !selList ? [
        { label: 'Colour', icon: 'tag', sub: (close) => (
          <ColorPicker name={selLabel.name} value={selLabel.color} onPick={(c) => { close(); listActions.colorLabel(selLabel, c); }} />) },
        { label: 'Delete label…', icon: 'trash', danger: true, onClick: () => listActions.deleteLabel(selLabel) }
      ] : []),
      ...(selList && !selList.private_hidden ? (selList.is_inbox ? [
        { label: 'Colour', icon: 'tag', sub: (close) => (
          <ColorPicker name={selList.name} value={selList.color} onPick={(c) => { close(); listActions.colorList(selList, c); }} />) }
      ] : listMenuItems(selList, { actions: listActions, onRename: () => setHeadRename(true) })) : [])
    ]} />
  );
  const deskSearch = searchable && (searchOpen || query ? (
    <span className="ctd-search-box is-open" role="search">
      <Icon name="search" size={14} className="ctd-search-ic" />
      <input type="search" className="ctd-search" value={query} placeholder={`Search ${headTitle}`}
             aria-label="Search to-dos" autoFocus={searchOpen}
             onChange={(e) => setQuery(e.target.value)}
             onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); closeSearch(); } }} />
      <button type="button" className="ctd-psearch-x" aria-label="Close search" title="Close search (Esc)"
              onClick={closeSearch}><Icon name="close" size={14} /></button>
    </span>
  ) : (
    <button type="button" className="ctd-search-btn tda-head-btn" aria-label="Search to-dos" title="Search this view (/)"
            onClick={() => setSearchOpen(true)}><Icon name="search" size={16} /></button>
  ));
  const tabs = withLists && ASSIGNED_TABS.includes(selView) && (
    <div className="tda-tabs" role="tablist" aria-label="Assigned"
         onKeyDown={(e) => {
           if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
           const all = [...e.currentTarget.querySelectorAll('[role="tab"]')];
           const at = all.indexOf(document.activeElement);
           const next = all[(at + (e.key === 'ArrowRight' ? 1 : all.length - 1)) % all.length];
           if (next) { e.preventDefault(); next.focus(); next.click(); }
         }}>
      {[['assigned', 'To me', allCounts.assigned], ['delegated', 'By me', allCounts.delegated],
        ...(features.workload && !isContractor ? [['workload', 'Team', null]] : [])].map(([k, l, n]) => (
        <button key={k} type="button" role="tab" data-tab={k} aria-selected={selView === k} tabIndex={selView === k ? 0 : -1}
                className={`tda-tab${selView === k ? ' is-on' : ''}`} onClick={() => choose({ kind: 'view', key: k })}>
          {l}{n ? <span className="tda-tab-n">{n}</span> : null}
        </button>
      ))}
    </div>
  );
  const showQuickInline = !phoneHead && !quickFloat && !showCal && curView !== 'notes' && curView !== 'done'
    && curView !== 'workload' && curView !== 'emails' && curView !== 'filters' && !(selList && selList.private_hidden);

  return (
    <div className={`ctd tda is-lists${swipe ? ' is-swiping' : ''}${drawer ? ' is-drawer-open' : ''}${display.density === 'compact' ? ' is-compact' : ''}${panelMode && panel && !narrow ? ' has-panel' : ''}`}>
      {/* On a phone the sidebar is this one line until it is opened - the
          title (a bottom sheet of the calm sidebar), the layout, the
          Display menu, a list's ⋯ and the search's magnifier; the search
          field takes the row over while it is open. */}
      {phoneHead && (
        <div className="tda-phone-head">
          {searchOpen ? phoneSearch : (
            <>
              <button type="button" className="tda-switch" aria-expanded={drawer} aria-controls="tda-side"
                      aria-haspopup="dialog" aria-label={`${headTitle}: choose a view, list or label`}
                      onClick={() => setDrawer((v) => !v)}>
                {headDot && <span className={`tda-dot ${headDot}`} aria-hidden="true" />}
                <span className="tda-switch-name">{headTitle}</span>
                {headCount ? <span className="tda-n">{headCount}</span> : null}
                <span className="tda-switch-caret" aria-hidden="true"><Icon name="chevronDown" /></span>
              </button>
              {!onTemplates && layoutSwitch(true)}
              {!onTemplates && phoneMenu}
              {searchable && searchButton}
            </>
          )}
        </div>
      )}

      <div className="tda-layout">
        {phoneHead && drawer && (
          <div className="tda-sheet-scrim" aria-hidden="true" onClick={() => setDrawer(false)} />
        )}
        <div className={`tda-side-wrap${phoneHead ? ' is-sheet' : ''}`} id="tda-side"
             {...(phoneHead && drawer ? { role: 'dialog', 'aria-modal': 'true', 'aria-label': 'Views and lists' } : {})}>
          {phoneHead && drawer && (
            <div className="tda-sheet-head">
              <span className="tda-sheet-grab" aria-hidden="true" />
              <button type="button" className="tda-sheet-x" aria-label="Close" onClick={() => setDrawer(false)}>
                <Icon name="close" size={18} />
              </button>
            </div>
          )}
          {sidebar}
        </div>

        <div className="tda-main">
          {onTemplates ? (
            <TodoTemplates lists={lists} labels={labels} people={people ? reachable : null} needPeople={needPeople}
                           selfKey={selfKey} isAdmin={Boolean(meInfo?.isAdmin)} phone={narrow}
                           intent={tplIntent} onIntentDone={() => setTplIntent(null)}
                           showToast={showToast}
                           onApplied={async (r) => {
                             await load();
                             if (r?.list_id) choose({ kind: 'list', id: r.list_id });
                             showToast(`Made ${plural(r?.created || 0, 'to-do')} in “${r?.list_name || 'the list'}”`);
                           }} />
          ) : (
          <>
          {(!phoneHead || selFilter) && (
          <div className="tda-head">
            {!phoneHead && (headRename && ((selList && !selList.is_inbox) || selLabel) ? (
              <NameForm label={`New name for "${(selList || selLabel).name}"`} initial={(selList || selLabel).name} submit="Save"
                        className="tda-rename-form tda-head-rename"
                        onSave={(name) => {
                          setHeadRename(false);
                          if (selList && name !== selList.name) listActions.renameList(selList, name);
                          if (selLabel && name !== selLabel.name) listActions.renameLabel(selLabel, name);
                        }}
                        onCancel={() => setHeadRename(false)} />
            ) : (
              <h2 className="tda-title" title={headTitle}>
                {headDot && <span className={`tda-dot ${headDot}`} aria-hidden="true" />}
                {selList && selList.is_private && (
                  <span className="tda-dot is-icon tda-head-lock" aria-hidden="true" data-head-lock=""><Icon name="lock" /></span>
                )}
                {selFilter && <FilterMark filter={selFilter} />}
                {onFilters && <span className="tda-dot is-icon" aria-hidden="true"><Icon name={smart.on ? 'filter' : 'tag'} /></span>}
                <span className="tda-title-text">{headTitle}</span>
              </h2>
            ))}
            {/* 074: a saved filter says what it asks, and can be changed from here. */}
            {selFilter && (
              <span className="tdf-head-tools">
                <code className="tdf-q" title={selFilter.query}>{selFilter.query}</code>
                <button type="button" className="btn btn-secondary btn-small" disabled={busy}
                        aria-label={`Edit filter “${selFilter.name}”`}
                        onClick={() => { choose({ kind: 'filters' }); setFilterEdit(selFilter); }}>Edit</button>
              </span>
            )}
            {!phoneHead && (
              <>
                {selList && selList.company && (
                  <ClientChip company={selList.company} full onClick={(c) => choose({ kind: 'client', id: c.id })} />
                )}
                {layoutSwitch(false)}
                {displayMenu()}
                {selList && !selList.is_inbox && !selList.is_private && (
                  <span className="tds-head-share">
                    {selList.shared && <Faces members={selList.members} />}
                    <button type="button" className="btn btn-secondary btn-small tds-share-btn" disabled={busy}
                            aria-label={`Share "${selList.name}"`} onClick={() => shareList(selList)}>Share</button>
                  </span>
                )}
                {headListMenu}
                {deskSearch}
              </>
            )}
          </div>
          )}
          {tabs}
          {selList && selList.is_private && !selList.private_hidden && !onFilters && <PrivateHint />}

          {error && <p className="ctd-error" role="alert">{error}</p>}
          {selFilterId && smart.match && smart.match.error && (
            <div className="tdf-error" role="alert">
              <p>{smart.match.error.error}</p>
              {selFilter && (
                <button type="button" className="tda-add-line"
                        onClick={() => { choose({ kind: 'filters' }); setFilterEdit(selFilter); }}>Fix the filter</button>
              )}
            </div>
          )}
          {showQuickInline && addForm}
          {curView !== 'filters' && !showCal && clientBar}

          {/* The workload keeps its own state (filters, what is open) while
              the list reloads behind it. */}
          {curView === 'filters' ? (
            <>
              {smart.on && (
                <TodoFilters smart={smart} ctx={filterCtx} openById={openById} phone={narrow} busy={busy}
                             showToast={showToast} editing={filterEdit} setEditing={setFilterEdit}
                             onOpen={(id) => choose({ kind: 'filter', id })} />
              )}
              {!filterEdit && (
                <TodoLabels labels={labels} count={(id) => labelCounts.get(id) || 0} busy={busy} actions={listActions}
                            onOpen={(b) => choose({ kind: 'label', id: b.id })} canPin={canPin}
                            isPinned={(b) => favKeys.includes(labelKey(b.id))}
                            onPin={(b, on) => pin(labelKey(b.id), on, { label: b.name })} />
              )}
            </>
          ) : curView === 'workload' ? workloadBlock()
            : curView === 'emails' ? emailsBlock()
            : selList && selList.private_hidden ? <PrivateHidden list={selList} />
            : showCal ? calendarBlock(calendarOpts)
            : loading && !open.length && !done.length ? (
              <p className="ctd-quiet">Loading…</p>
            ) : curView === 'notes' ? notesBlock()
            : isBoard ? boardBlock()
              : (
                <>
                  {notesStrip()}
                  {isTree ? sectionsBlock() : flatBlock()}
                </>
              )}
          </>
          )}
        </div>
      </div>

      {/* The phone's +: quick add from the foot of any page. */}
      {phoneHead && !quickFloat && !drawer && (
        <button type="button" className="tda-fab" aria-label="Add a to-do" title="Add a to-do"
                onClick={() => setQuickFloat(true)}><Icon name="plus" size={24} /></button>
      )}

      {ghostSheetToast()}

      {quickFloat && (
        <Dialog label="Add a to-do" onClose={() => setQuickFloat(false)} className="tda-quick-dialog">
          {quickForm({ floating: true })}
          <p className="tda-quick-where">
            {(() => {
              const l = listById.get(form.list_id || (parsed.list ? parsed.list.id : defaultListId));
              return l ? <>Into <strong>{l.name}</strong>{curView === 'today' && !selList ? ', for today' : ''} · <kbd>Enter</kbd> to add</> : null;
            })()}
          </p>
        </Dialog>
      )}
      {palette && (
        <TodoPalette groups={paletteGroups()} todos={paletteTodos()} onAdd={paletteAdd} onClose={() => setPalette(false)} />
      )}
      {keysOpen && <ShortcutsSheet Dialog={Dialog} onClose={() => setKeysOpen(false)} mac={mac} />}
      {newList && (
        <Dialog label="New list" onClose={() => setNewList(false)} className="tda-newlist-dialog">
          <NameForm label="Name of the new list" placeholder="List name" withColor
                    onSave={(name, color) => { setNewList(false); listActions.addList(name, color); }}
                    onCancel={() => setNewList(false)} />
        </Dialog>
      )}
      {confirmDialog}
    </div>
  );
}
